import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FINANCEUR_TYPES } from '../constants/financeurs'; // fallback si l'API n'est pas encore chargée
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { SignatureCanvas, SectionRepliable, PageHeader, Badge, DataTable, Loading, formatDate, ConflictModal, ouvrirDocument, fermerDocument, ouvrirApercuDevis, ouvrirApercuConvention, BackButton, FichesDirectesPanel, StatutSmsPin, CarteConvention, niveauMessage, couleurMessage, styleMessage, CarteSms, MenuStagiairesMulti, ResultatSms, HistoriqueSms, ACTIONS_SMS_GROUPE, compterSms, COULEUR_ATTENTION, useJoursNonTravailles, libelleJourNonTravaille } from '../components/Shared';

// URL de base du frontend (pour les liens d'émargement accessibles depuis les téléphones)
// Si REACT_APP_FRONTEND_URL est défini dans .env, il est prioritaire.
// Sinon, on utilise window.location.origin qui s'adapte automatiquement à l'adresse IP du serveur.
const APP_URL_BASE = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

// Fallback clipboard sans navigator.clipboard (HTTP non-localhost)
function copierPressePapier(texte) {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(texte).then(() => alert('Lien copié !')).catch(() => fallbackCopy(texte));
  } else {
    fallbackCopy(texte);
  }
}
function fallbackCopy(texte) {
  const ta = document.createElement('textarea');
  ta.value = texte;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try { document.execCommand('copy'); alert('Lien copié !'); }
  catch (e) { alert('Impossible de copier automatiquement.\nLien : ' + texte); }
  document.body.removeChild(ta);
}

// ─── QR Code SVG léger (sans dépendance) ────────────────────────────────────
function QRCodeImg({ url, size = 160 }) {
  // On utilise l'API Google Charts (marche en local réseau)
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}`;
  return (
    <img src={src} alt="QR Code" width={size} height={size}
      style={{ border: '4px solid #fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
      onError={e => { e.target.style.display = 'none'; }}
    />
  );
}

// ─── Modal Aménagement d'horaires ─────────────────────────────────────────────
function ModalAmenagement({ session, inscriptions, groupes = [], sessionId, onClose, onSaved }) {
  const API_BASE_AM = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
  const token       = localStorage.getItem('gf_token');

  // Grouper les inscriptions par entreprise (nom_client_snapshot)
  const companies = React.useMemo(() => {
    const map = {};
    (inscriptions || []).forEach(i => {
      const key = i.nom_client_snapshot || '—';
      if (!map[key]) map[key] = { nom: key, stagiaires: [] };
      map[key].stagiaires.push(i);
    });
    return Object.values(map);
  }, [inscriptions]);

  const [selectedIds, setSelectedIds]     = useState(new Set());
  const [motif,       setMotif]           = useState('');
  const [horairesOrig,setHorairesOrig]    = useState(session?.horaires || '');
  const [nouveauxH,   setNouveauxH]       = useState('');
  const [companyEmails, setCompanyEmails] = useState(() => {
    const initial = {};
    (groupes || []).forEach(g => {
      if (g.nom_client && g.client_contact_email) initial[g.nom_client] = g.client_contact_email;
    });
    return initial;
  }); // { nom_client: email }
  const [saving,      setSaving]          = useState(false);
  const [error,       setError]           = useState('');

  const inputS = {
    width: '100%', padding: '0.45rem 0.7rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box',
  };

  function toggleStagiaire(id) {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleAll() {
    if (selectedIds.size === inscriptions.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(inscriptions.map(i => i.id)));
  }

  async function handleSave() {
    if (!motif.trim())       { setError('Le motif est requis.'); return; }
    if (!nouveauxH.trim())   { setError('Les nouveaux horaires sont requis.'); return; }
    if (selectedIds.size === 0) { setError('Sélectionnez au moins un stagiaire.'); return; }
    setSaving(true); setError('');
    try {
      const stagiaires = inscriptions
        .filter(i => selectedIds.has(i.id))
        .map(i => ({
          stagiaire_id:  i.stagiaire_id,
          inscription_id: i.id,
          nom_stagiaire: i.nom_stagiaire_snapshot || i.stagiaire_id,
          nom_client:    i.nom_client_snapshot || '',
          client_id:     i.client_id || '',
          email_entreprise: (companyEmails[i.nom_client_snapshot || '—'] || '').trim(),
        }));
      const r = await fetch(`${API_BASE_AM}/sessions/${sessionId}/amenagements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ motif, horaires_originaux: horairesOrig, nouveaux_horaires: nouveauxH, stagiaires }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      onSaved(d.emailsEnvoyes || []);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '14px', width: '640px', maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', padding: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1.2rem', color: 'var(--text-primary)', fontSize: '1rem' }}>
          🕐 Aménagement d'horaires — {session?.session_id || sessionId}
        </h3>

        {/* Motif */}
        <div style={{ marginBottom: '0.8rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
            Motif de l'aménagement <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <textarea value={motif} onChange={e => setMotif(e.target.value)} rows={2}
            placeholder="Ex : Réduction de la pause déjeuner à la demande des stagiaires, sans modification de la durée globale de formation."
            style={{ ...inputS, resize: 'vertical' }} />
        </div>

        {/* Horaires */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.8rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
              Horaires initiaux
            </label>
            <input type="text" value={horairesOrig} onChange={e => setHorairesOrig(e.target.value)}
              placeholder="Ex : 08h30 – 12h00 / 13h00 – 16h30" style={inputS} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
              Nouveaux horaires <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input type="text" value={nouveauxH} onChange={e => setNouveauxH(e.target.value)}
              placeholder="Ex : 08h30 – 12h00 / 12h30 – 16h00" style={inputS} />
          </div>
        </div>

        {/* Sélection stagiaires */}
        <div style={{ marginBottom: '0.8rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              Stagiaires concernés <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <button type="button" onClick={toggleAll}
              style={{ fontSize: '0.73rem', padding: '0.2rem 0.6rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              {selectedIds.size === inscriptions.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', maxHeight: '220px', overflowY: 'auto' }}>
            {companies.map(company => (
              <div key={company.nom}>
                {/* En-tête entreprise */}
                <div style={{ background: 'var(--bg-card)', padding: '0.4rem 0.7rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>
                  🏢 {company.nom}
                </div>
                {/* Stagiaires */}
                {company.stagiaires.map(i => (
                  <div key={i.id}
                    onClick={() => toggleStagiaire(i.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.45rem 0.7rem', cursor: 'pointer', background: selectedIds.has(i.id) ? 'rgba(124,58,237,0.07)' : 'transparent', borderBottom: '1px solid var(--border)' }}>
                    <input type="checkbox" checked={selectedIds.has(i.id)} onChange={() => toggleStagiaire(i.id)}
                      onClick={e => e.stopPropagation()}
                      style={{ cursor: 'pointer', accentColor: '#7c3aed' }} />
                    <span style={{ fontSize: '0.84rem', color: 'var(--text-primary)' }}>
                      {i.nom_stagiaire_snapshot || i.stagiaire_id}
                    </span>
                  </div>
                ))}
              </div>
            ))}
            {inscriptions.length === 0 && (
              <div style={{ padding: '1rem', color: 'var(--text-tertiary)', fontSize: '0.84rem', fontStyle: 'italic', textAlign: 'center' }}>
                Aucun stagiaire inscrit.
              </div>
            )}
          </div>
          {selectedIds.size > 0 && (
            <div style={{ fontSize: '0.75rem', color: '#7c3aed', marginTop: '0.3rem' }}>
              {selectedIds.size} stagiaire{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Emails entreprises */}
        {companies.length > 0 && (
          <div style={{ marginBottom: '0.8rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
              Email(s) entreprise(s) pour validation
              <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: '0.4rem' }}>(optionnel — un email par entreprise représentée)</span>
            </label>
            {companies.map(company => (
              <div key={company.nom} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', minWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={company.nom}>
                  {company.nom}
                </span>
                <input type="email" value={companyEmails[company.nom] || ''} onChange={e => setCompanyEmails(prev => ({ ...prev, [company.nom]: e.target.value }))}
                  placeholder="email@entreprise.fr"
                  style={{ ...inputS, flex: 1 }} />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#f87171', fontSize: '0.84rem', marginBottom: '0.8rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '0.5rem 1.2rem', borderRadius: '8px', border: 'none', background: saving ? 'var(--bg-elevated)' : '#7c3aed', color: saving ? 'var(--text-tertiary)' : '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: saving ? 'default' : 'pointer' }}>
            {saving ? '⏳ Enregistrement…' : "✓ Créer l'aménagement"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal onglet Présences ────────────────────────────────────
function PresencesTab({ sessionId, presences, occurrences, session, inscriptions = [], groupes = [] }) {
  const [emargements, setEmargements] = useState([]);
  const [amenagements,setAmenagements]= useState([]);
  const [showAmenModal,setShowAmenModal] = useState(false);
  const [amenMsg, setAmenMsg] = useState('');

  // Date locale du jour sans décalage UTC
  const aujourdhui = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  })();

  const chargerEmargements = useCallback(async () => {
    try {
      const r = await apiFetch(`/emargement/session/${sessionId}`);
      setEmargements(r.data || []);
    } catch (e) { console.error(e); }
  }, [sessionId]);

  useEffect(() => { chargerEmargements(); }, [chargerEmargements]);

  const chargerAmenagements = useCallback(async () => {
    try {
      const r = await apiFetch(`/sessions/${sessionId}/amenagements`);
      setAmenagements(r.data || []);
    } catch (e) { /* silencieux */ }
  }, [sessionId]);

  useEffect(() => { chargerAmenagements(); }, [chargerAmenagements]);

  // Polling auto toutes les 30s si un émargement actif existe
  useEffect(() => {
    const actif = emargements.some(em => !em.clos && new Date(em.expires_at) > new Date());
    if (!actif) return;
    const interval = setInterval(() => chargerEmargements(), 30000);
    return () => clearInterval(interval);
  }, [emargements, chargerEmargements]);

  const presencesPassees = presences?.filter(p => (p.date_iso || String(p.date||'').substring(0,10)) <= aujourdhui) || [];
  const nbPresent = presencesPassees.filter(p => p.statut === 'Présent').length;
  const nbAbsent  = presencesPassees.filter(p => p.statut === 'Absent').length;
  const total     = presencesPassees.length;
  const taux      = total ? Math.round(100 * nbPresent / total) : 0;

  return (
    <div style={{ padding: '1rem 0' }}>

      {/* ── Bouton Aménagement d'horaires ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button
          onClick={() => !session.terminee && setShowAmenModal(true)}
          disabled={!!session.terminee}
          title={session.terminee ? 'Session clôturée — création désactivée' : undefined}
          style={{ padding: '0.45rem 1rem', borderRadius: '8px', border: 'none', background: session.terminee ? '#9ca3af' : '#7c3aed', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: session.terminee ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', opacity: session.terminee ? 0.6 : 1 }}>
          🕐 Aménagement d'horaires
        </button>
      </div>

      {amenMsg && (
        <div style={{ padding: '0.5rem 0.8rem', borderRadius: '8px', ...styleMessage(niveauMessage(amenMsg)), fontSize: '0.85rem', fontWeight: 600, marginBottom: '1rem' }}>
          {amenMsg}
        </div>
      )}

      {/* ── Liste des aménagements existants ── */}
      {amenagements.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            🕐 Aménagements d'horaires ({amenagements.length})
          </div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-tertiary)', marginBottom: '0.6rem' }}>
            Signature des attestations : menu « Diffusion » → Émargement actif « Aménagement d'horaires » → générer, puis afficher le 📷 QR : les stagiaires dont l'employeur a validé signent avec leur chevalet ou sur l'écran du formateur.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {amenagements.map(am => {
              const nbTotal   = (am.stagiaires || []).length;
              const nbValides = (am.stagiaires || []).filter(s => s.statut_entreprise === 'Validé').length;
              const nbSigues  = (am.stagiaires || []).filter(s => s.statut_signature === 'Signé').length;
              return (
                <div key={am.id} style={{ background: 'var(--bg-elevated)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: '10px', padding: '0.8rem 1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.4rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)' }}>
                      {new Date(am.date_creation).toLocaleDateString('fr-FR')} — {nbTotal} stagiaire{nbTotal > 1 ? 's' : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.73rem', background: 'rgba(124,58,237,0.1)', color: '#7c3aed', borderRadius: '6px', padding: '0.15rem 0.5rem', fontWeight: 600 }}>
                        Accord employeur : {nbValides}/{nbTotal}
                      </span>
                      <span style={{ fontSize: '0.73rem', background: 'rgba(22,163,74,0.1)', color: '#16a34a', borderRadius: '6px', padding: '0.15rem 0.5rem', fontWeight: 600 }}>
                        Attestations signées : {nbSigues}/{nbTotal}
                      </span>
                      <button onClick={() => ouvrirDocument({ titre: `Aménagement d'horaires — ${sessionId}`, url: `/amenagements/${am.id}/pdf`, nomFichier: `amenagement_${sessionId}_${am.id}.pdf` })} style={{ fontSize: '0.73rem', padding: '0.15rem 0.55rem', borderRadius: '6px', border: '1px solid #E87722', background: '#fff7ed', color: '#E87722', cursor: 'pointer', fontWeight: 600 }}>
                        📄 Document
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                    <strong>Motif :</strong> {am.motif}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)' }}>
                    {am.horaires_originaux && <><span style={{ textDecoration: 'line-through' }}>{am.horaires_originaux}</span> → </>}
                    <strong style={{ color: '#7c3aed' }}>{am.nouveaux_horaires}</strong>
                  </div>
                  {/* Liens attestations */}
                  {(am.stagiaires || []).length > 0 && (
                    <details style={{ marginTop: '0.5rem' }}>
                      <summary style={{ fontSize: '0.76rem', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
                        Détail des stagiaires
                      </summary>
                      <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                        {am.stagiaires.map(s => (
                          <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.3rem 0.5rem', background: 'var(--bg-card)', borderRadius: '6px', gap: '0.5rem' }}>
                            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{s.nom_stagiaire}</span>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                              <span style={{ color: s.statut_entreprise === 'Validé' ? '#16a34a' : s.statut_entreprise === 'Refusé' ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>
                                {s.statut_entreprise === 'Validé' ? '✓ Accord' : s.statut_entreprise === 'Refusé' ? '✗ Refusé' : '⏳ Attente'}
                              </span>
                              <span style={{ color: s.statut_signature === 'Signé' ? '#16a34a' : 'var(--text-tertiary)', fontWeight: 600 }}>
                                {s.statut_signature === 'Signé' ? '✓ Signé' : '⏳ À signer'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Détail des présences : résumé + tableau, replié par défaut */}
      <SectionRepliable title="📋 Détail des présences" defaultOpen={false}>
      <div style={{ padding: '1rem' }}>
      {/* Résumé présences */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[
          { label: 'Présents', val: nbPresent, color: '#16a34a', bg: '#f0fdf4' },
          { label: 'Absents',  val: nbAbsent,  color: '#f97316', bg: '#fff7ed' },
          { label: 'Total',    val: total,      color: '#6b7280', bg: '#f9fafb' },
          { label: 'Taux',     val: `${taux}%`, color: '#2563eb', bg: '#eff6ff' },
        ].map(({ label, val, color, bg }) => (
          <div key={label} style={{
            padding: '0.6rem 1.2rem', borderRadius: '8px',
            background: bg, border: `1px solid ${color}33`,
            textAlign: 'center', minWidth: 80,
          }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color }}>{val}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Table présences enrichie */}
      {presences?.length > 0 && (() => {
        // Lookup inscriptions : stagiaire_id → { nom, client }
        const inscrMap = {};
        (inscriptions || []).forEach(i => {
          if (!inscrMap[i.stagiaire_id]) {
            inscrMap[i.stagiaire_id] = {
              nom: i.nom_stagiaire_snapshot || i.stagiaire_id,
              client: i.nom_client_snapshot || '—',
            };
          }
        });

        // Lookup signatures : `${date}_${slot}_${stagiaire_id}` → true
        const signedMap = {};
        emargements.forEach(em => {
          const dateStr = em.date_emargt ? String(em.date_emargt).substring(0, 10) : '';
          const ids = Array.isArray(em.signataires_ids) ? em.signataires_ids : [];
          ids.forEach(sid => { signedMap[`${dateStr}_${em.slot || ''}_${sid}`] = true; });
        });

        // Y a-t-il un émargement actif ?
        const hasActif = emargements.some(em => !em.clos && new Date(em.expires_at) > new Date());

        return (
          <div>
            {hasActif && (
              <div style={{ marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#16a34a', background: 'rgba(22,163,74,0.1)', padding: '0.15rem 0.5rem', borderRadius: '10px', fontWeight: 600 }}>
                  🔄 Mise à jour auto toutes les 30s
                </span>
              </div>
            )}
            <DataTable
              columns={[
                { header: 'Date', render: r => formatDate(r.date) },
                { header: 'Cr.', key: 'slot' },
                { header: 'Réf.', key: 'stagiaire_id' },
                { header: 'Nom / Prénom', render: r => inscrMap[r.stagiaire_id]?.nom || r.stagiaire_id },
                { header: 'Client', render: r => inscrMap[r.stagiaire_id]?.client || '—' },
                { header: 'Statut', render: r => {
                  const dp = r.date_iso || String(r.date||'').substring(0,10);
                  if (dp > aujourdhui) return <Badge style={{ background: '#f1f5f9', color: '#94a3b8' }}>Planifié</Badge>;
                  if (!r.statut) return <Badge style={{ background: '#f1f5f9', color: '#94a3b8' }}>—</Badge>;
                  return <Badge>{r.statut}</Badge>;
                }},
                { header: 'Signé', render: r => {
                  const dp = r.date_iso || String(r.date||'').substring(0,10);
                  const key = `${dp}_${r.slot || ''}_${r.stagiaire_id}`;
                  return signedMap[key]
                    ? <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '1rem' }}>✓</span>
                    : <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>;
                }},
                { header: 'Groupe', key: 'sous_session_id' },
              ]}
              data={presences}
            />
          </div>
        );
      })()}
      </div>
      </SectionRepliable>

      {/* Modal aménagement */}
      {showAmenModal && (
        <ModalAmenagement
          session={session}
          inscriptions={inscriptions}
          groupes={groupes}
          sessionId={sessionId}
          onClose={() => setShowAmenModal(false)}
          onSaved={(emails) => {
            chargerAmenagements();
            const msg = emails.length > 0
              ? `✓ Aménagement créé — email(s) envoyé(s) à : ${emails.join(', ')}`
              : '✓ Aménagement créé (aucun email envoyé)';
            setAmenMsg(msg);
            setTimeout(() => setAmenMsg(''), 6000);
          }}
        />
      )}

    </div>
  );
}

function ModalModifierSession({ session, onSave, onClose }) {
  const { data: formationsData } = useApi('/formations', { limit: 500 }, []);
  const { data: formateursData } = useApi('/formateurs', {}, []);
  const { data: sallesData } = useApi('/salles', {}, []);
  const { data: paramsData } = useApi('/parametres');

  const origDateDebut = session.date_debut ? session.date_debut.split('T')[0] : '';
  const origDateFin   = session.date_fin   ? session.date_fin.split('T')[0]   : '';

  const [form, setForm] = useState({
    intitule_formation: session.intitule_formation || '',
    formation_id: session.formation_id || '',
    formateur_id: session.formateur_id || '',
    nom_formateur: session.nom_formateur || '',
    date_debut: origDateDebut,
    date_fin: origDateFin,
    duree: session.duree || '',
    horaires: session.horaires || '',
    type_session: session.type_session || '',
    lieu: session.lieu || '',
    salle_id: session.salle_id || '',
    occurrence_count: session.occurrence_count || 1,
    annulee: session.annulee || false,
    reportee: session.reportee || false,
    session_samedi: session.session_samedi || false,
    notes: session.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [formationSearch, setFormationSearch] = useState('');
  const [pendingAccesInterne, setPendingAccesInterne] = useState(false);
  const [sendingAcces, setSendingAcces] = useState(false);
  const [accesResult, setAccesResult] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function afterSave() {
    const sel = formateursData?.data?.find(f => f.formateur_id === form.formateur_id);
    if (form.formateur_id && sel?.type_formateur === 'interne') {
      setPendingAccesInterne(true);
    } else {
      onSave();
    }
  }

  // Mode dates libres (individuelles + plages consécutives)
  // Dates enregistrées de la session (jours uniques triés)
  const datesInitiales = (() => {
    const occ = session.occurrences || [];
    return [...new Set(occ.map(o => o.date_iso || (o.date ? String(o.date).substring(0, 10) : null)).filter(Boolean))].sort();
  })();
  const isoLocal = dt => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  // Session aux dates NON consécutives (saisie en « Dates libres ») : on ouvre directement ce mode, sinon changer le début ou la
  // fin en mode « Consécutives » remplirait tous les jours intermédiaires
  const joursNT = useJoursNonTravailles();                 // jours fériés + fermetures CEJ (exclus des jours générés en mode « Consécutives »)
  const sessionNonConsecutive = (() => {
    if (!datesInitiales.length || !origDateDebut || !origDateFin) return false;
    const attendues = [];
    const cur = new Date(origDateDebut + 'T00:00:00'); const fin = new Date(origDateFin + 'T00:00:00');
    while (cur <= fin) { const j = cur.getDay(); const iso = isoLocal(cur); if (j !== 0 && (j !== 6 || session.session_samedi) && !joursNT.has(iso)) attendues.push(iso); cur.setDate(cur.getDate() + 1); }
    return attendues.length !== datesInitiales.length || attendues.some((x, i) => x !== datesInitiales[i]);
  })();
  const [datesLibres, setDatesLibres] = useState(sessionNonConsecutive);
  // La liste des fériés arrive après l'ouverture : on réévalue le mode tant que l'utilisateur ne l'a pas choisi lui-même
  const modeChoisi = React.useRef(false);
  useEffect(() => { if (!modeChoisi.current) setDatesLibres(sessionNonConsecutive); }, [sessionNonConsecutive]);
  const [datesSelectionnees, setDatesSelectionnees] = useState(datesInitiales);
  const [inputDate, setInputDate] = useState('');
  const [plageDebut, setPlageDebut] = useState('');
  const [plageFin, setPlageFin] = useState('');
  const [plageSamedi, setPlageSamedi] = useState(false);

  const [infoDates, setInfoDates] = useState('');           // alerte non bloquante : date posée sur un jour férié / fermé
  function ajouterDate() {
    if (!inputDate) return;
    const alerte = libelleJourNonTravaille(joursNT, inputDate);
    setInfoDates(alerte ? `⚠ ${new Date(inputDate + 'T00:00:00').toLocaleDateString('fr-FR')} : ${alerte} — date ajoutée, vous pouvez la retirer.` : '');
    setDatesSelectionnees(prev => [...new Set([...prev, inputDate])].sort());
    setInputDate('');
  }
  function ajouterPlage() {
    if (!plageDebut || !plageFin) return;
    const d1 = new Date(plageDebut + 'T00:00:00'), d2 = new Date(plageFin + 'T00:00:00');
    if (d1 > d2) return;
    const nouvelles = [];
    const cur = new Date(d1);
    while (cur <= d2) { const j = cur.getDay(); if (j !== 0 && (j !== 6 || plageSamedi)) nouvelles.push(isoLocal(cur)); cur.setDate(cur.getDate() + 1); }
    // Les jours fériés et de fermeture CEJ sont ignorés comme les week-ends (on peut toujours les ajouter un par un)
    const ignores = nouvelles.filter(x => joursNT.has(x));
    const retenues = nouvelles.filter(x => !joursNT.has(x));
    setInfoDates(ignores.length ? `ℹ Ignoré dans la plage : ${ignores.map(x => `${new Date(x + 'T00:00:00').toLocaleDateString('fr-FR')} (${libelleJourNonTravaille(joursNT, x)})`).join(', ')}.` : '');
    setDatesSelectionnees(prev => [...new Set([...prev, ...(retenues.length ? retenues : nouvelles)])].sort());
    setPlageDebut(''); setPlageFin('');
  }
  function retirerDate(d) { setDatesSelectionnees(prev => prev.filter(x => x !== d)); }
  const fmtJour = d => new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });

  // Auto-calcul date_fin quand date_debut change (7h standard/jour)
  function handleDateDebutChange(value) {
    const newForm = { ...form, date_debut: value };
    if (value && form.duree && parseFloat(form.duree) > 0) {
      const nbJours = Math.max(1, Math.ceil(parseFloat(form.duree) / 7));
      const d = new Date(value);
      d.setDate(d.getDate() + nbJours - 1);
      newForm.date_fin = d.toISOString().split('T')[0];
    }
    setForm(newForm);
  }

  const params = paramsData || {};
  const LIEU_SUGGESTION = [params.societe_nom, params.societe_adresse1, params.societe_adresse2]
    .filter(Boolean).join(' - ');
  const inputStyle = {
    width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
  };

  function champ(label, children) {
    return (
      <div style={{ marginBottom: '0.7rem' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{label}</label>
        {children}
      </div>
    );
  }

  async function handleSave() {
    if (!form.intitule_formation) return setError('L\'intitulé est requis');
    if (datesLibres && datesSelectionnees.length === 0) return setError('Veuillez sélectionner au moins une date');
    setSaving(true); setError('');
    try {
      const body = { ...form };
      if (datesLibres && datesSelectionnees.length > 0) {
        body.dates_specifiques = datesSelectionnees;
        body.date_debut = datesSelectionnees[0];
        body.date_fin   = datesSelectionnees[datesSelectionnees.length - 1];
      } else {
        // Mode « Consécutives » : le serveur ne complète PAS les jours tout seul, c'est la fenêtre de confirmation qui décide
        body.regenerer_occurrences = false;
      }
      const maj = await apiFetch(`/sessions/${session.session_id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (maj?.avertissement_occurrences) window.alert('⚠ ' + maj.avertissement_occurrences);
      if (datesLibres) {
        afterSave();
      } else {
        const datesChanged = form.date_debut !== origDateDebut || form.date_fin !== origDateFin;
        if (datesChanged && form.date_debut && form.date_fin) {
          setShowRegenConfirm(true);
        } else {
          afterSave();
        }
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function confirmerRegen() {
    setRegenerating(true);
    try {
      await apiFetch(`/sessions/${session.session_id}/regenerer-occurrences`, { method: 'POST' });
    } catch (e) { /* on ferme quand même */ }
    setRegenerating(false);
    afterSave();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '580px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>✏ Modifier — {session.session_id}</h3>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '0.7rem', letterSpacing: '0.06em' }}>Formation</p>
        {champ('Formation du catalogue', (
          <>
            <input
              type="text"
              value={formationSearch}
              onChange={e => setFormationSearch(e.target.value)}
              placeholder="Rechercher par numéro ou nom…"
              style={{ ...inputStyle, marginBottom: '0.3rem' }}
            />
            <select value={form.formation_id || ''} onChange={e => {
              const f = formationsData?.data?.find(f => f.formation_id === parseInt(e.target.value));
              setFormationSearch('');
              setForm(prev => ({
                ...prev,
                formation_id: e.target.value,
                intitule_formation: f ? f.intitule : prev.intitule_formation,
                duree: f ? (f.duree_heures || prev.duree) : prev.duree,
              }));
            }} style={inputStyle}>
              <option value="">— Choisir ou laisser libre —</option>
              {(formationsData?.data || [])
                .filter(f => {
                  if (!formationSearch) return true;
                  const s = formationSearch.toLowerCase();
                  return String(f.numero || f.formation_id).toLowerCase().includes(s) || (f.intitule || '').toLowerCase().includes(s);
                })
                .map(f => (
                  <option key={f.formation_id} value={f.formation_id}>[{f.numero || f.formation_id}] {f.intitule?.substring(0, 60)}</option>
                ))}
            </select>
          </>
        ))}
        {champ('Intitulé *', <input type="text" value={form.intitule_formation} onChange={e => set('intitule_formation', e.target.value)} style={inputStyle} />)}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Planification</p>

        {/* Sélecteur de mode dates */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {[{ key: false, label: '📅 Consécutives' }, { key: true, label: '📆 Dates libres' }].map(({ key, label }) => (
            <button key={String(key)} type="button" onClick={() => { modeChoisi.current = true; setDatesLibres(key); }}
              style={{ padding: '0.3rem 0.85rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border)', background: datesLibres === key ? 'var(--accent-blue)' : 'var(--bg-elevated)', color: datesLibres === key ? '#fff' : 'var(--text-secondary)' }}>
              {label}
            </button>
          ))}
        </div>

        {!datesLibres ? (
          <>
            <div className="grid-2" style={{ gap: '0 0.7rem' }}>
              {champ('Date début', <input type="date" value={form.date_debut} onChange={e => handleDateDebutChange(e.target.value)} style={inputStyle} />)}
              {champ('Date fin', <input type="date" value={form.date_fin} onChange={e => set('date_fin', e.target.value)} style={inputStyle} title="Calculée automatiquement depuis la durée, modifiable manuellement" />)}
              {champ('Durée (heures)', <input type="number" value={form.duree} onChange={e => set('duree', e.target.value)} style={inputStyle} />)}
              {champ('Nb occurrences', <input type="number" value={form.occurrence_count} onChange={e => set('occurrence_count', e.target.value)} min="1" style={inputStyle} />)}
            </div>
          </>
        ) : (
          <div style={{ marginBottom: '0.75rem' }}>
            {/* Date unique */}
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.4rem' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', minWidth: '80px', fontWeight: 600 }}>Date</span>
              <input type="date" value={inputDate} onChange={e => setInputDate(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && ajouterDate()}
                style={{ ...inputStyle, flex: 1 }} />
              <button type="button" onClick={ajouterDate}
                style={{ padding: '0.45rem 0.8rem', borderRadius: '6px', border: 'none', background: 'var(--accent-blue)', color: '#fff', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                + Ajouter
              </button>
            </div>
            {/* Plage consécutive */}
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', minWidth: '80px', fontWeight: 600 }}>Plage</span>
              <input type="date" value={plageDebut} onChange={e => setPlageDebut(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              <input type="date" value={plageFin} onChange={e => setPlageFin(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                <input type="checkbox" checked={plageSamedi} onChange={e => setPlageSamedi(e.target.checked)} style={{ accentColor: 'var(--accent-blue)' }} /> Sam
              </label>
              <button type="button" onClick={ajouterPlage}
                style={{ padding: '0.45rem 0.8rem', borderRadius: '6px', border: 'none', background: 'var(--accent-blue)', color: '#fff', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                + Plage
              </button>
            </div>
            {/* Liste des dates */}
            {infoDates && <div role="status" style={{ margin: '0.4rem 0', fontSize: '0.8rem', fontWeight: 600, color: COULEUR_ATTENTION }}>{infoDates}</div>}
            {datesSelectionnees.length > 0 ? (
              <>
                <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', maxHeight: '200px', overflowY: 'auto', marginBottom: '0.4rem' }}>
                  {datesSelectionnees.map((d, i) => (
                    <div key={d} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.3rem 0.6rem', borderBottom: i < datesSelectionnees.length - 1 ? '1px solid var(--border)' : 'none', background: i % 2 === 0 ? 'var(--bg-elevated)' : 'transparent' }}>
                      <span style={{ fontSize: '0.85rem' }}>{fmtJour(d)}{joursNT.has(d) && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: 600, color: COULEUR_ATTENTION }}>⚠ {libelleJourNonTravaille(joursNT, d)}</span>}</span>
                      <button type="button" onClick={() => retirerDate(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '1.1rem', lineHeight: 1, padding: '0 0.2rem' }}>×</button>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  {datesSelectionnees.length} jour{datesSelectionnees.length > 1 ? 's' : ''} — du {new Date(datesSelectionnees[0] + 'T00:00:00').toLocaleDateString('fr-FR')} au {new Date(datesSelectionnees[datesSelectionnees.length - 1] + 'T00:00:00').toLocaleDateString('fr-FR')}
                </div>
              </>
            ) : (
              <div style={{ padding: '0.4rem 0', color: 'var(--text-tertiary)', fontSize: '0.82rem', fontStyle: 'italic', marginBottom: '0.5rem' }}>Aucune date sélectionnée</div>
            )}
            {champ('Durée (heures)', <input type="number" value={form.duree} onChange={e => set('duree', e.target.value)} style={inputStyle} />)}
          </div>
        )}
        {champ('Horaires', <input type="text" value={form.horaires} onChange={e => set('horaires', e.target.value)} style={inputStyle} />)}
        {champ('Type', (
          <select value={form.type_session} onChange={e => set('type_session', e.target.value)} style={inputStyle}>
            <option value="">— Non renseigné —</option>
            <option value="Inter">Inter</option>
            <option value="Intra">Intra</option>
          </select>
        ))}
        {champ(
          <span>Lieu <span style={{ fontWeight: 400, fontSize: '0.72rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Nom – adresse – CP et Ville</span></span>,
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={form.lieu}
              onChange={e => set('lieu', e.target.value)}
              style={inputStyle}
              onKeyDown={e => {
                if (e.key === 'Tab' && !form.lieu) set('lieu', LIEU_SUGGESTION);
              }}
            />
            {!form.lieu && (
              <span style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                padding: '0.5rem 0.7rem', color: 'var(--text-tertiary)',
                fontSize: '0.9rem', pointerEvents: 'none',
                display: 'flex', alignItems: 'center',
                overflow: 'hidden', whiteSpace: 'nowrap',
              }}>
                {LIEU_SUGGESTION}
              </span>
            )}
          </div>
        )}
        {champ('Salle', (
          <div style={{ display:'flex', gap:'0.4rem', alignItems:'center' }}>
            <select value={form.salle_id} onChange={e => set('salle_id', e.target.value)} style={{ ...inputStyle, flex: 1 }}>
              <option value="">— Aucune salle —</option>
              {(sallesData?.data || []).map(s => (
                <option key={s.salle_id} value={s.salle_id}>
                  {s.nom_salle}{s.capacite ? ` (${s.capacite} pers.)` : ''}
                </option>
              ))}
            </select>
            {form.salle_id && (() => {
              const sel = (sallesData?.data || []).find(s => String(s.salle_id) === String(form.salle_id));
              return sel ? <span style={{ display:'inline-block', width:22, height:22, borderRadius:4, flexShrink:0, background: sel.couleur || '#6b7280', border:'1px solid var(--border)' }} title={sel.nom_salle} /> : null;
            })()}
          </div>
        ))}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Formateur</p>
        {champ('Formateur', (
          <select
            value={form.formateur_id}
            onChange={e => {
              const f = formateursData?.data?.find(fo => fo.formateur_id === e.target.value);
              set('formateur_id', e.target.value);
              set('nom_formateur', f ? f.nom_formateur : '');
            }}
            style={inputStyle}
          >
            <option value="">— Choisir —</option>
            {formateursData?.data?.map(f => (
              <option key={f.formateur_id} value={f.formateur_id}>
                {[f.prenom, f.nom_formateur].filter(Boolean).join(' ')}
              </option>
            ))}
          </select>
        ))}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Statut</p>
        <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.7rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.88rem' }}>
            <input type="checkbox" checked={form.annulee} onChange={e => set('annulee', e.target.checked)} /> Annulée
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.88rem' }}>
            <input type="checkbox" checked={form.reportee} onChange={e => set('reportee', e.target.checked)} /> Reportée
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.88rem' }} title="Inclure le(s) samedi dans les créneaux de présence et le planning">
            <input type="checkbox" checked={form.session_samedi} onChange={e => set('session_samedi', e.target.checked)} /> Sessions le samedi
          </label>
        </div>

        {champ('Notes', <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />)}

        {error && <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : '✓ Enregistrer'}
          </button>
        </div>
      </div>

      {/* Modal accès formateur interne */}
      {pendingAccesInterne && (() => {
        const sel = formateursData?.data?.find(f => f.formateur_id === form.formateur_id);
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.75rem', width: '420px', maxWidth: '90vw' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.6rem' }}>👤 Formateur interne</div>
              <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginBottom: '1.2rem', lineHeight: 1.6 }}>
                Souhaitez-vous envoyer maintenant le lien d'accès à la session (QR code + PIN) à <strong>{sel?.nom_formateur}</strong> ?
              </p>
              {accesResult && (
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '1rem',
                  color: couleurMessage(accesResult === 'ok' ? 'succes' : 'attention') }}>
                  {accesResult === 'ok'
                    ? '✓ Email envoyé avec succès.'
                    : "⚠ Envoi échoué — vous pourrez le renvoyer depuis l'onglet Accès formateur."}
                </div>
              )}
              {!accesResult && (
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" onClick={onSave} disabled={sendingAcces}>Pas maintenant</button>
                  <button className="btn-primary" disabled={sendingAcces} onClick={async () => {
                    setSendingAcces(true);
                    try {
                      await apiFetch(`/sessions/${session.session_id}/formateur-token`, { method: 'POST', body: JSON.stringify({ formateur_id: form.formateur_id || null }) });
                      setAccesResult('ok');
                    } catch {
                      setAccesResult('error');
                    }
                    setSendingAcces(false);
                    setTimeout(onSave, 1500);
                  }}>
                    {sendingAcces ? '⏳ Envoi…' : "📧 Envoyer le lien d'accès"}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Modale de confirmation régénération des créneaux */}
      {showRegenConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '420px', maxWidth: '90vw' }}>
            <h3 style={{ marginBottom: '0.8rem', color: 'var(--text-primary)' }}>🗓 Dates modifiées</h3>
            <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              Les dates de la session ont changé.<br/>
              Voulez-vous <strong>régénérer les créneaux de présence</strong> en fonction des nouvelles dates ?<br/>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>⚠ Les créneaux et présences existants seront supprimés et recréés.</span>
              {sessionNonConsecutive && (
                <><br/><span style={{ fontSize: '0.8rem', color: '#f59e0b' }}>⚠ Cette session avait des dates non consécutives : la régénération les remplacera par tous les jours ouvrés entre le début et la fin. Pour garder des dates précises, répondez « Non » et utilisez « Dates libres ».</span></>
              )}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={onSave} disabled={regenerating}>
                Non, conserver les créneaux
              </button>
              <button className="btn-primary" onClick={confirmerRegen} disabled={regenerating}>
                {regenerating ? '⏳ Régénération...' : '🔄 Oui, régénérer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const API_BASE = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

// ── Statut commande formateur ────────────────────────────────────────────────
const COMMANDE_STATUTS = ['Brouillon', 'Envoyé', 'Validé', 'Refusé'];

function CommandeStatutBadge({ statut }) {
  const cfg = {
    'Brouillon': { color: 'var(--text-secondary)', bg: 'var(--bg-elevated)', label: '🔖 Brouillon' },
    'Envoyé':    { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  label: '📨 Envoyé' },
    'Validé':    { color: '#16a34a', bg: 'rgba(22,163,74,0.1)',   label: '✓ Validé' },
    'Refusé':    { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   label: '✗ Refusé' },
  }[statut] || { color: 'var(--text-tertiary)', bg: 'var(--bg-elevated)', label: statut };
  return (
    <span style={{
      fontSize: '0.78rem', fontWeight: 700, padding: '0.2rem 0.6rem',
      borderRadius: '4px', color: cfg.color, background: cfg.bg,
    }}>{cfg.label}</span>
  );
}

// ── Onglet Commande formateur ────────────────────────────────────────────────
function UneCommandeFormateur({ sessionId, formateurId, nomFormateur, dateDebut, dateFin, occurrences = [], sessionFormateurId = null }) {
  const { data: formateursData } = useApi('/formateurs', {}, []);
  const [commande, setCommande] = useState(undefined); // undefined=chargement, null=aucune
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // Lien de signature
  const [showLienModal, setShowLienModal]   = useState(false);
  const [emailLien, setEmailLien]           = useState('');
  const [loadingLien, setLoadingLien]       = useState(false);
  const [sendingEmailLien, setSendingEmailLien] = useState(false);
  const [msgLien, setMsgLien]               = useState('');
  const [lienGenere, setLienGenere]         = useState('');
  const [sendingRelance, setSendingRelance] = useState(false);
  const [showMotifInput, setShowMotifInput] = useState(false);
  const [motifRefus, setMotifRefus] = useState('');

  const token = localStorage.getItem('gf_formateur_token') || localStorage.getItem('gf_token');

  // Accès formateur QR
  const [fmtToken, setFmtToken]               = useState(undefined); // undefined=chargement
  const [genFmt, setGenFmt]                   = useState(false);
  const [showFmtModal, setShowFmtModal]       = useState(false);
  const [fmtJustGenerated, setFmtJustGenerated] = useState(false);
  const [emailFmt, setEmailFmt]               = useState('');
  const [sendingFmt, setSendingFmt]           = useState(false);
  const [msgFmt, setMsgFmt]                   = useState('');
  const [renvoi, setRenvoi]                   = useState(false);

  // Notification formateur (email unifié)
  const [showNotifModal, setShowNotifModal]   = useState(false);
  const [sendingNotif, setSendingNotif]       = useState(false);
  const [notifResult, setNotifResult]         = useState(null); // null | 'ok' | string(erreur)
  const [notifSmsErreur, setNotifSmsErreur]   = useState('');   // cause lisible quand le SMS du PIN n'est pas parti

  // Recherche par ID pour éviter les collisions sur des noms identiques
  const formateur = formateurId
    ? formateursData?.data?.find(f => f.formateur_id === formateurId)
    : null;
  // Formateur non interne : son accès à la session part AUTOMATIQUEMENT à la signature de son bon de commande (pas d'envoi direct avant)
  const accesBloque = !!formateur && formateur.type_formateur !== 'interne' && commande?.statut !== 'Validé';
  // Formateur interne : pas de bon de commande — carte « Notifier » + accès à la session (QR + PIN) uniquement
  const estInterne = !!formateur && formateur.type_formateur === 'interne';

  useEffect(() => {
    setLoading(true);
    const qp = formateurId ? `?formateur_id=${encodeURIComponent(formateurId)}` : '';
    fetch(`${API_BASE}/commandes-formateur/session/${sessionId}${qp}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => {
        setCommande(d);
        if (d) {
          setEmailLien(d.formateur_email || '');
          if (d.lien_token) setLienGenere(`${window.location.origin}/commande-formateur/${d.lien_token}`);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [sessionId, formateurId, token]);

  // Remplir emailLien depuis le formateur (ou l'OF si type='of')
  // Pour un OF : toujours écraser avec organisme_email même si le mail perso a déjà été pré-rempli
  useEffect(() => {
    const email = formateur?.type_formateur === 'of'
      ? (formateur?.organisme_email || formateur?.email)
      : formateur?.email;
    if (email && (formateur?.type_formateur === 'of' || !emailLien)) setEmailLien(email);
  }, [formateur?.email, formateur?.organisme_email, formateur?.type_formateur]); // eslint-disable-line

  useEffect(() => {
    const qp = formateurId ? `?formateur_id=${encodeURIComponent(formateurId)}` : '';
    fetch(`${API_BASE}/sessions/${sessionId}/formateur-token${qp}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => {
        const t = d.token ? d : null;
        setFmtToken(t);
        if (t?.formateur_email) setEmailFmt(t.formateur_email);
      })
      .catch(() => setFmtToken(null));
  }, [sessionId, formateurId, token]);

  // Quantité = nombre de créneaux réels pour CE formateur (occurrences filtrées)
  function calcQuantite(fmtId) {
    if (occurrences.length > 0) {
      const isMain = fmtId === sessionFormateurId;
      const mySlots = occurrences.filter(o =>
        isMain ? (!o.formateur_id || o.formateur_id === fmtId) : o.formateur_id === fmtId
      );
      const distinctDates = new Set(mySlots.map(o => o.date_iso || String(o.date || '').substring(0, 10)).filter(Boolean));
      if (distinctDates.size > 0) return distinctDates.size;
    }
    // Fallback si pas d'occurrences chargées
    if (dateDebut && dateFin) {
      const diff = Math.round((new Date(dateFin) - new Date(dateDebut)) / 86400000) + 1;
      if (diff > 0) return diff;
    }
    return 1;
  }

  async function creerCommande() {
    const fmtId = formateurId || formateur?.formateur_id;
    if (!fmtId) return alert('Formateur non trouvé dans la base. Vérifiez que le nom correspond à un formateur enregistré.');
    setSaving(true);
    try {
      const quantite = calcQuantite(fmtId);
      const r = await fetch(`${API_BASE}/commandes-formateur`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, formateur_id: fmtId, quantite }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setCommande(d);
      showMsg('✓ Commande créée', true);
    } catch (e) { showMsg('Erreur : ' + e.message, false); }
    finally { setSaving(false); }
  }

  async function changerStatut(statut, motif = '') {
    try {
      const r = await fetch(`${API_BASE}/commandes-formateur/${commande.id}/statut`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ statut, motif }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setCommande(prev => ({
        ...prev, statut,
        lien_token: statut === 'Refusé' ? null : prev.lien_token,
        motif_refus: statut === 'Refusé' ? (motif || prev.motif_refus) : prev.motif_refus,
      }));
      if (statut === 'Refusé') {
        setLienGenere('');
        setShowMotifInput(false);
      }
      showMsg('✓ Statut mis à jour', true);
    } catch (e) { showMsg('Erreur : ' + e.message, false); }
  }

  async function supprimerCommande() {
    if (!window.confirm('Supprimer cette commande ?')) return;
    await fetch(`${API_BASE}/commandes-formateur/${commande.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    setCommande(null);
    showMsg('Commande supprimée', true);
  }

  async function nouvelleCommande() {
    if (!window.confirm('Supprimer la commande refusée et en créer une nouvelle ?')) return;
    setSaving(true);
    try {
      await fetch(`${API_BASE}/commandes-formateur/${commande.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      setCommande(null);
      setLienGenere('');
      setMotifRefus('');
      setShowMotifInput(false);
      const fmtId = formateurId || formateur?.formateur_id;
      if (!fmtId) { showMsg('Formateur non trouvé dans la base', false); return; }
      const quantite = calcQuantite(fmtId);
      const r = await fetch(`${API_BASE}/commandes-formateur`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, formateur_id: fmtId, quantite }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setCommande(d);
      setEmailLien(
        formateur?.type_formateur === 'of'
          ? (formateur?.organisme_email || formateur?.email || d.formateur_email || '')
          : (d.formateur_email || formateur?.email || '')
      );
      showMsg('✓ Nouvelle commande créée', true);
    } catch (e) { showMsg('Erreur : ' + e.message, false); }
    finally { setSaving(false); }
  }

  function ouvrirModalLien() {
    setEmailLien(
      formateur?.type_formateur === 'of'
        ? (formateur?.organisme_email || formateur?.email || commande?.formateur_email || '')
        : (formateur?.email || commande?.formateur_email || '')
    );
    setMsgLien('');
    setLienGenere(commande?.lien_token ? `${window.location.origin}/commande-formateur/${commande.lien_token}` : '');
    setShowLienModal(true);
  }

  async function genererLien(envoyerEmail = false) {
    setLoadingLien(true); setMsgLien('');
    try {
      const r = await fetch(`${API_BASE}/commandes-formateur/${commande.id}/generer-lien`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur génération lien');
      const lienCree = `${window.location.origin}/commande-formateur/${d.token}`;
      setLienGenere(lienCree);
      setCommande(prev => ({ ...prev, statut: 'Envoyé', lien_token: d.token }));

      if (envoyerEmail && emailLien.trim()) {
        setSendingEmailLien(true);
        try {
          const re = await fetch(`${API_BASE}/commandes-formateur/${commande.id}/envoyer-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ email: emailLien.trim(), lien: lienCree }),
          });
          const de = await re.json();
          if (!re.ok) throw new Error(de.error);
          setMsgLien('✓ Email envoyé à ' + emailLien.trim());
          setTimeout(() => setShowLienModal(false), 2000);
        } catch (e) { setMsgLien('❌ ' + e.message); }
        finally { setSendingEmailLien(false); }
      } else {
        setShowLienModal(false);
      }
    } catch (e) { setMsgLien('❌ ' + e.message); }
    finally { setLoadingLien(false); }
  }

  async function renvoyerEmailLien() {
    if (!emailLien.trim()) return;
    setSendingEmailLien(true); setMsgLien('');
    try {
      const lien = lienGenere || (commande?.lien_token ? `${window.location.origin}/commande-formateur/${commande.lien_token}` : '');
      const re = await fetch(`${API_BASE}/commandes-formateur/${commande.id}/envoyer-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: emailLien.trim(), lien }),
      });
      const de = await re.json();
      if (!re.ok) throw new Error(de.error);
      setMsgLien('✓ Email renvoyé à ' + emailLien.trim());
      setTimeout(() => setShowLienModal(false), 2000);
    } catch (e) { setMsgLien('❌ ' + e.message); }
    finally { setSendingEmailLien(false); }
  }

  async function envoyerRelanceCommande() {
    if (!emailLien.trim() || !lienGenere) return;
    setSendingRelance(true); setMsgLien('');
    try {
      const re = await fetch(`${API_BASE}/commandes-formateur/${commande.id}/relancer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: emailLien.trim(), lien: lienGenere }),
      });
      const de = await re.json();
      if (!re.ok) throw new Error(de.error);
      setMsgLien('✓ Relance envoyée à ' + emailLien.trim());
      setTimeout(() => setMsgLien(''), 3000);
    } catch (e) { setMsgLien('❌ ' + e.message); }
    finally { setSendingRelance(false); }
  }

  function showMsg(text, ok) {
    setMsg({ text, ok });
    setTimeout(() => setMsg(''), 4000);
  }

  async function genererAccesFormateur() {
    setGenFmt(true); setMsgFmt('');
    try {
      const r = await fetch(`${API_BASE}/sessions/${sessionId}/formateur-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ formateur_id: formateurId || formateur?.formateur_id || null, email: emailFmt.trim() || formateur?.email || '' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setFmtToken(d);
      const parts = [];
      if (d.emailEnvoye) parts.push('lien envoyé par email');
      if (d.smsEnvoye) parts.push('code PIN envoyé par SMS');
      const causeSms = !d.smsEnvoye && d.smsErreur ? ` — ⚠ SMS non envoyé : ${d.smsErreur} (le code PIN n'est pas dans l'e-mail : transmettez-le autrement)` : '';
      setMsgFmt((parts.length ? `✓ Accès généré (${parts.join(', ')})` : '✓ Accès généré (email et SMS non envoyés)') + causeSms);
      setFmtJustGenerated(true);
    } catch (e) { setMsgFmt('❌ ' + e.message); }
    finally { setGenFmt(false); }
  }

  async function renvoyerEmailFmt() {
    setRenvoi(true); setMsgFmt('');
    try {
      const r = await fetch(`${API_BASE}/sessions/${sessionId}/formateur-token/renvoyer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ formateur_id: formateurId || formateur?.formateur_id || null, email: emailFmt.trim() || fmtToken?.formateur_email || '' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setMsgFmt(d.smsEnvoye ? '✓ Email renvoyé (code PIN renvoyé par SMS)' : `✓ Email renvoyé — ⚠ SMS non envoyé : ${d.smsErreur || 'cause inconnue'} (le code PIN n'est pas dans l'e-mail : transmettez-le autrement)`);
      setFmtToken(prev => ({
        ...prev,
        ...(emailFmt.trim() ? { formateur_email: emailFmt.trim(), email_envoye: true } : {}),
        sms_envoye: d.smsEnvoye || prev?.sms_envoye,
        smsEnvoye: d.smsEnvoye || prev?.smsEnvoye,
        sms_diagnostic: d.smsEnvoye ? null : (d.smsErreur || prev?.sms_diagnostic),
      }));
    } catch (e) { setMsgFmt('❌ ' + e.message); }
    finally { setRenvoi(false); }
  }

  function imprimerAccesFmt() {
    if (!fmtToken) return;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(fmtToken.lien)}`;
    // Dates réelles de ce formateur (occurrences filtrées)
    const fmtId2 = formateurId || formateur?.formateur_id;
    const isMain2 = fmtId2 === sessionFormateurId;
    const datesFormateur = [...new Set(
      occurrences
        .filter(o => isMain2 ? (!o.formateur_id || o.formateur_id === fmtId2) : o.formateur_id === fmtId2)
        .map(o => o.date_iso || String(o.date || '').substring(0, 10))
        .filter(Boolean)
    )].sort();
    const periodeStr = datesFormateur.length > 1
      ? datesFormateur.map(d => new Date(d).toLocaleDateString('fr-FR')).join(', ')
      : datesFormateur.length === 1
        ? new Date(datesFormateur[0]).toLocaleDateString('fr-FR')
        : (dateDebut ? `Du ${new Date(dateDebut).toLocaleDateString('fr-FR')} au ${new Date(dateFin || dateDebut).toLocaleDateString('fr-FR')}` : '');
    const w = window.open('', '_blank', 'width=520,height=750');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Accès formateur</title>
      <style>body{font-family:Arial,sans-serif;padding:2rem;text-align:center;background:#fff;color:#1e293b}
      h2{margin-bottom:0.3rem}
      .session-id{display:inline-block;background:#f1f5f9;border:1px solid #e2e8f0;border-radius:6px;padding:0.2rem 0.7rem;font-size:0.85rem;font-weight:700;color:#475569;margin-bottom:0.5rem}
      .formation{color:#1e293b;font-size:1rem;font-weight:600;margin-bottom:0.2rem}
      .periode{color:#64748b;font-size:0.88rem;margin-bottom:0.2rem}
      .formateur{color:#475569;font-size:0.85rem;font-style:italic;margin-bottom:0.8rem}
      .pin{font-size:3rem;font-weight:900;letter-spacing:0.3em;color:#E87722;margin:1.2rem 0}
      .info{font-size:0.82rem;color:#64748b;margin-top:1rem;border-top:1px solid #e2e8f0;padding-top:1rem}
      @media print{button{display:none}}</style></head><body>
      <h2>Accès Formateur</h2>
      <div class="session-id">Session ${sessionId}</div>
      <div class="formation">${fmtToken.intitule_formation || ''}</div>
      ${periodeStr ? `<div class="periode">${periodeStr}</div>` : ''}
      ${nomFormateur ? `<div class="formateur">Formateur : ${nomFormateur}</div>` : ''}
      <img src="${qrUrl}" width="220" height="220" style="border:4px solid #e2e8f0;border-radius:8px;margin:0.8rem 0"/>
      <p style="font-size:0.85rem;color:#64748b">Scannez ce QR Code, puis saisissez votre code PIN :</p>
      <div class="pin">${fmtToken.pin}</div>
      <div class="info">⚠ Ce code PIN est strictement confidentiel. Ne le communiquez pas aux stagiaires.</div>
      <button onclick="window.print()" style="margin-top:1.5rem;padding:0.6rem 1.5rem;border-radius:8px;border:none;background:#E87722;color:#fff;font-weight:700;cursor:pointer;font-size:1rem">Imprimer</button>
      </body></html>`);
    w.document.close();
  }

  if (loading) return <p style={{ padding: '1rem', color: 'var(--text-tertiary)' }}>Chargement...</p>;

  return (
    <div style={{ padding: '0.5rem 0' }}>

      {/* Modal notification formateur */}
      {showNotifModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', width: '460px', maxWidth: '95vw' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.5rem' }}>📧 Notifier le formateur</div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
              Un email sera envoyé à <strong>{formateur?.email || '—'}</strong> contenant :
            </p>
            <ul style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', marginBottom: '1rem', paddingLeft: '1.2rem', lineHeight: 1.8 }}>
              <li>📱 <strong>QR Code + lien</strong> — accès émargement &amp; questionnaires (le code PIN est envoyé séparément par SMS si un numéro est renseigné)</li>
              <li>📄 <strong>Fiche progrès</strong> — créée dans le système à son nom</li>
            </ul>
            {(notifResult === 'ok' || notifResult === 'ok-sms') && (
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#16a34a', marginBottom: '1rem' }}>
                ✓ Email envoyé avec succès.{notifResult === 'ok-sms' ? ' Code PIN envoyé par SMS.' : ''}
              </div>
            )}
            {notifResult === 'ok' && notifSmsErreur && (
              <div style={{ fontSize: '0.84rem', color: '#b45309', background: 'rgba(245,158,11,0.12)', borderRadius: 6, padding: '0.5rem 0.7rem', marginBottom: '1rem' }}>
                ⚠ Le code PIN n'a pas pu être envoyé par SMS : {notifSmsErreur}.<br />
                Le code PIN est visible dans « Accès formateur » ci-dessous ; transmettez-le au formateur par un autre moyen.
              </div>
            )}
            {notifResult && notifResult !== 'ok' && notifResult !== 'ok-sms' && (
              <div style={{ fontSize: '0.85rem', color: '#f87171', marginBottom: '1rem' }}>
                ⚠ {notifResult}
              </div>
            )}
            {!notifResult && (
              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={() => setShowNotifModal(false)} disabled={sendingNotif}>Annuler</button>
                <button className="btn-primary" disabled={sendingNotif || !formateur?.email} onClick={async () => {
                  setSendingNotif(true);
                  try {
                    const dNotif = await apiFetch(`/sessions/${sessionId}/notifier-formateur`, {
                      method: 'POST',
                      body: JSON.stringify({ formateur_id: formateurId || null }),
                    });
                    setNotifResult(dNotif?.sms_envoye ? 'ok-sms' : 'ok');
                    setNotifSmsErreur(dNotif?.sms_envoye ? '' : (dNotif?.sms_erreur || ''));
                    // La fenêtre se referme seule seulement si tout est parti ; sinon l'utilisateur lit l'avertissement et ferme
                    if (dNotif?.sms_envoye || !dNotif?.sms_erreur) setTimeout(() => { setShowNotifModal(false); setNotifResult(null); }, 2000);
                  } catch (err) {
                    setNotifResult(err.data?.error || err.message || 'Erreur lors de l\'envoi');
                  }
                  setSendingNotif(false);
                }}>
                  {sendingNotif ? '⏳ Envoi…' : '📧 Envoyer'}
                </button>
              </div>
            )}
            {notifResult === 'ok' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={() => { setShowNotifModal(false); setNotifResult(null); }}>Fermer</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* En-tête formateur / OF */}
      <div style={{ marginBottom: '1rem', padding: '0.7rem 1rem', borderRadius: '8px',
        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: '0.8rem', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          <span style={{ fontSize: '1.3rem' }}>{formateur?.type_formateur === 'of' ? '🏢' : '👤'}</span>
          <div>
            {formateur?.type_formateur === 'of' && formateur?.organisme_nom ? (
              <>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{formateur.organisme_nom}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                  Organisme de Formation · Intervenant : {nomFormateur}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{nomFormateur || '—'}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                  {formateur ? `Formateur enregistré · ID ${formateur.formateur_id} · ${{ interne: 'Interne', vacataire: 'Vacataire', of: 'OF' }[formateur.type_formateur] || formateur.type_formateur}` : 'Nom non associé à un formateur enregistré'}
                </div>
              </>
            )}
          </div>
        </div>
        {formateur?.email && accesBloque && (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', maxWidth: 260, textAlign: 'right' }}>
            Bon de commande à signer : l'accès à la session sera envoyé automatiquement à la signature.
          </span>
        )}
        {formateur?.email && !accesBloque && (
          <button
            onClick={() => { setNotifResult(null); setNotifSmsErreur(''); setShowNotifModal(true); }}
            style={{ padding: '0.4rem 0.9rem', borderRadius: '8px', border: 'none', background: '#E87722', color: '#fff', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            📧 Notifier
          </button>
        )}
      </div>

      {estInterne ? null : !commande ? (
        /* ── Pas encore de commande ── */
        <div>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.8rem' }}>
            {formateur?.type_formateur === 'of' && formateur?.organisme_nom
              ? `Aucune commande générée pour ${formateur.organisme_nom} sur cette session. La commande sera établie à l'ordre de l'organisme de formation, avec ${nomFormateur} en qualité d'intervenant.`
              : 'Aucune commande générée pour cet intervenant sur cette session. La commande reprend la formation, les dates, le lieu et le tarif de l\'intervenant.'
            }
          </p>
          {formateur ? (
            <button className="btn-primary" onClick={creerCommande} disabled={saving}>
              {saving ? '⏳...' : '📄 Créer la commande'}
            </button>
          ) : (
            <p style={{ fontSize: '0.82rem', color: '#f59e0b', fontStyle: 'italic' }}>
              ⚠ Associez d'abord cet intervenant dans la base pour pouvoir générer une commande.
            </p>
          )}
        </div>
      ) : (
        /* ── Commande existante ── */
        <div>
          {/* Carte statut */}
          <div style={{
            padding: '0.9rem 1rem', borderRadius: '8px', marginBottom: '1rem',
            background: 'var(--bg-elevated)', border: `1px solid ${
              commande.statut === 'Validé' ? 'rgba(22,163,74,0.4)' :
              commande.statut === 'Envoyé' ? 'rgba(245,158,11,0.4)' :
              commande.statut === 'Refusé' ? 'rgba(239,68,68,0.4)' : 'var(--border)'
            }`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                Commande {commande.commande_numero || commande.numero}
              </span>
              <CommandeStatutBadge statut={commande.statut} />
            </div>
            <div className="info-grid" style={{ marginBottom: '0' }}>
              <div className="info-row"><label>Unité</label><span>{commande.unite_tarif}</span></div>
              <div className="info-row"><label>Quantité</label><span>{commande.quantite}</span></div>
              <div className="info-row">
                <label>P.U. HT</label>
                <span>{commande.prix_unitaire_ht != null
                  ? new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(commande.prix_unitaire_ht) + ' €'
                  : '—'}</span>
              </div>
              <div className="info-row"><label>TVA</label><span>{Math.round((parseFloat(commande.taux_tva) || 0) * 100)}%</span></div>
              <div className="info-row"><label>Créée le</label><span>{commande.created_at ? new Date(commande.created_at).toLocaleDateString('fr-FR') : '—'}</span></div>
            </div>
          </div>

          {/* Signature via lien */}
          {commande.signataire_nom && (
            <div style={{ marginBottom: '0.75rem', padding: '0.55rem 0.8rem', borderRadius: '6px',
              background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)',
              fontSize: '0.82rem', color: '#16a34a' }}>
              ✅ Signé par <strong>{commande.signataire_nom}</strong>
              {commande.date_signature && (
                <span style={{ color: 'var(--text-tertiary)', marginLeft: '0.4rem' }}>
                  · le {new Date(commande.date_signature).toLocaleDateString('fr-FR')}
                </span>
              )}
            </div>
          )}

          {/* ── Encart signature inline ── */}
          {!commande.signataire_nom && (
            <div style={{ marginBottom: '0.9rem', padding: '0.9rem 1rem', borderRadius: '8px',
              background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                🔗 {lienGenere ? 'Renvoyer le lien de signature' : 'Envoyer pour signature'}
              </div>
              {lienGenere && (
                <div style={{ marginBottom: '0.6rem', fontSize: '0.78rem', color: '#f59e0b', fontStyle: 'italic' }}>
                  ✉ Lien {commande.lien_token ? 'précédemment envoyé' : 'généré'} — en attente de signature
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="email" value={emailLien} onChange={e => setEmailLien(e.target.value)}
                  placeholder="Email du formateur"
                  style={{ flex: 1, minWidth: '180px', padding: '0.4rem 0.6rem', borderRadius: '6px',
                    border: '1px solid var(--border)', background: 'var(--bg-card)',
                    color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                <button
                  onClick={lienGenere ? renvoyerEmailLien : () => genererLien(true)}
                  disabled={loadingLien || sendingEmailLien || sendingRelance || !emailLien.trim()}
                  style={{ padding: '0.4rem 0.9rem', borderRadius: '6px', border: 'none',
                    background: emailLien.trim() ? '#E87722' : 'var(--bg-elevated)',
                    color: emailLien.trim() ? '#fff' : 'var(--text-tertiary)',
                    fontSize: '0.85rem', fontWeight: 700, cursor: emailLien.trim() ? 'pointer' : 'default',
                    opacity: loadingLien || sendingEmailLien ? 0.7 : 1, whiteSpace: 'nowrap' }}>
                  {loadingLien || sendingEmailLien ? '⏳…' : lienGenere ? '✉ Renvoyer' : '✉ Envoyer'}
                </button>
                {lienGenere && (
                  <button
                    onClick={envoyerRelanceCommande}
                    disabled={sendingRelance || !emailLien.trim()}
                    title="Envoie un email de relance avec un template spécifique"
                    style={{ padding: '0.4rem 0.9rem', borderRadius: '6px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-elevated)',
                      color: 'var(--text-primary)',
                      fontSize: '0.85rem', fontWeight: 700,
                      cursor: emailLien.trim() ? 'pointer' : 'default',
                      opacity: sendingRelance ? 0.7 : 1, whiteSpace: 'nowrap' }}>
                    {sendingRelance ? '⏳…' : '🔔 Relancer'}
                  </button>
                )}
              </div>
              {lienGenere && (
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  <input readOnly value={lienGenere}
                    style={{ flex: 1, fontSize: '0.72rem', padding: '0.3rem 0.5rem',
                      border: '1px solid var(--border)', borderRadius: '4px',
                      background: 'var(--bg-card)', color: 'var(--text-tertiary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
                  <button onClick={() => navigator.clipboard.writeText(lienGenere)}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem', border: '1px solid var(--border)',
                      borderRadius: '4px', background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                      cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    📋 Copier
                  </button>
                </div>
              )}
              {msgLien && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', fontWeight: 600,
                  color: couleurMessage(niveauMessage(msgLien)) }}>
                  {msgLien}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
            {/* PDF */}
            <button
              onClick={() => ouvrirDocument({ titre: `Bon de commande ${commande.commande_numero || commande.numero || ''}`.trim(), url: `/commandes-formateur/${commande.id}/pdf` })}
              style={{ padding: '0.4rem 0.9rem', borderRadius: '6px', background: 'var(--bg-elevated)',
                border: '1px solid var(--border)', color: 'var(--text-primary)',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
              📄 PDF
            </button>

            {/* Changer le statut */}
            {commande.statut !== 'Validé' && (
              <>
                {(commande.statut === 'Envoyé' || commande.statut === 'Brouillon') && (
                  <>
                    <button className="btn-primary" onClick={() => changerStatut('Validé')}
                      style={{ fontSize: '0.82rem', padding: '0.4rem 0.9rem', background: '#16a34a', borderColor: '#16a34a' }}>
                      ✓ Marquer Validé
                    </button>
                    {!showMotifInput && (
                      <button className="btn-secondary" onClick={() => { setShowMotifInput(true); setMotifRefus(''); }}
                        style={{ fontSize: '0.82rem', padding: '0.4rem 0.9rem', color: '#ef4444', borderColor: 'rgba(239,68,68,0.4)' }}>
                        ✗ Refusé
                      </button>
                    )}
                  </>
                )}
                {commande.statut === 'Refusé' && (
                  <>
                    <button className="btn-secondary" onClick={() => changerStatut('Brouillon')}
                      style={{ fontSize: '0.82rem', padding: '0.4rem 0.9rem' }}>
                      ↩ Remettre en brouillon
                    </button>
                    <button className="btn-primary" onClick={nouvelleCommande} disabled={saving}
                      style={{ fontSize: '0.82rem', padding: '0.4rem 0.9rem', background: '#E87722', borderColor: '#E87722' }}>
                      {saving ? '⏳…' : '📄 Nouvelle commande'}
                    </button>
                  </>
                )}
              </>
            )}

            {/* Supprimer */}
            {commande.statut !== 'Validé' && (
              <button onClick={supprimerCommande}
                style={{ padding: '0.4rem 0.9rem', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.4)',
                  background: 'transparent', color: '#f87171', fontSize: '0.82rem', cursor: 'pointer' }}>
                🗑 Supprimer
              </button>
            )}
          </div>

          {/* Saisie du motif de refus */}
          {showMotifInput && (
            <div style={{ marginBottom: '0.8rem', padding: '0.8rem 1rem', borderRadius: '8px',
              background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#ef4444', marginBottom: '0.5rem' }}>
                ✗ Motif du refus
              </div>
              <textarea
                value={motifRefus}
                onChange={e => setMotifRefus(e.target.value)}
                placeholder="Indiquer le motif du refus…"
                rows={2}
                style={{ width: '100%', boxSizing: 'border-box', padding: '0.4rem 0.6rem',
                  borderRadius: '6px', border: '1px solid rgba(239,68,68,0.4)',
                  background: 'var(--bg-card)', color: 'var(--text-primary)',
                  fontSize: '0.85rem', resize: 'vertical', fontFamily: 'inherit' }}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button onClick={() => changerStatut('Refusé', motifRefus)}
                  style={{ padding: '0.35rem 0.9rem', borderRadius: '6px', border: 'none',
                    background: '#ef4444', color: '#fff', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
                  Confirmer le refus
                </button>
                <button onClick={() => setShowMotifInput(false)}
                  style={{ padding: '0.35rem 0.9rem', borderRadius: '6px',
                    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                    color: 'var(--text-secondary)', fontSize: '0.82rem', cursor: 'pointer' }}>
                  Annuler
                </button>
              </div>
            </div>
          )}

          {/* Affichage du motif de refus */}
          {commande.statut === 'Refusé' && commande.motif_refus && (
            <div style={{ marginBottom: '0.8rem', padding: '0.6rem 0.9rem', borderRadius: '6px',
              background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)',
              fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
              <span style={{ fontWeight: 700, color: '#ef4444' }}>Motif : </span>
              {commande.motif_refus}
            </div>
          )}

          {/* Sélecteur de statut direct */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>Forcer le statut :</span>
            <select
              value={commande.statut}
              onChange={e => changerStatut(e.target.value)}
              style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', borderRadius: '4px',
                border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}>
              {COMMANDE_STATUTS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Feedback */}
      {msg && (
        <div style={{ marginTop: '0.7rem', padding: '0.5rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem',
          ...styleMessage(!!msg.ok) }}>
          {msg.text}
        </div>
      )}

      {/* ── Section Accès Formateur QR ── */}
      {!estInterne && commande && commande.statut !== 'Validé' && (
        <div style={{ marginTop: '1.5rem', padding: '0.8rem 1rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
          🔒 L'accès formateur (QR + PIN) sera disponible une fois la commande <strong>validée</strong>.
        </div>
      )}
      {(estInterne || commande?.statut === 'Validé') && (
      <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.7rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>🔑 Accès formateur (QR + PIN)</div>
          {fmtToken ? (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              <button onClick={() => { setEmailFmt(fmtToken.formateur_email || formateur?.email || ''); setShowFmtModal(true); setMsgFmt(''); setFmtJustGenerated(false); }}
                style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                🔄 Regénérer
              </button>
              <button onClick={imprimerAccesFmt}
                style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                🖨 Imprimer
              </button>
            </div>
          ) : (
            <button onClick={() => { setEmailFmt(formateur?.email || ''); setShowFmtModal(true); setMsgFmt(''); setFmtJustGenerated(false); }}
              style={{ padding: '0.35rem 0.8rem', borderRadius: '6px', border: 'none', background: '#E87722', color: '#fff', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
              + Générer l'accès
            </button>
          )}
        </div>

        {fmtToken === undefined && <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)' }}>Chargement…</p>}
        {fmtToken === null && (
          <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>
            Générez un QR Code + PIN pour que le formateur accède à la saisie des présences depuis son téléphone.
          </p>
        )}
        {fmtToken && !fmtToken.cloture && (
          <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flexShrink: 0 }}>
              <QRCodeImg url={fmtToken.lien} size={130} />
            </div>
            <div style={{ flex: 1, minWidth: '180px' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Code PIN</div>
              <div style={{ fontSize: '2rem', fontWeight: 900, letterSpacing: '0.25em', color: '#E87722', marginBottom: '0.6rem' }}>{fmtToken.pin}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginBottom: '0.7rem' }}>
                {fmtToken.email_envoye
                  ? <>✅ Lien envoyé par email à <strong>{fmtToken.formateur_email}</strong><br/>{fmtToken.email_envoye_at ? `le ${new Date(fmtToken.email_envoye_at).toLocaleDateString('fr-FR')}` : ''}</>
                  : '⚠ Email non envoyé'}
                <br/>
                <StatutSmsPin envoye={fmtToken.sms_envoye} envoyeLe={fmtToken.sms_envoye_at} diagnostic={fmtToken.sms_diagnostic} />
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                <input type="email" placeholder="Email formateur"
                  value={emailFmt}
                  onChange={e => setEmailFmt(e.target.value)}
                  style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.8rem', width: '200px' }} />
                <button onClick={renvoyerEmailFmt} disabled={renvoi || !emailFmt.trim()}
                  style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', opacity: renvoi ? 0.7 : 1 }}>
                  {renvoi ? '⏳…' : '✉ Renvoyer'}
                </button>
              </div>
            </div>
          </div>
        )}
        {fmtToken && fmtToken.cloture && (
          <p style={{ fontSize: '0.85rem', color: '#10b981' }}>✅ Session clôturée par le formateur{fmtToken.cloture_at ? ` le ${new Date(fmtToken.cloture_at).toLocaleDateString('fr-FR')}` : ''}</p>
        )}

        {msgFmt && (
          <div style={{ marginTop: '0.6rem', fontSize: '0.82rem', color: couleurMessage(niveauMessage(msgFmt)), fontWeight: 600 }}>{msgFmt}</div>
        )}
      </div>
      )}

      {/* ── Modal génération accès formateur ── */}
      {showFmtModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '460px', maxWidth: '90vw' }}>
            {!fmtJustGenerated ? (
              <>
                <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>🔑 Accès formateur</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
                  Un QR Code et un code PIN seront générés pour <strong>{nomFormateur}</strong>.
                  Le lien sera envoyé par email si une adresse est renseignée, et le code PIN par SMS si un numéro de téléphone est renseigné.
                </p>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
                  Email du formateur
                </label>
                <input type="email" value={emailFmt} onChange={e => setEmailFmt(e.target.value)}
                  placeholder="formateur@exemple.fr" autoFocus
                  style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '1.5rem' }} />
                {msgFmt && (
                  <p style={{ fontSize: '0.85rem', marginBottom: '1rem', color: '#dc2626', fontWeight: 600 }}>{msgFmt}</p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" onClick={() => setShowFmtModal(false)} disabled={genFmt}>Annuler</button>
                  <button className="btn-primary" onClick={genererAccesFormateur} disabled={genFmt}>
                    {genFmt ? '⏳...' : '🔑 Générer'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>🔑 Accès formateur généré</h3>
                <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  <div style={{ flexShrink: 0 }}>
                    <QRCodeImg url={fmtToken.lien} size={140} />
                  </div>
                  <div style={{ flex: 1, minWidth: '180px' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Code PIN</div>
                    <div style={{ fontSize: '2.2rem', fontWeight: 900, letterSpacing: '0.3em', color: '#E87722', marginBottom: '0.6rem' }}>{fmtToken.pin}</div>
                    <div style={{ fontSize: '0.78rem', color: fmtToken.email_envoye ? '#10b981' : 'var(--text-tertiary)', marginBottom: '0.3rem' }}>
                      {fmtToken.email_envoye
                        ? <>✅ Lien envoyé par email à <strong>{fmtToken.formateur_email}</strong></>
                        : '⚠ Email non envoyé'}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: fmtToken.smsEnvoye ? '#10b981' : 'var(--text-tertiary)', marginBottom: '0.7rem' }}>
                      <StatutSmsPin envoye={fmtToken.smsEnvoye ?? fmtToken.sms_envoye} diagnostic={fmtToken.sms_diagnostic} />
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <input type="email" placeholder="Email formateur"
                        value={emailFmt} onChange={e => setEmailFmt(e.target.value)}
                        style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem', width: '190px' }} />
                      <button onClick={renvoyerEmailFmt} disabled={renvoi || !emailFmt.trim()}
                        style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', opacity: renvoi ? 0.7 : 1 }}>
                        {renvoi ? '⏳…' : '✉ Renvoyer'}
                      </button>
                    </div>
                  </div>
                </div>
                {msgFmt && (
                  <p style={{ fontSize: '0.82rem', marginBottom: '0.8rem', color: couleurMessage(niveauMessage(msgFmt)), fontWeight: 600 }}>{msgFmt}</p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" onClick={imprimerAccesFmt}>🖨 Imprimer</button>
                  <button className="btn-primary" onClick={() => setShowFmtModal(false)}>Fermer</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConventionExterneZone
        sessionId={sessionId}
        titre="🧾 Facture formateur"
        categorie={`facture_formateur_${formateurId}`}
      />

    </div>
  );
}

// ── Wrapper multi-formateurs ─────────────────────────────────────────────
// modules = data.modules, occurrences = data.occurrences (formateur par jour)
function CommandeFormateurTab({ sessionId, formateurId, nomFormateur, dateDebut, dateFin, modules = [], occurrences = [] }) {
  const { data: formateursData } = useApi('/formateurs', {}, []);
  const [expanded, setExpanded] = useState(formateurId || null);

  // Liste des formateurs externes : principal + modules + formateurs de remplacement (créneaux)
  const formateurs = React.useMemo(() => {
    const list = [];
    const seen = new Set();
    if (formateurId && nomFormateur) {
      list.push({ formateur_id: formateurId, nom: nomFormateur, label: 'Formateur principal' });
      seen.add(formateurId);
    }
    modules.forEach(mod => {
      if (!mod.formateur_id || seen.has(mod.formateur_id)) return;
      const fmt = formateursData?.data?.find(f => f.formateur_id === mod.formateur_id);
      if ((fmt?.type_formateur ?? mod.formateur_type) === 'interne') return;
      seen.add(mod.formateur_id);
      list.push({
        formateur_id: mod.formateur_id,
        nom: fmt?.nom_formateur || mod.formateur_nom || mod.formateur_id,
        label: `Module ${mod.ordre} — ${mod.intitule_module}`,
      });
    });
    // Formateurs assignés via l'onglet Créneaux (formateur du jour)
    occurrences.forEach(occ => {
      if (!occ.formateur_id || seen.has(occ.formateur_id)) return;
      const fmt = formateursData?.data?.find(f => f.formateur_id === occ.formateur_id);
      if (fmt?.type_formateur === 'interne') return;
      seen.add(occ.formateur_id);
      const nom = occ.occurrence_formateur_nom || fmt?.nom_formateur || occ.formateur_id;
      list.push({
        formateur_id: occ.formateur_id,
        nom,
        label: 'Formateur remplaçant (créneaux)',
      });
    });
    return list;
  }, [formateurId, nomFormateur, modules, occurrences, formateursData]);

  if (formateurs.length === 0) return null;
  // Cas standard (1 formateur) : rendu direct sans accordéon
  if (formateurs.length === 1) {
    return <UneCommandeFormateur sessionId={sessionId} formateurId={formateurs[0].formateur_id} nomFormateur={formateurs[0].nom} dateDebut={dateDebut} dateFin={dateFin} occurrences={occurrences} sessionFormateurId={formateurId} />;
  }

  const couleurs = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#ef4444'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', padding: '0.5rem 0' }}>
      <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>
        Plusieurs formateurs interviennent sur cette session. Gérez chaque commande séparément.
      </p>
      {formateurs.map((fmt, i) => {
        const isOpen = expanded === fmt.formateur_id;
        const couleur = couleurs[i % couleurs.length];
        return (
          <div key={fmt.formateur_id} style={{ border: '1px solid var(--border)', borderLeft: `4px solid ${couleur}`, borderRadius: '10px', overflow: 'hidden' }}>
            <div
              onClick={() => setExpanded(e => e === fmt.formateur_id ? null : fmt.formateur_id)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.75rem 1rem', cursor: 'pointer', background: isOpen ? 'var(--bg-elevated)' : 'var(--bg-card)', userSelect: 'none' }}>
              <span style={{ fontSize: '1.1rem' }}>👤</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{fmt.nom}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{fmt.label}</div>
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', flexShrink: 0 }}>{isOpen ? '▲' : '▼'}</span>
            </div>
            {isOpen && (
              <div style={{ borderTop: '1px solid var(--border)', padding: '0.75rem 1rem', background: 'var(--bg-card)' }}>
                <UneCommandeFormateur
                  sessionId={sessionId}
                  formateurId={fmt.formateur_id}
                  nomFormateur={fmt.nom}
                  dateDebut={dateDebut}
                  dateFin={dateFin}
                  occurrences={occurrences}
                  sessionFormateurId={formateurId}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Zone dépôt conventions externes ─────────────────────────────────────
function ConventionExterneZone({ sessionId, titre, categorie }) {
  const cat = categorie || 'convention';
  const token = localStorage.getItem('gf_formateur_token') || localStorage.getItem('gf_token');
  const inputRef = React.useRef();
  const [docs, setDocs] = React.useState([]);
  const [dragOver, setDragOver] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [renameId, setRenameId] = React.useState(null);
  const [renameVal, setRenameVal] = React.useState('');
  const [showDrop, setShowDrop] = React.useState(false);
  const [showFiles, setShowFiles] = React.useState(false);

  const load = React.useCallback(() => {
    fetch(`${API_BASE}/sessions/${sessionId}/documents?categorie=${cat}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setDocs(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [sessionId, token, cat]);

  React.useEffect(() => { load(); }, [load]);

  async function upload(file) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('categorie', cat);
      const r = await fetch(`${API_BASE}/sessions/${sessionId}/documents`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).error);
      load();
    } catch (e) { alert('Erreur upload : ' + e.message); }
    finally { setUploading(false); }
  }

  function handleDrop(e) {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) upload(file);
  }

  async function supprimer(doc) {
    if (!window.confirm(`Supprimer "${doc.nom_affiche || doc.nom_original}" ?`)) return;
    await fetch(`${API_BASE}/sessions/documents/${doc.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  }

  async function renommer(doc) {
    if (!renameVal.trim()) return;
    await fetch(`${API_BASE}/sessions/documents/${doc.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ nom_affiche: renameVal.trim() }),
    });
    setRenameId(null);
    load();
  }

  function ouvrirDoc(doc) {
    ouvrirDocument({ titre: doc.nom_affiche || doc.nom_original || 'Document', url: `/sessions/documents/${doc.id}/download`, nomFichier: doc.nom_original });
  }

  function fileIcon(doc) {
    const n = (doc.nom_original || '').toLowerCase();
    if (n.endsWith('.pdf')) return '📄';
    if (n.match(/\.(jpe?g|png|gif|webp)$/)) return '🖼️';
    if (n.match(/\.(doc|docx)$/)) return '📝';
    return '📎';
  }

  function fmtSize(b) {
    if (!b) return '';
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} Ko`;
    return `${(b / 1024 / 1024).toFixed(1)} Mo`;
  }

  return (
    <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '10px' }}>

      {/* En-tête avec contrôles */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: (showDrop || showFiles) ? '0.7rem' : 0 }}>
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)', flex: 1 }}>
          {titre || '📎 Convention(s) externe(s) déposée(s)'}
        </span>
        {/* Toggle dépôt */}
        <label title="Afficher / masquer la zone de dépôt" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-secondary)', userSelect: 'none' }}>
          <input type="checkbox" checked={showDrop} onChange={e => setShowDrop(e.target.checked)} style={{ cursor: 'pointer' }} />
          Déposer
        </label>
        {/* Badge + flèche fichiers */}
        <button onClick={() => setShowFiles(f => !f)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'none', border: '1px solid var(--border)', borderRadius: '20px', padding: '0.15rem 0.55rem', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          {docs.length > 0 && (
            <span style={{ background: '#E87722', color: '#fff', borderRadius: '10px', padding: '0 0.4rem', fontSize: '0.72rem', fontWeight: 700 }}>
              {docs.length}
            </span>
          )}
          <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: showFiles ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
        </button>
      </div>

      {/* Zone drag & drop */}
      {showDrop && (
        <div
          onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => { if (!uploading) inputRef.current.click(); }}
          style={{
            border: `2px dashed ${dragOver ? '#E87722' : 'rgba(245,158,11,0.5)'}`,
            borderRadius: '8px', padding: '1.2rem 1rem', textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            background: dragOver ? 'rgba(232,119,34,0.06)' : 'transparent',
            color: 'var(--text-secondary)', fontSize: '0.85rem', transition: 'all 0.15s',
            marginBottom: showFiles ? '0.75rem' : 0,
          }}>
          <input ref={inputRef} type="file" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ''; }} />
          {uploading
            ? <span>⏳ Envoi en cours...</span>
            : <><div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>📁</div>
                <strong>Cliquez ou déposez un fichier ici</strong>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.2rem' }}>PDF, Word, images... (max. 100 Mo)</div>
              </>
          }
        </div>
      )}

      {/* Liste des documents déposés */}
      {showFiles && (
        <>
          {docs.length === 0 && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', fontStyle: 'italic', margin: 0 }}>Aucun document déposé.</p>
          )}
          {docs.map(doc => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.7rem', borderRadius: '7px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{fileIcon(doc)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {renameId === doc.id ? (
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <input autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') renommer(doc); if (e.key === 'Escape') setRenameId(null); }}
                      style={{ flex: 1, padding: '0.25rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                    <button onClick={() => renommer(doc)} style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: 'none', background: '#16a34a', color: '#fff', fontSize: '0.8rem', cursor: 'pointer' }}>✓</button>
                    <button onClick={() => setRenameId(null)} style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: '0.8rem', cursor: 'pointer' }}>✕</button>
                  </div>
                ) : (
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.nom_affiche || doc.nom_original}
                  </div>
                )}
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '1px' }}>
                  {fmtSize(doc.taille)}{doc.taille ? ' · ' : ''}Déposé le {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fr-FR') : '—'}
                </div>
              </div>
              <button onClick={() => ouvrirDoc(doc)} title="Aperçu / Télécharger"
                style={{ padding: '0.25rem 0.55rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>
                👁
              </button>
              <button onClick={() => { setRenameId(doc.id); setRenameVal(doc.nom_affiche || doc.nom_original || ''); }}
                title="Renommer"
                style={{ padding: '0.25rem 0.55rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>
                ✏
              </button>
              <button onClick={() => supprimer(doc)} title="Supprimer"
                style={{ padding: '0.25rem 0.55rem', borderRadius: '5px', border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#f87171', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>
                🗑
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// « Émargement actif » : les deux modes s'appuient sur les émargements de la session (présence/certificat, ou signature des aménagements d'horaires)
const estTypeEmargement = t => t === 'emargement' || t === 'amenagement';

// ── Onglet Documents ────────────────────────────────────────────────────
const TYPES_DOC = [
  { value: 'tout',          label: 'Tous les documents' },
  { value: 'etiquettes',    label: 'Étiquettes' },
  { value: 'certificat',    label: 'Certificat de réalisation' },
  { value: 'attestations',  label: 'Attestation de formation' },
  { value: 'chevalet',      label: 'Chevalet QR' },
];

function imprimerUrl(url, titre) {
  return ouvrirDocument({ titre, url });
}



// ══════════════════════════════════════════════════════════════════════════════
// ONGLET RÉSULTATS QUESTIONNAIRES
// ══════════════════════════════════════════════════════════════════════════════

const SMILEY_LABELS = ['', 'Pas du tout', 'Insuffisant', 'Moyen', 'Bien', 'Excellent'];
const SMILEY_EMOJIS = ['', '😞', '😕', '😐', '🙂', '😄'];
const TYPE_COLORS = { positionnement: '#7c3aed', evaluation: '#2563eb', evaluation_formative: '#ea580c', satisfaction: '#16a34a' };
const TYPE_LABELS = { positionnement: 'Positionnement', evaluation: 'Évaluation', evaluation_formative: 'Évaluation formative', satisfaction: 'Satisfaction' };
const TYPES_Q_DEFAULT = [
  { val: 'positionnement',       label: 'Positionnement',        color: '#7c3aed', bg: '#f5f3ff', is_common: false },
  { val: 'evaluation',           label: 'Évaluation',            color: '#2563eb', bg: '#eff6ff', is_common: false },
  { val: 'evaluation_formative', label: 'Évaluation formative',  color: '#ea580c', bg: '#fff7ed', is_common: false },
  { val: 'satisfaction',         label: 'Satisfaction',          color: '#16a34a', bg: '#f0fdf4', is_common: true  },
];

// Questionnaire de satisfaction par défaut (même structure que QuestionnairePublic.jsx)
const SATISFACTION_DEFAULT_DEF = {
  questionnaire_id: 'satisfaction_defaut',
  titre: 'Questionnaire de satisfaction',
  type: 'satisfaction',
  sections: ['A propos du centre de formation', 'A propos du formateur', 'A propos du contenu de la formation'],
  questions: [
    { question_id: 's1', texte: "L'accès au centre de formation", type_reponse: 'smiley', section: 0 },
    { question_id: 's2', texte: "L'accueil à votre arrivée en salle de formation", type_reponse: 'smiley', section: 0 },
    { question_id: 's3', texte: "La salle de formation", type_reponse: 'smiley', section: 0 },
    { question_id: 's4', texte: "La communication et le comportement général du formateur", type_reponse: 'smiley', section: 1 },
    { question_id: 's5', texte: "Le niveau pédagogique du formateur", type_reponse: 'smiley', section: 1 },
    { question_id: 's6', texte: "La pertinence et la mise en situation", type_reponse: 'smiley', section: 1 },
    { question_id: 's7', texte: "La satisfaction des attentes personnelles", type_reponse: 'smiley', section: 2 },
    { question_id: 's8', texte: "La qualité du contenu du support pédagogique", type_reponse: 'smiley', section: 2 },
    { question_id: 's9', texte: "L'évaluation finale", type_reponse: 'smiley', section: 2 },
    { question_id: 's10', texte: "Avez-vous des remarques et des suggestions ?", type_reponse: 'texte', section: null },
  ],
};

function ScoreBarre({ val, max }) {
  const pct = max ? Math.round(100 * val / max) : 0;
  const color = pct >= 80 ? '#16a34a' : pct >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
      <div style={{ flex: 1, height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: color, borderRadius: '4px', transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: '0.8rem', fontWeight: 700, color, minWidth: '35px', textAlign: 'right' }}>{val.toFixed(1)}/{max}</span>
    </div>
  );
}

function StatsSmiley({ reponses, questionId }) {
  const vals = reponses.map(r => {
    const rep = r.reponses || {};
    return typeof rep[questionId] === 'number' ? rep[questionId] : null;
  }).filter(v => v !== null);
  if (!vals.length) return <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>Aucune reponse</span>;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  const counts = [0, 0, 0, 0, 0];
  vals.forEach(v => { if (v >= 1 && v <= 5) counts[v - 1]++; });
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.4rem' }}>
        <span style={{ fontSize: '1.4rem' }}>{SMILEY_EMOJIS[Math.round(avg)]}</span>
        <ScoreBarre val={avg} max={5} />
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{vals.length} rep.</span>
      </div>
      <div style={{ display: 'flex', gap: '0.3rem' }}>
        {counts.map((c, i) => (
          <div key={i} title={SMILEY_LABELS[i + 1] + ': ' + c}
            style={{ flex: 1, textAlign: 'center', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
            <div>{SMILEY_EMOJIS[i + 1]}</div>
            <div style={{ fontWeight: c > 0 ? 700 : 400, color: c > 0 ? 'var(--text-primary)' : '#ccc' }}>{c}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsNote({ reponses, questionId }) {
  const vals = reponses.map(r => {
    const rep = r.reponses || {};
    return typeof rep[questionId] === 'number' ? rep[questionId] : null;
  }).filter(v => v !== null);
  if (!vals.length) return <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>Aucune reponse</span>;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
      <ScoreBarre val={avg} max={10} />
      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{vals.length} rep.</span>
    </div>
  );
}

function StatsChoix({ reponses, questionId, options, reponseCorrecte }) {
  const vals = reponses.map(r => (r.reponses || {})[questionId]).filter(Boolean);
  if (!vals.length) return <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>Aucune reponse</span>;
  const counts = {};
  vals.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
  const bonnes = Array.isArray(reponseCorrecte) ? reponseCorrecte : [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
      {(options || Object.keys(counts)).map(opt => {
        const isCorrecte = bonnes.includes(opt);
        return (
          <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
            <span style={{ minWidth: '100px', color: isCorrecte ? '#16a34a' : 'var(--text-secondary)', fontWeight: isCorrecte ? 700 : 400 }}>
              {isCorrecte ? '✓ ' : ''}{opt}
            </span>
            <div style={{ flex: 1, height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: (((counts[opt] || 0) / vals.length) * 100) + '%', height: '100%', background: isCorrecte ? '#16a34a' : '#2563eb', borderRadius: '3px' }} />
            </div>
            <span style={{ fontWeight: 700, minWidth: '25px', textAlign: 'right' }}>{counts[opt] || 0}</span>
          </div>
        );
      })}
    </div>
  );
}

function StatsOuiNon({ reponses, questionId }) {
  const vals = reponses.map(r => (r.reponses || {})[questionId]).filter(Boolean);
  if (!vals.length) return <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>Aucune reponse</span>;
  const oui = vals.filter(v => v === 'Oui').length;
  const non = vals.filter(v => v === 'Non').length;
  const pctOui = Math.round(100 * oui / vals.length);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', fontSize: '0.82rem' }}>
      <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ Oui : {oui} ({pctOui}%)</span>
      <span style={{ color: '#dc2626', fontWeight: 700 }}>✗ Non : {non} ({100 - pctOui}%)</span>
    </div>
  );
}

function StatsTexte({ reponses, questionId }) {
  const vals = reponses.map(r => (r.reponses || {})[questionId]).filter(v => v && String(v).trim());
  if (!vals.length) return <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>Aucun commentaire</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '150px', overflowY: 'auto' }}>
      {vals.map((v, i) => (
        <div key={i} style={{ padding: '0.3rem 0.6rem', background: 'var(--bg-card)', borderRadius: '6px', fontSize: '0.82rem', color: 'var(--text-primary)', borderLeft: '2px solid #e2e8f0' }}>
          "{v}"
        </div>
      ))}
    </div>
  );
}

function StatsPhoto({ reponses, questionId }) {
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const vals = reponses.map(r => (r.reponses || {})[questionId]).filter(v => v?.photo_url);
  if (!vals.length) return <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>Aucune photo</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
      {vals.map((v, i) => (
        <a key={i} href={apiBase + v.photo_url} target="_blank" rel="noopener noreferrer">
          <img src={apiBase + v.photo_url} alt={'Photo ' + (i + 1)} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)' }} />
        </a>
      ))}
    </div>
  );
}

// ── Zone dépôt fichiers évaluations scannées ─────────────────────────────────
function EvaluationDocsZone({ sessionId, stagiaires }) {
  const token = localStorage.getItem('gf_formateur_token') || localStorage.getItem('gf_token');
  const inputRef = React.useRef();
  const [docs, setDocs] = React.useState([]);
  const [selectedVal, setSelectedVal] = React.useState('');
  const [dragOver, setDragOver] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [showDrop, setShowDrop] = React.useState(false);
  const [showFiles, setShowFiles] = React.useState(false);

  const load = React.useCallback(() => {
    fetch(`${API_BASE}/sessions/${sessionId}/evaluation-docs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setDocs(Array.isArray(d) ? d : [])).catch(() => {});
  }, [sessionId, token]);

  React.useEffect(() => { load(); }, [load]);

  async function upload(file) {
    if (!selectedVal) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      if (selectedVal !== 'session') fd.append('stagiaire_id', selectedVal);
      const r = await fetch(`${API_BASE}/sessions/${sessionId}/evaluation-docs`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).error);
      load();
    } catch (e) { alert('Erreur upload : ' + e.message); }
    setUploading(false);
  }

  async function supprimer(docId) {
    if (!window.confirm('Supprimer ce fichier ?')) return;
    await fetch(`${API_BASE}/sessions/evaluation-docs/${docId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  }

  function ouvrirDoc(docId) {
    const doc = docs.find(d => d.id === docId);
    ouvrirDocument({ titre: doc?.nom_original || 'Document d\'évaluation', url: `/sessions/evaluation-docs/${docId}/download`, nomFichier: doc?.nom_original });
  }

  const docsSession = docs.filter(d => !d.stagiaire_id);
  const docsByStagId = {};
  docs.filter(d => d.stagiaire_id).forEach(d => {
    if (!docsByStagId[d.stagiaire_id]) docsByStagId[d.stagiaire_id] = [];
    docsByStagId[d.stagiaire_id].push(d);
  });
  const totalDocs = docs.length;
  const canUpload = !!selectedVal;
  const nomSelectionné = selectedVal === 'session' ? 'Session complète'
    : selectedVal ? (stagiaires.find(s => String(s.stagiaire_id) === selectedVal)?.nom || '') : '';

  function DocRow({ doc }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.7rem', borderRadius: '7px', background: 'var(--bg-card)', border: '1px solid var(--border)', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '1rem', flexShrink: 0 }}>📎</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.nom_affiche || doc.nom_original}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
            {doc.taille ? (doc.taille < 1048576 ? `${(doc.taille/1024).toFixed(0)} Ko` : `${(doc.taille/1048576).toFixed(1)} Mo`) + ' · ' : ''}
            Déposé le {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fr-FR') : '—'}
          </div>
        </div>
        <button onClick={() => ouvrirDoc(doc.id)} title="Ouvrir"
          style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>👁</button>
        <button onClick={() => supprimer(doc.id)} title="Supprimer"
          style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#f87171', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>🗑</button>
      </div>
    );
  }

  return (
    <div>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: (showDrop || showFiles) ? '0.7rem' : 0 }}>
        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-primary)', flex: 1 }}>
          📄 Fichiers d'évaluation / corrections scannées
        </span>
        <label title="Afficher / masquer la zone de dépôt" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-secondary)', userSelect: 'none' }}>
          <input type="checkbox" checked={showDrop} onChange={e => setShowDrop(e.target.checked)} style={{ cursor: 'pointer' }} />
          Déposer
        </label>
        <button onClick={() => setShowFiles(f => !f)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'none', border: '1px solid var(--border)', borderRadius: '20px', padding: '0.15rem 0.55rem', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          {totalDocs > 0 && (
            <span style={{ background: '#E87722', color: '#fff', borderRadius: '10px', padding: '0 0.4rem', fontSize: '0.72rem', fontWeight: 700 }}>
              {totalDocs}
            </span>
          )}
          <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: showFiles ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
        </button>
      </div>

      {/* Zone de dépôt */}
      {showDrop && (
        <div style={{ marginBottom: showFiles && totalDocs > 0 ? '0.75rem' : 0 }}>
          <select value={selectedVal} onChange={e => setSelectedVal(e.target.value)}
            style={{ width: '100%', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', marginBottom: '0.6rem', cursor: 'pointer' }}>
            <option value="">— Sélectionner —</option>
            <option value="session">Session complète</option>
            {stagiaires.map(s => (
              <option key={s.stagiaire_id} value={String(s.stagiaire_id)}>{s.nom}</option>
            ))}
          </select>
          <div
            onDragOver={e => { e.preventDefault(); if (canUpload && !uploading) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); if (!canUpload) return; const f = e.dataTransfer.files[0]; if (f) upload(f); }}
            onClick={() => canUpload && inputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? '#E87722' : canUpload ? 'rgba(232,119,34,0.5)' : 'var(--border)'}`,
              borderRadius: '8px', padding: '1.2rem 1rem', textAlign: 'center',
              cursor: canUpload ? 'pointer' : 'default',
              background: dragOver ? 'rgba(232,119,34,0.06)' : 'transparent',
              transition: 'all 0.15s', opacity: canUpload ? 1 : 0.5,
              fontSize: '0.82rem', color: 'var(--text-secondary)',
            }}>
            <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files[0]; if (f) { upload(f); e.target.value = ''; } }} />
            {uploading ? '⏳ Envoi en cours…'
              : canUpload
                ? <><div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>📁</div>
                    <strong>Cliquez ou déposez un fichier</strong>
                    <div style={{ fontSize: '0.75rem', marginTop: '0.2rem' }}>Pour : {nomSelectionné}</div>
                  </>
                : 'Sélectionnez d\'abord une option ci-dessus'}
          </div>
        </div>
      )}

      {/* Liste des fichiers déposés */}
      {showFiles && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
          {totalDocs === 0 && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', fontStyle: 'italic', margin: 0 }}>Aucun fichier déposé.</p>
          )}
          {docsSession.length > 0 && (
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Session complète</div>
              {docsSession.map(doc => <DocRow key={doc.id} doc={doc} />)}
            </div>
          )}
          {stagiaires.filter(s => docsByStagId[s.stagiaire_id]?.length > 0).map(stag => (
            <div key={stag.stagiaire_id}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{stag.nom}</div>
              {(docsByStagId[stag.stagiaire_id] || []).map(doc => <DocRow key={doc.id} doc={doc} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Résultats manuels par stagiaire ─────────────────────────────────────────
function ResultatsManuelSection({ sessionId, inscriptions }) {
  const [resultats, setResultats] = useState({});  // { stagiaire_id: row }
  const [editing,   setEditing]   = useState({});  // { stagiaire_id: formObj }
  const [saving,    setSaving]    = useState({});
  const [open,      setOpen]      = useState(true);
  const [infoAttestation, setInfoAttestation] = useState(''); // retour du serveur : attestation enregistrée automatiquement / alerte avis défavorable

  const charger = useCallback(async () => {
    try {
      const d = await apiFetch(`/sessions/${sessionId}/resultats-manuels`);
      const map = {};
      (d.data || []).forEach(r => { map[r.stagiaire_id] = r; });
      setResultats(map);
    } catch(e) {}
  }, [sessionId]);

  useEffect(() => { charger(); }, [charger]);

  function startEdit(stagId) {
    const ex = resultats[stagId] || {};
    setEditing(e => ({ ...e, [stagId]: {
      note_theorique:     ex.note_theorique     != null ? String(ex.note_theorique)     : '',
      note_max_theorique: ex.note_max_theorique != null ? String(ex.note_max_theorique) : '20',
      avis_theorique:     ex.avis_theorique     || '',
      avis_pratique:      ex.avis_pratique      || '',
      commentaire:        ex.commentaire        || '',
    }}));
  }
  function cancelEdit(stagId) {
    setEditing(e => { const n = { ...e }; delete n[stagId]; return n; });
  }
  function setField(stagId, field, val) {
    setEditing(e => ({ ...e, [stagId]: { ...e[stagId], [field]: val } }));
  }
  async function saveResult(stagId) {
    const form = editing[stagId] || {};
    setSaving(s => ({ ...s, [stagId]: true }));
    try {
      setInfoAttestation('');
      const retour = await apiFetch(`/sessions/${sessionId}/resultats-manuels`, {
        method: 'POST',
        body: JSON.stringify({
          stagiaire_id:       stagId,
          note_theorique:     form.note_theorique !== '' ? parseFloat(form.note_theorique) : null,
          note_max_theorique: form.note_max_theorique ? parseFloat(form.note_max_theorique) : 20,
          avis_theorique:     form.avis_theorique || null,
          avis_pratique:      form.avis_pratique  || null,
          commentaire:        form.commentaire    || null,
        }),
      });
      if (retour?.alerte) setInfoAttestation(`⚠ ${retour.alerte}`);
      else if (retour?.attestation_auto) setInfoAttestation("✅ Attestation de formation enregistrée automatiquement dans la fiche du stagiaire (signature numérisée du formateur).");
      await charger();
      cancelEdit(stagId);
    } catch(e) { alert('Erreur : ' + e.message); }
    setSaving(s => ({ ...s, [stagId]: false }));
  }
  async function supprimerResult(stagId) {
    if (!window.confirm('Effacer les résultats de ce stagiaire ?')) return;
    try {
      await apiFetch(`/sessions/${sessionId}/resultats-manuels/${stagId}`, { method: 'DELETE' });
      await charger();
    } catch(e) { alert('Erreur : ' + e.message); }
  }

  // Stagiaires uniques
  const stagiaires = [];
  const seen = new Set();
  for (const i of inscriptions) {
    if (i.stagiaire_id && !seen.has(i.stagiaire_id)) {
      seen.add(i.stagiaire_id);
      stagiaires.push({ stagiaire_id: i.stagiaire_id, nom: i.nom_stagiaire_snapshot || String(i.stagiaire_id) });
    }
  }

  const nbRenseignes = stagiaires.filter(s => resultats[s.stagiaire_id]).length;

  const inpSt = {
    padding: '0.25rem 0.45rem', borderRadius: '5px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.8rem', width: '100%',
  };
  const AvisChip = ({ val }) => {
    if (!val) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
    const ok = val === 'favorable';
    return (
      <span style={{ padding: '0.15rem 0.55rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700,
        background: ok ? '#f0fdf4' : '#fef2f2', color: ok ? '#16a34a' : '#dc2626',
        border: `1px solid ${ok ? '#bbf7d0' : '#fecaca'}` }}>
        {ok ? '✅ Favorable' : '❌ Défavorable'}
      </span>
    );
  };
  const AvisSelect = ({ val, onChange }) => (
    <select value={val} onChange={e => onChange(e.target.value)} style={inpSt}>
      <option value="">— non renseigné</option>
      <option value="favorable">✅ Favorable</option>
      <option value="defavorable">❌ Défavorable</option>
    </select>
  );

  const thSt = (w) => ({
    padding: '0.4rem 0.6rem', textAlign: 'center', fontWeight: 600,
    fontSize: '0.75rem', color: 'var(--text-secondary)',
    borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)',
    width: w,
  });

  return (
    <div style={{ marginBottom: '1.5rem', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>

      {/* En-tête section */}
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem',
          background: 'var(--bg-elevated)', cursor: 'pointer', userSelect: 'none' }}>
        <span>📝</span>
        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)', flex: 1 }}>
          Évaluations individuelles
        </span>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', background: 'var(--bg-card)',
          border: '1px solid var(--border)', borderRadius: '20px', padding: '0.1rem 0.55rem' }}>
          {nbRenseignes} / {stagiaires.length} renseignés
        </span>
        <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && infoAttestation && (
        <div style={{ margin: '0.6rem 1rem', padding: '0.5rem 0.8rem', borderRadius: '8px', fontSize: '0.83rem', fontWeight: 600, ...styleMessage(niveauMessage(infoAttestation)) }}>
          {infoAttestation}
        </div>
      )}
      {open && (
        <div style={{ overflowX: 'auto' }}>
          {stagiaires.length === 0 ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
              Aucun stagiaire inscrit à cette session.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
              <thead>
                {/* Ligne 1 : groupes */}
                <tr style={{ background: 'var(--bg-elevated)' }}>
                  <th rowSpan={2} style={{ ...thSt('28%'), textAlign: 'left', verticalAlign: 'bottom', borderRight: '2px solid var(--border)' }}>
                    Stagiaire
                  </th>
                  <th colSpan={3} style={{ ...thSt('auto'), background: '#eff6ff', color: '#1d4ed8', borderBottom: '1px solid #bfdbfe' }}>
                    📝 Évaluation théorique
                  </th>
                  <th colSpan={1} style={{ ...thSt('18%'), background: '#f0fdf4', color: '#166534', borderBottom: '1px solid #bbf7d0', borderRight: '2px solid var(--border)' }}>
                    🔧 Évaluation pratique
                  </th>
                  <th rowSpan={2} style={{ ...thSt('12%'), verticalAlign: 'bottom', borderRight: 'none' }}>
                    Actions
                  </th>
                </tr>
                {/* Ligne 2 : sous-colonnes */}
                <tr style={{ background: 'var(--bg-elevated)' }}>
                  <th style={{ ...thSt('13%'), background: '#f8faff', fontSize: '0.72rem' }}>Note</th>
                  <th style={{ ...thSt('5%'),  background: '#f8faff', fontSize: '0.72rem' }}>/</th>
                  <th style={{ ...thSt('18%'), background: '#f8faff', fontSize: '0.72rem' }}>Avis</th>
                  <th style={{ ...thSt('18%'), background: '#f7fef9', fontSize: '0.72rem', borderRight: '2px solid var(--border)' }}>Avis</th>
                </tr>
              </thead>
              <tbody>
                {stagiaires.map((stag, idx) => {
                  const ex     = resultats[stag.stagiaire_id];
                  const form   = editing[stag.stagiaire_id];
                  const isEdit = !!form;
                  const bg     = idx % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-elevated)';
                  const tdSt   = (extra = {}) => ({
                    padding: '0.4rem 0.6rem', borderBottom: '1px solid var(--border)',
                    borderRight: '1px solid var(--border)', verticalAlign: 'middle', background: bg, ...extra,
                  });

                  return (
                    <tr key={stag.stagiaire_id}>
                      {/* Nom */}
                      <td style={{ ...tdSt(), fontWeight: 500, borderRight: '2px solid var(--border)' }}>
                        {stag.nom}
                        {ex?.attestation_auto_anomalie ? (
                          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: COULEUR_ATTENTION, marginTop: 2 }}>
                            ⚠ Attestation déjà enregistrée alors que l'avis n'est plus favorable — à vérifier dans la fiche stagiaire
                          </div>
                        ) : ex?.attestation_auto ? (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 2 }}>📎 Attestation enregistrée dans la fiche stagiaire</div>
                        ) : null}
                      </td>

                      {/* Note théorique */}
                      <td style={tdSt({ background: isEdit ? '#f8faff' : bg })}>
                        {isEdit ? (
                          <input type="number" min="0" max="100" step="0.5"
                            value={form.note_theorique}
                            onChange={e => setField(stag.stagiaire_id, 'note_theorique', e.target.value)}
                            style={{ ...inpSt, width: '60px' }} placeholder="—" />
                        ) : ex?.note_theorique != null ? (
                          <span style={{ fontWeight: 700 }}>{ex.note_theorique}</span>
                        ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                      </td>

                      {/* Note max */}
                      <td style={tdSt({ textAlign: 'center', background: isEdit ? '#f8faff' : bg, fontSize: '0.75rem', color: 'var(--text-tertiary)' })}>
                        {isEdit ? (
                          <input type="number" min="1" step="1"
                            value={form.note_max_theorique}
                            onChange={e => setField(stag.stagiaire_id, 'note_max_theorique', e.target.value)}
                            style={{ ...inpSt, width: '44px' }} />
                        ) : ex?.note_theorique != null ? (
                          <span>/{ex.note_max_theorique || 20}</span>
                        ) : ''}
                      </td>

                      {/* Avis théorique */}
                      <td style={tdSt({ background: isEdit ? '#f8faff' : bg })}>
                        {isEdit ? (
                          <AvisSelect val={form.avis_theorique} onChange={v => setField(stag.stagiaire_id, 'avis_theorique', v)} />
                        ) : <AvisChip val={ex?.avis_theorique} />}
                      </td>

                      {/* Avis pratique */}
                      <td style={tdSt({ background: isEdit ? '#f7fef9' : bg, borderRight: '2px solid var(--border)' })}>
                        {isEdit ? (
                          <AvisSelect val={form.avis_pratique} onChange={v => setField(stag.stagiaire_id, 'avis_pratique', v)} />
                        ) : <AvisChip val={ex?.avis_pratique} />}
                      </td>

                      {/* Actions */}
                      <td style={{ ...tdSt(), borderRight: 'none' }}>
                        {isEdit ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                            <button onClick={() => saveResult(stag.stagiaire_id)} disabled={saving[stag.stagiaire_id]}
                              style={{ padding: '0.25rem 0.5rem', borderRadius: '5px', border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.78rem' }}>
                              {saving[stag.stagiaire_id] ? '…' : '✓ Enreg.'}
                            </button>
                            <button onClick={() => cancelEdit(stag.stagiaire_id)}
                              style={{ padding: '0.25rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.78rem' }}>
                              Annuler
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                            <button onClick={() => startEdit(stag.stagiaire_id)}
                              style={{ padding: '0.25rem 0.55rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.78rem' }}>
                              ✏ {ex ? 'Modifier' : 'Saisir'}
                            </button>
                            {ex && (
                              <button onClick={() => supprimerResult(stag.stagiaire_id)}
                                style={{ padding: '0.25rem 0.4rem', borderRadius: '5px', border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem' }} title="Effacer">
                                ✕
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div style={{ marginTop: '1rem', padding: '0.85rem 1.1rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' }}>
        <EvaluationDocsZone sessionId={sessionId} stagiaires={stagiaires} />
      </div>
    </div>
  );
}

function ResultatsQuestionnaires({ sessionId, inscriptions, modules = [], session = {}, typesQ = TYPES_Q_DEFAULT }) {
  const { isFormateur, formateurId: ctxFormateurId } = useAuth();
  const [reponses, setReponses] = useState([]);
  const [questionnaires, setQuestionnaires] = useState({});
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('tous');
  const typeColorFn = (val) => typesQ.find(t => t.val === val)?.color || TYPE_COLORS[val] || '#64748b';
  const typeLabelFn = (val) => typesQ.find(t => t.val === val)?.label || TYPE_LABELS[val] || val;
  const typeBgFn    = (val) => typesQ.find(t => t.val === val)?.bg    || '#f1f5f9';
  const typeIsCommonFn = (val) => typesQ.find(t => t.val === val)?.is_common ?? (val === 'satisfaction');
  const [viewMode, setViewMode] = useState('stats'); // stats | individuel
  const [selectedStagiaire, setSelectedStagiaire] = useState(null);
  const [formsImport, setFormsImport] = useState(null); // { questions, matched, unmatched, questionnaire_id, saving }
  const formsFileRef = useRef(null);
  const [correctionEnCours, setCorrectionEnCours] = useState({}); // { reponse_id: { corrections_manuelles: {}, note_totale: '' } }
  const [savingCorrection, setSavingCorrection] = useState({});

  const chargerResultats = useCallback(async () => {
    try {
      const d = await apiFetch('/sessions/' + sessionId + '/resultats-questionnaires');
      const reps = d.data || [];
      setReponses(reps);
      const ids = [...new Set(reps.filter(r => r.questionnaire_id).map(r => r.questionnaire_id))];
      const qrs = await Promise.all(ids.map(id => apiFetch('/questionnaires/' + id)));
      const map = {};
      qrs.forEach(q => { if (q) map[q.questionnaire_id] = q; });
      setQuestionnaires(map);
    } catch(e) {}
  }, [sessionId]);

  useEffect(() => {
    setLoading(true);
    chargerResultats().finally(() => setLoading(false));
  }, [chargerResultats]);

  // Auto-refresh toutes les 10s
  useEffect(() => {
    const interval = setInterval(() => chargerResultats(), 10000);
    return () => clearInterval(interval);
  }, [chargerResultats]);

  function exportCSV() {
    if (!reponses.length) return;
    const rows = [['Stagiaire', 'Type', 'Questionnaire', 'Date', 'Reponses']];
    reponses.forEach(r => {
      rows.push([
        r.nom_stagiaire || '',
        typeLabelFn(r.type_questionnaire) || '',
        r.titre_questionnaire || 'Satisfaction',
        r.soumis_at ? new Date(r.soumis_at).toLocaleString('fr-FR') : '',
        JSON.stringify(r.reponses || {}),
      ]);
    });
    const csv = rows.map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(';')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = 'resultats_questionnaires_' + sessionId + '.csv'; a.click();
  }

  async function handleFormsFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
      const token = localStorage.getItem('gf_formateur_token') || localStorage.getItem('gf_token');
      const resp = await fetch(API_BASE + '/sessions/' + sessionId + '/import-forms?preview=1', {
        method: 'POST', body: fd,
        headers: token ? { Authorization: 'Bearer ' + token } : {},
      });
      const json = await resp.json();
      if (json.error) return alert('Erreur: ' + json.error);
      setFormsImport({ questions: json.questions, matched: json.matched, unmatched: json.unmatched, questionnaire_id: '', saving: false });
    } catch (err) { alert('Erreur lecture fichier: ' + err.message); }
    e.target.value = '';
  }

  async function saveFormsImport() {
    if (!formsImport) return;
    setFormsImport(f => ({ ...f, saving: true }));
    try {
      await apiFetch('/sessions/' + sessionId + '/import-forms-save', {
        method: 'POST',
        body: JSON.stringify({ questionnaire_id: formsImport.questionnaire_id || null, matched: formsImport.matched }),
      });
      setFormsImport(null);
      const d = await apiFetch('/sessions/' + sessionId + '/resultats-questionnaires');
      setReponses(d.data || []);
    } catch (err) { alert('Erreur: ' + err.message); setFormsImport(f => ({ ...f, saving: false })); }
  }

  async function saveCorrection(reponseId) {
    const c = correctionEnCours[reponseId] || {};
    setSavingCorrection(s => ({ ...s, [reponseId]: true }));
    try {
      await apiFetch('/questionnaire-reponses/' + reponseId + '/correction', {
        method: 'PUT',
        body: JSON.stringify({ corrections_manuelles: c.corrections_manuelles || {}, note_totale: c.note_totale != null ? c.note_totale : null }),
      });
      const d = await apiFetch('/sessions/' + sessionId + '/resultats-questionnaires');
      setReponses(d.data || []);
    } catch (e) { alert('Erreur : ' + e.message); }
    setSavingCorrection(s => ({ ...s, [reponseId]: false }));
  }

  // Module du formateur dans cette session — doit être avant tout return conditionnel
  const formateurModule = React.useMemo(() => {
    if (!isFormateur || !ctxFormateurId) return null;
    const isPrincipal = ctxFormateurId === session?.formateur_id;
    if (isPrincipal) return null;
    return modules.find(m => m.formateur_id === ctxFormateurId) || null;
  }, [isFormateur, ctxFormateurId, session, modules]);

  if (loading) return <Loading />;

  const types = [...new Set(reponses.map(r => r.type_questionnaire).filter(Boolean))];
  const repsFiltrees = typeFilter === 'tous' ? reponses : reponses.filter(r => r.type_questionnaire === typeFilter);

  // Regrouper par stagiaire pour la vue individuelle
  const parStagiaire = {};
  repsFiltrees.forEach(r => {
    if (!parStagiaire[r.stagiaire_id]) parStagiaire[r.stagiaire_id] = { nom: r.nom_stagiaire, reponses: [] };
    parStagiaire[r.stagiaire_id].reponses.push(r);
  });

  // Pour la vue stats — regrouper par questionnaire
  const parQuestionnaire = {};
  repsFiltrees.forEach(r => {
    const key = r.questionnaire_id || 'satisfaction_defaut';
    if (!parQuestionnaire[key]) parQuestionnaire[key] = { titre: r.titre_questionnaire || 'Satisfaction', type: r.type_questionnaire, reponses: [] };
    parQuestionnaire[key].reponses.push(r);
  });

  return (
    <div style={{ padding: '0.5rem 0' }}>
      {/* Bandeau de contexte pour les formateurs de module */}
      {formateurModule && (
        <div style={{ margin: '0 0 0.75rem 0', padding: '0.5rem 0.8rem', background: '#f5f3ff', borderRadius: '8px', border: '1px solid #7c3aed33', fontSize: '0.82rem', color: '#7c3aed', fontWeight: 600 }}>
          📚 Résultats filtrés pour votre module : {formateurModule.intitule_module}
        </div>
      )}

      <SectionRepliable title="📝 Évaluations manuelles" defaultOpen={false}>
        <ResultatsManuelSection sessionId={sessionId} inscriptions={inscriptions} />
      </SectionRepliable>

      <SectionRepliable title="📊 Résultats questionnaires (Microsoft Forms)" defaultOpen={false}>

      {/* En-tête stats globales */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.2rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'Réponses', val: reponses.length, color: '#2563eb', bg: '#eff6ff' },
          { label: 'Stagiaires', val: Object.keys(parStagiaire).length, color: '#7c3aed', bg: '#f5f3ff' },
          { label: 'Questionnaires', val: Object.keys(parQuestionnaire).length, color: '#16a34a', bg: '#f0fdf4' },
        ].map(({ label, val, color, bg }) => (
          <div key={label} style={{ padding: '0.5rem 1.1rem', borderRadius: '8px', background: bg, border: '1px solid ' + color + '33', textAlign: 'center', minWidth: 90 }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color }}>{val}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{label}</div>
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <input ref={formsFileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFormsFile} />
        <button onClick={() => formsFileRef.current.click()}
          style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: '1px solid #7c3aed', background: '#f5f3ff', color: '#7c3aed', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600 }}>
          📊 Importer Forms
        </button>
        <button onClick={exportCSV} disabled={!reponses.length}
          style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: reponses.length ? 'pointer' : 'not-allowed', fontSize: '0.83rem', fontWeight: 600 }}>
          📥 Export CSV
        </button>
      </div>

      {/* Modal import Forms */}
      {formsImport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '14px', padding: '1.5rem', width: '100%', maxWidth: 680, maxHeight: '85vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>📊 Importer réponses Microsoft Forms</div>
            <div style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>
              <strong>{formsImport.questions.length}</strong> question(s) détectée(s) dans le fichier Excel.
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {formsImport.questions.map((q, i) => (
                <span key={i} style={{ padding: '0.2rem 0.6rem', borderRadius: '20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{q}</span>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#16a34a', marginBottom: '0.4rem' }}>✅ Correspondances trouvées ({formsImport.matched.length})</div>
                {formsImport.matched.map((m, i) => (
                  <div key={i} style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem', background: '#f0fdf4', borderRadius: '6px', marginBottom: '0.2rem', color: '#166534' }}>{m.nom}</div>
                ))}
                {formsImport.matched.length === 0 && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Aucune correspondance</div>}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#dc2626', marginBottom: '0.4rem' }}>⚠ Non identifiés ({formsImport.unmatched.length})</div>
                {formsImport.unmatched.map((u, i) => (
                  <div key={i} style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem', background: '#fef2f2', borderRadius: '6px', marginBottom: '0.2rem', color: '#991b1b' }}>{u.nom}</div>
                ))}
                {formsImport.unmatched.length === 0 && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Aucun</div>}
              </div>
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Associer à un questionnaire (optionnel)</label>
              <select value={formsImport.questionnaire_id} onChange={e => setFormsImport(f => ({ ...f, questionnaire_id: e.target.value }))}
                style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '100%' }}>
                <option value="">Aucun questionnaire (réponses libres)</option>
                {Object.values(questionnaires).map(q => <option key={q.questionnaire_id} value={q.questionnaire_id}>{q.titre}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setFormsImport(null)} style={{ padding: '0.45rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>Annuler</button>
              <button onClick={saveFormsImport} disabled={formsImport.saving || formsImport.matched.length === 0}
                style={{ padding: '0.45rem 1.2rem', borderRadius: '8px', background: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
                {formsImport.saving ? '⏳ Import...' : '✓ Importer ' + formsImport.matched.length + ' réponse(s)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reponses.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '10px', fontSize: '0.9rem' }}>
          Aucune reponse pour le moment. Les stagiaires n'ont pas encore soumis de questionnaire.
        </div>
      ) : (
        <>
          {/* Filtres type + vue */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {[{ val: 'tous', label: 'Tous', color: '#475569' }, ...typesQ].map(t => {
              const count = t.val === 'tous' ? reponses.length : reponses.filter(r => r.type_questionnaire === t.val).length;
              const hasData = count > 0;
              const isActive = typeFilter === t.val;
              const color = typeColorFn(t.val) || '#475569';
              return (
                <button key={t.val}
                  onClick={() => { if (hasData) { setTypeFilter(t.val); setViewMode('stats'); } }}
                  style={{
                    padding: '0.3rem 0.85rem', borderRadius: '16px', display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                    border: `1px solid ${isActive ? color : 'var(--border)'}`,
                    cursor: hasData ? 'pointer' : 'default',
                    fontSize: '0.8rem', fontWeight: isActive ? 700 : 400,
                    background: isActive ? color : 'var(--bg-elevated)',
                    color: isActive ? '#fff' : (hasData ? 'var(--text-primary)' : 'var(--text-secondary)'),
                    opacity: hasData ? 1 : 0.45,
                    transition: 'all 0.15s',
                  }}>
                  {t.label}
                  <span style={{
                    minWidth: 18, height: 18, borderRadius: '50%', fontSize: '0.68rem', fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px',
                    background: isActive ? 'rgba(255,255,255,0.25)' : color + '22',
                    color: isActive ? '#fff' : color,
                  }}>{count}</span>
                </button>
              );
            })}
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
              {[{ val: 'stats', label: '📊 Statistiques' }, { val: 'individuel', label: '👤 Par stagiaire' }].map(v => (
                <button key={v.val} onClick={() => setViewMode(v.val)}
                  style={{ padding: '0.3rem 0.8rem', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: viewMode === v.val ? 700 : 400, background: viewMode === v.val ? 'var(--accent-blue)' : 'transparent', color: viewMode === v.val ? '#fff' : 'var(--text-secondary)' }}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tableau récapitulatif stagiaires / notes — visible quand un type spécifique est sélectionné */}
          {viewMode === 'stats' && typeFilter !== 'tous' && (() => {
            // Construire la liste des stagiaires avec leur(s) note(s) pour ce type
            const stagMap = {};
            repsFiltrees.forEach(r => {
              if (!stagMap[r.stagiaire_id]) stagMap[r.stagiaire_id] = { nom: r.nom_stagiaire, scores: [] };
              const score = r.note_totale != null ? r.note_totale : (r.score_auto != null ? r.score_auto : null);
              const max   = r.score_max || null;
              stagMap[r.stagiaire_id].scores.push({ score, max, titre: r.titre_questionnaire || 'Questionnaire' });
            });
            const lignes = Object.entries(stagMap).map(([sid, v]) => ({ sid, ...v }));
            if (!lignes.length) return null;
            const hasAnyScore = lignes.some(l => l.scores.some(s => s.score != null));
            return (
              <div style={{ marginBottom: '1.5rem', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{
                  padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.6rem',
                  background: (typeFilter === 'positionnement' ? '#f5f3ff' : typeFilter === 'evaluation' ? '#eff6ff' : '#f0fdf4'),
                  borderBottom: '1px solid var(--border)',
                }}>
                  <span style={{ padding: '0.15rem 0.6rem', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 700, background: typeColorFn(typeFilter) + '22', color: typeColorFn(typeFilter) }}>
                    {typeLabelFn(typeFilter)}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>
                    Récapitulatif des stagiaires
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                    {lignes.length} stagiaire{lignes.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-elevated)' }}>
                        <th style={{ textAlign: 'left', padding: '0.5rem 1rem', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.78rem', borderBottom: '1px solid var(--border)' }}>
                          Stagiaire
                        </th>
                        {hasAnyScore && (
                          <th style={{ textAlign: 'center', padding: '0.5rem 1rem', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.78rem', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                            Note / Score
                          </th>
                        )}
                        <th style={{ textAlign: 'center', padding: '0.5rem 1rem', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.78rem', borderBottom: '1px solid var(--border)' }}>
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lignes.sort((a, b) => (a.nom || '').localeCompare(b.nom || '')).map((l, i) => {
                        const totalScore = l.scores.reduce((acc, s) => acc + (s.score != null ? Number(s.score) : 0), 0);
                        const totalMax   = l.scores.reduce((acc, s) => acc + (s.max != null ? Number(s.max) : 0), 0);
                        const anyScore   = l.scores.some(s => s.score != null);
                        const pct = totalMax > 0 ? Math.round(100 * totalScore / totalMax) : null;
                        const scoreCouleur = pct == null ? '#64748b' : pct >= 80 ? '#16a34a' : pct >= 60 ? '#f59e0b' : '#ef4444';
                        return (
                          <tr key={l.sid} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.1s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            onClick={() => { setViewMode('individuel'); setSelectedStagiaire(l.sid); }}>
                            <td style={{ padding: '0.55rem 1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {l.nom || '—'}
                            </td>
                            {hasAnyScore && (
                              <td style={{ padding: '0.55rem 1rem', textAlign: 'center' }}>
                                {anyScore ? (
                                  <span style={{ padding: '0.15rem 0.65rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.82rem', background: scoreCouleur + '18', color: scoreCouleur }}>
                                    {totalScore}{totalMax > 0 ? ' / ' + totalMax : ''} pts
                                    {pct != null && <span style={{ marginLeft: '0.35rem', opacity: 0.8 }}>({pct}%)</span>}
                                  </span>
                                ) : <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>—</span>}
                              </td>
                            )}
                            <td style={{ padding: '0.55rem 1rem', textAlign: 'center' }}>
                              <span style={{ padding: '0.15rem 0.55rem', borderRadius: '10px', fontSize: '0.73rem', fontWeight: 700, background: '#f0fdf4', color: '#16a34a', border: '1px solid #16a34a33' }}>
                                ✓ Soumis
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Vue statistiques */}
          {viewMode === 'stats' && Object.entries(parQuestionnaire).map(([key, { titre, type, reponses: reps }]) => {
            // Fallback sur le questionnaire de satisfaction par défaut si aucun questionnaire lié
            const qDef = questionnaires[key] || (key === 'satisfaction_defaut' ? SATISFACTION_DEFAULT_DEF : null);
            const questions = qDef?.questions || [];
            const effectiveType = type || qDef?.type || 'satisfaction';
            const effectiveTitre = titre || qDef?.titre || 'Questionnaire de satisfaction';
            const typeCfg = { color: typeColorFn(effectiveType), bg: typeBgFn(effectiveType) };
            return (
              <div key={key} style={{ marginBottom: '1.5rem', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ padding: '0.8rem 1rem', background: typeCfg.bg, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <span style={{ padding: '0.15rem 0.6rem', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 700, background: typeCfg.color + '22', color: typeCfg.color }}>{typeLabelFn(effectiveType)}</span>
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem' }}>{effectiveTitre}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{reps.length} réponse(s)</span>
                </div>
                <div style={{ padding: '0.8rem 1rem' }}>
                  {questions.length === 0 ? (
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Questions non disponibles.</div>
                  ) : questions.map(q => (
                    <div key={q.question_id} style={{ marginBottom: '0.9rem', paddingBottom: '0.9rem', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>{q.texte}</div>
                      {q.type_reponse === 'smiley'  && <StatsSmiley reponses={reps} questionId={q.question_id} />}
                      {q.type_reponse === 'note'    && <StatsNote reponses={reps} questionId={q.question_id} />}
                      {q.type_reponse === 'choix'   && <StatsChoix reponses={reps} questionId={q.question_id} options={q.options} reponseCorrecte={q.reponse_correcte} />}
                      {q.type_reponse === 'oui_non' && <StatsOuiNon reponses={reps} questionId={q.question_id} />}
                      {q.type_reponse === 'texte'   && <StatsTexte reponses={reps} questionId={q.question_id} />}
                      {q.type_reponse === 'photo'   && <StatsPhoto reponses={reps} questionId={q.question_id} />}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Vue par stagiaire */}
          {viewMode === 'individuel' && (
            <div>
              {selectedStagiaire ? (
                <div>
                  <button onClick={() => setSelectedStagiaire(null)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '7px', padding: '0.3rem 0.7rem', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.83rem', marginBottom: '1rem' }}>← Retour</button>
                  <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>{parStagiaire[selectedStagiaire]?.nom}</div>
                  {(parStagiaire[selectedStagiaire]?.reponses || []).map((r, ri) => {
                    // Fallback satisfaction par défaut si pas de questionnaire_id
                    const qDef = questionnaires[r.questionnaire_id] || (!r.questionnaire_id ? SATISFACTION_DEFAULT_DEF : null);
                    const questions = qDef?.questions || [];
                    const hasChoixCorrigeable = questions.some(q => q.type_reponse === 'choix' && q.reponse_correcte?.length > 0);
                    const hasManuelCorrigeable = questions.some(q => ['texte', 'photo', 'oui_non'].includes(q.type_reponse) && q.points > 0);
                    const hasCorrectionUI = hasChoixCorrigeable || hasManuelCorrigeable;
                    const corrState = correctionEnCours[r.reponse_id] || { corrections_manuelles: r.corrections_manuelles || {}, note_totale: r.note_totale != null ? r.note_totale : '' };
                    return (
                      <div key={ri} style={{ marginBottom: '1rem', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
                        <div style={{ padding: '0.6rem 0.9rem', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: typeColorFn(r.type_questionnaire) }}>{typeLabelFn(r.type_questionnaire)}</span>
                          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{r.titre_questionnaire || 'Satisfaction'}</span>
                          {(r.score_max > 0 || r.score_auto > 0) && <span style={{ padding: '0.1rem 0.5rem', borderRadius: '10px', background: '#eff6ff', border: '1px solid #2563eb33', fontSize: '0.75rem', fontWeight: 700, color: '#2563eb' }}>Score : {r.score_auto}{r.score_max > 0 ? ' / ' + r.score_max : ''} pts</span>}
                          {r.note_totale != null && <span style={{ padding: '0.1rem 0.5rem', borderRadius: '10px', background: '#f0fdf4', border: '1px solid #16a34a33', fontSize: '0.75rem', fontWeight: 700, color: '#16a34a' }}>Note : {r.note_totale} pts</span>}
                          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{r.soumis_at ? new Date(r.soumis_at).toLocaleString('fr-FR') : ''}</span>
                        </div>
                        <div style={{ padding: '0.7rem 0.9rem' }}>
                          {questions.length === 0 ? (
                            <pre style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(r.reponses, null, 2)}</pre>
                          ) : questions.map(q => {
                            const val = (r.reponses || {})[q.question_id];
                            if (val === undefined || val === null || val === '') return null;
                            const bonnes = Array.isArray(q.reponse_correcte) ? q.reponse_correcte : [];
                            const estCorrect = bonnes.length > 0 && bonnes.includes(val);
                            const estIncorrect = bonnes.length > 0 && !bonnes.includes(val);
                            return (
                              <div key={q.question_id} style={{ marginBottom: '0.5rem', fontSize: '0.83rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                  <span style={{ color: 'var(--text-secondary)' }}>{q.texte} : </span>
                                  {q.type_reponse === 'smiley' && <strong>{SMILEY_EMOJIS[val]} {SMILEY_LABELS[val]}</strong>}
                                  {q.type_reponse === 'note'   && <strong>{val}/10</strong>}
                                  {q.type_reponse === 'choix' && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                                      {estCorrect && <span style={{ color: '#16a34a', fontWeight: 700 }}>✅ {val}</span>}
                                      {estIncorrect && <span style={{ color: '#dc2626', fontWeight: 700 }}>❌ {val}</span>}
                                      {!estCorrect && !estIncorrect && <strong>{val}</strong>}
                                    </span>
                                  )}
                                  {(q.type_reponse === 'oui_non' || q.type_reponse === 'texte') && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <strong>{val}</strong>
                                      {q.points > 0 && (
                                        <input type="number" min={0} max={q.points}
                                          value={corrState.corrections_manuelles?.[q.question_id] != null ? corrState.corrections_manuelles[q.question_id] : ''}
                                          onChange={e => {
                                            const v = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                                            setCorrectionEnCours(prev => ({
                                              ...prev,
                                              [r.reponse_id]: {
                                                ...corrState,
                                                corrections_manuelles: { ...(corrState.corrections_manuelles || {}), [q.question_id]: v === '' ? null : v }
                                              }
                                            }));
                                          }}
                                          placeholder={'/ ' + q.points}
                                          style={{ width: '70px', padding: '0.15rem 0.3rem', borderRadius: '5px', border: '1px solid var(--border)', fontSize: '0.8rem', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                                        />
                                      )}
                                    </span>
                                  )}
                                  {q.type_reponse === 'photo' && val?.photo_url && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <a href={(process.env.REACT_APP_API_URL || 'http://localhost:3001') + val.photo_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-blue)' }}>Voir la photo</a>
                                      {q.points > 0 && (
                                        <input type="number" min={0} max={q.points}
                                          value={corrState.corrections_manuelles?.[q.question_id] != null ? corrState.corrections_manuelles[q.question_id] : ''}
                                          onChange={e => {
                                            const v = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                                            setCorrectionEnCours(prev => ({
                                              ...prev,
                                              [r.reponse_id]: {
                                                ...corrState,
                                                corrections_manuelles: { ...(corrState.corrections_manuelles || {}), [q.question_id]: v === '' ? null : v }
                                              }
                                            }));
                                          }}
                                          placeholder={'/ ' + q.points}
                                          style={{ width: '70px', padding: '0.15rem 0.3rem', borderRadius: '5px', border: '1px solid var(--border)', fontSize: '0.8rem', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                                        />
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {hasCorrectionUI && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginTop: '0.7rem', paddingTop: '0.7rem', borderTop: '1px solid var(--border)' }}>
                              <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Note totale :</label>
                              <input type="number" min={0}
                                value={corrState.note_totale != null ? corrState.note_totale : ''}
                                onChange={e => {
                                  const v = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                                  setCorrectionEnCours(prev => ({ ...prev, [r.reponse_id]: { ...corrState, note_totale: v === '' ? null : v } }));
                                }}
                                placeholder="Note"
                                style={{ width: '80px', padding: '0.25rem 0.4rem', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '0.85rem', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                              />
                              <button onClick={() => saveCorrection(r.reponse_id)} disabled={savingCorrection[r.reponse_id]}
                                style={{ padding: '0.3rem 0.8rem', borderRadius: '7px', background: savingCorrection[r.reponse_id] ? '#94a3b8' : '#16a34a', color: '#fff', border: 'none', cursor: savingCorrection[r.reponse_id] ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                                {savingCorrection[r.reponse_id] ? '...' : '✓ Enregistrer la correction'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {Object.entries(parStagiaire).map(([sid, { nom, reponses: reps }]) => {
                    const scoreTotal = reps.reduce((acc, r) => acc + (r.note_totale != null ? r.note_totale : (r.score_auto || 0)), 0);
                    const scoreMaxTotal = reps.reduce((acc, r) => acc + (r.score_max || 0), 0);
                    const hasScore = reps.some(r => r.note_totale != null || r.score_auto > 0 || r.score_max > 0);
                    return (
                      <div key={sid} onClick={() => setSelectedStagiaire(sid)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.7rem 1rem', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-blue)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                        <div style={{ flex: 1, fontWeight: 600, fontSize: '0.9rem' }}>{nom}</div>
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                          {reps.map((r, i) => (
                            <span key={i} style={{ padding: '0.15rem 0.5rem', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700, background: typeColorFn(r.type_questionnaire) + '22', color: typeColorFn(r.type_questionnaire) }}>
                              {typeLabelFn(r.type_questionnaire)}
                            </span>
                          ))}
                          {hasScore && <span style={{ padding: '0.15rem 0.5rem', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700, background: '#f0fdf4', color: '#16a34a', border: '1px solid #16a34a33' }}>{scoreTotal}{scoreMaxTotal > 0 ? ' / ' + scoreMaxTotal : ''} pts</span>}
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{reps.length} rep. →</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
      </SectionRepliable>
    </div>
  );
}

// ── Section Chevalets QR ────────────────────────────────────────────────────
function ChevaletsSection({ sessionId, data }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const chargerTokens = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/sessions/' + sessionId + '/qr-tokens');
      setTokens(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { chargerTokens(); }, [chargerTokens]);

  async function genererTokens() {
    if (!window.confirm('Générer les QR codes pour tous les stagiaires inscrits ?')) return;
    setGenerating(true);
    try {
      const r = await apiFetch('/sessions/' + sessionId + '/qr-tokens/generer', { method: 'POST' });
      const deja = r.total - r.created;
      const msg = r.created === 0
        ? `Tokens déjà existants pour les ${r.total} inscrits (aucun nouveau créé).`
        : r.created === r.total
          ? `${r.created} token(s) créé(s) pour ${r.total} inscrits.`
          : `${r.created} nouveau(x) token(s) créé(s) — ${deja} existaient déjà (total : ${r.total}).`;
      alert(msg);
      chargerTokens();
    } catch (e) { alert('Erreur : ' + e.message); }
    finally { setGenerating(false); }
  }

  async function telechargerChevalets() {
    setPdfLoading(true);
    try {
      // APP_URL_BASE contient l'IP locale réelle du frontend (ex: http://192.168.x.x:3003 en dev,
      // ou http://192.168.x.x:3002 en build) pour que le QR code soit scannable depuis un smartphone
      const baseUrl = APP_URL_BASE;
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
      const url = apiBase + '/sessions/' + sessionId + '/chevalets?baseUrl=' + encodeURIComponent(baseUrl);
      const authToken = localStorage.getItem('gf_token');
      const r = await fetch(url, { headers: { Authorization: 'Bearer ' + authToken } });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || r.statusText); }
      const ct = r.headers.get('content-type') || '';
      if (!ct.includes('pdf')) { const t = await r.text(); throw new Error('Réponse inattendue (' + ct + ') : ' + t.substring(0, 300)); }
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'chevalets_' + sessionId + '.pdf';
      a.click();
    } catch (e) { alert('Erreur : ' + e.message); }
    finally { setPdfLoading(false); }
  }

  const nbUtilises = tokens.filter(t => t.utilise).length;

  return (
    <div style={{ marginTop: '2rem', borderTop: '2px solid var(--border)', paddingTop: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.2rem' }}>
            🪪 Chevalets avec QR Code
          </h3>
          {tokens.length > 0 && (
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {tokens.length} stagiaire(s) — {nbUtilises} QR scanné(s)
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={genererTokens} disabled={generating}
            style={{ fontSize: '0.85rem', padding: '0.4rem 0.9rem' }}>
            {generating ? '⏳ Génération...' : '🔑 Générer les QR codes'}
          </button>
          {tokens.length > 0 && (
            <button className="btn-primary" onClick={telechargerChevalets} disabled={pdfLoading}
              style={{ fontSize: '0.85rem', padding: '0.4rem 0.9rem' }}>
              {pdfLoading ? '⏳ PDF...' : '📄 Télécharger les chevalets (PDF)'}
            </button>
          )}
        </div>
      </div>

      {loading ? <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Chargement...</div> : (
        tokens.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '8px', fontSize: '0.9rem' }}>
            Aucun QR code généré. Cliquez sur "Générer les QR codes" pour créer les tokens de chaque stagiaire inscrit.
          </div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Stagiaire</th>
                <th style={{ textAlign: 'center' }}>Statut QR</th>
                <th style={{ textAlign: 'center' }}>Scanné le</th>
                <th style={{ textAlign: 'center' }}>Resets</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map(t => (
                <tr key={t.token}>
                  <td style={{ fontWeight: 600 }}>{t.nom_complet || t.prenom}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 700,
                      background: t.utilise ? '#fef2f2' : '#f0fdf4',
                      color: t.utilise ? '#dc2626' : '#16a34a',
                    }}>
                      {t.utilise ? '✓ Scanné' : '○ En attente'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    {t.utilise_at ? new Date(t.utilise_at).toLocaleString('fr-FR') : '—'}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    {t.reset_count > 0 ? t.reset_count : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}


// ── Présentation plein écran du questionnaire ─────────────────────────────────
function QuestionnairePresentation({ questionnaireId, nomFormation, onClose }) {
  const [q, setQ]             = useState(null);
  const [idx, setIdx]         = useState(-1);   // -1 = page de couverture
  const [loading, setLoading] = useState(true);
  const containerRef = React.useRef(null);

  useEffect(() => {
    apiFetch('/questionnaires/' + questionnaireId)
      .then(d => { setQ(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [questionnaireId]);

  // Plein écran
  useEffect(() => {
    const el = containerRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    return () => { if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); };
  }, []);

  useEffect(() => {
    const nb = q?.questions?.length || 0;
    function onKey(e) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') setIdx(i => i < nb - 1 ? i + 1 : i);
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   setIdx(i => i > -1 ? i - 1 : -1);
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [q, onClose]);

  const questions = q?.questions || [];
  const question  = idx >= 0 ? (questions[idx] || null) : null;
  const onCover   = idx === -1;
  const isChapitre = question?.type_reponse === 'chapitre';
  const nonChapterQuestions = questions.filter(q2 => q2.type_reponse !== 'chapitre');
  // Position parmi les vraies questions (hors chapitres)
  const questionNumero = question && !isChapitre
    ? nonChapterQuestions.findIndex(q2 => q2.question_id === question.question_id) + 1
    : null;
  const apiBase   = (process.env.REACT_APP_API_URL || 'http://localhost:3002/api').replace('/api', '');

  const SMILEY_LABELS = [
    { e: '😞', l: 'Très insatisfait' },
    { e: '😕', l: 'Insatisfait' },
    { e: '😐', l: 'Neutre' },
    { e: '🙂', l: 'Satisfait' },
    { e: '😄', l: 'Très satisfait' },
  ];

  return (
    <div ref={containerRef} style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: '#0f172a', color: '#f8fafc',
      display: 'flex', flexDirection: 'column',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      {/* Barre supérieure */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '0.8rem 1.5rem', borderBottom: '1px solid #1e293b', gap: '1rem', flexShrink: 0 }}>
        <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Présentation
        </div>
        <div style={{ flex: 1, fontWeight: 700, fontSize: '1rem', color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {q?.titre || '...'}
        </div>
        {questions.length > 0 && !onCover && !isChapitre && (
          <div style={{ fontSize: '0.85rem', color: '#64748b', whiteSpace: 'nowrap' }}>
            Question <strong style={{ color: '#94a3b8' }}>{questionNumero}</strong> / {nonChapterQuestions.length}
          </div>
        )}
        {questions.length > 0 && !onCover && isChapitre && (
          <div style={{ fontSize: '0.85rem', color: '#6366f1', whiteSpace: 'nowrap', fontWeight: 600 }}>
            Chapitre
          </div>
        )}
        {questions.length > 0 && onCover && (
          <div style={{ fontSize: '0.82rem', color: '#475569' }}>Page de présentation</div>
        )}
        <button onClick={onClose} style={{ background: 'none', border: '1px solid #334155', borderRadius: '8px', padding: '0.35rem 0.9rem', cursor: 'pointer', color: '#94a3b8', fontSize: '0.82rem' }}>
          ✕ Fermer
        </button>
      </div>

      {/* Contenu principal */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        {loading && <div style={{ color: '#64748b', fontSize: '1.1rem' }}>Chargement…</div>}

        {!loading && questions.length === 0 && (
          <div style={{ color: '#64748b', fontSize: '1.1rem' }}>Ce questionnaire ne contient aucune question.</div>
        )}

        {/* ── Page de couverture ── */}
        {!loading && onCover && q && (
          <div style={{ width: '100%', maxWidth: 760, textAlign: 'center' }}>
            {nomFormation && (
              <div style={{ fontSize: 'clamp(0.9rem, 1.8vw, 1.2rem)', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1.2rem' }}>
                {nomFormation}
              </div>
            )}
            <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 800, color: '#f1f5f9', lineHeight: 1.2, margin: '0 0 1.5rem' }}>
              {q.titre}
            </h1>
            {q.description && (
              <div style={{ fontSize: 'clamp(1rem, 2vw, 1.4rem)', color: '#94a3b8', lineHeight: 1.7, padding: '1.5rem 2rem', background: '#1e293b', borderRadius: '16px', border: '1px solid #334155', marginBottom: '2.5rem', textAlign: 'left', whiteSpace: 'pre-wrap' }}>
                {q.description}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '0.9rem', color: '#475569' }}>
                {nonChapterQuestions.length} question{nonChapterQuestions.length > 1 ? 's' : ''}
              </div>
              <button onClick={() => setIdx(0)}
                style={{ padding: '0.8rem 2.5rem', borderRadius: '12px', background: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 'clamp(1rem, 2vw, 1.3rem)', letterSpacing: '0.02em' }}>
                Commencer →
              </button>
            </div>
          </div>
        )}

        {!loading && !onCover && question && (
          <div style={{ width: '100%', maxWidth: 860 }}>
            {/* Slide chapitre */}
            {isChapitre ? (
              <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, color: '#f1f5f9', lineHeight: 1.2, margin: 0, borderLeft: '6px solid #6366f1', paddingLeft: '1.5rem', textAlign: 'left' }}>
                  {question.texte}
                </h2>
              </div>
            ) : (<>
            {/* Numéro */}
            <div style={{ fontSize: '1rem', color: '#475569', fontWeight: 700, marginBottom: '1.2rem', letterSpacing: '0.03em' }}>
              QUESTION {questionNumero} <span style={{ color: '#334155' }}>/ {nonChapterQuestions.length}</span>
            </div>

            {/* Image de la question */}
            {question.image_url && (
              <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                <img src={`${apiBase}${question.image_url}`} alt="" style={{ maxHeight: 280, maxWidth: '100%', borderRadius: '12px', border: '1px solid #1e293b' }} />
              </div>
            )}

            {/* Texte de la question */}
            <p style={{ fontSize: 'clamp(1.4rem, 3vw, 2.2rem)', fontWeight: 700, color: '#f1f5f9', lineHeight: 1.35, marginBottom: '2rem', margin: '0 0 2rem' }}>
              {question.texte}
              {question.obligatoire === false && (
                <span style={{ marginLeft: '0.6rem', fontSize: '0.7em', color: '#475569', fontWeight: 400 }}>(optionnel)</span>
              )}
            </p>

            {/* Options */}
            {question.type_reponse === 'choix' && (question.options || []).map((opt, oi) => (
              <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.9rem 1.4rem', borderRadius: '12px', border: '1px solid #1e293b', background: '#1e293b', marginBottom: '0.7rem', fontSize: 'clamp(1rem, 2vw, 1.4rem)', color: '#cbd5e1' }}>
                <span style={{ minWidth: 36, height: 36, borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9em', color: '#94a3b8', flexShrink: 0 }}>
                  {String.fromCharCode(65 + oi)}
                </span>
                {question.options_images?.[oi] && (
                  <img src={`${apiBase}${question.options_images[oi]}`} alt="" style={{ maxHeight: 60, borderRadius: 6 }} />
                )}
                <span>{opt}</span>
              </div>
            ))}

            {question.type_reponse === 'smiley' && (
              <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
                {SMILEY_LABELS.map(({ e, l }) => (
                  <div key={l} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}>{e}</span>
                    <span style={{ fontSize: '0.75rem', color: '#475569', textAlign: 'center' }}>{l}</span>
                  </div>
                ))}
              </div>
            )}

            {question.type_reponse === 'note' && (
              <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1rem' }}>
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <div key={n} style={{ width: 'clamp(44px,6vw,70px)', height: 'clamp(44px,6vw,70px)', borderRadius: '10px', border: '1px solid #334155', background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'clamp(1rem,2vw,1.5rem)', color: '#94a3b8', fontWeight: 700 }}>{n}</div>
                ))}
              </div>
            )}

            {question.type_reponse === 'oui_non' && (
              <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem' }}>
                {['Oui', 'Non'].map(v => (
                  <div key={v} style={{ flex: 1, padding: '1.2rem', textAlign: 'center', borderRadius: '12px', border: '1px solid #334155', background: '#1e293b', fontSize: 'clamp(1.2rem,3vw,2rem)', color: '#cbd5e1', fontWeight: 700 }}>{v}</div>
                ))}
              </div>
            )}

            {question.type_reponse === 'texte' && (
              <div style={{ marginTop: '1rem', border: '1px solid #334155', borderRadius: '12px', padding: '1.5rem', background: '#1e293b', fontSize: '1rem', color: '#475569', minHeight: 80 }}>Réponse libre…</div>
            )}

            {question.type_reponse === 'photo' && (
              <div style={{ marginTop: '1rem', border: '2px dashed #334155', borderRadius: '12px', padding: '2rem', textAlign: 'center', color: '#475569', fontSize: '1.1rem' }}>📷 Le stagiaire prend une photo</div>
            )}
            </>)}
          </div>
        )}
      </div>

      {/* Barre de navigation */}
      {questions.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.2rem', padding: '1rem 1.5rem', borderTop: '1px solid #1e293b', flexShrink: 0 }}>
          {/* Retour à la couverture */}
          <button onClick={() => setIdx(-1)} disabled={onCover}
            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #334155', background: onCover ? '#0f172a' : '#1e293b', color: onCover ? '#334155' : '#94a3b8', cursor: onCover ? 'default' : 'pointer', fontSize: '0.85rem' }}>
            ⌂ Accueil
          </button>
          <button onClick={() => setIdx(i => Math.max(i - 1, 0))} disabled={onCover || idx === 0}
            style={{ padding: '0.5rem 1.4rem', borderRadius: '8px', border: '1px solid #334155', background: (onCover || idx === 0) ? '#0f172a' : '#1e293b', color: (onCover || idx === 0) ? '#334155' : '#e2e8f0', cursor: (onCover || idx === 0) ? 'default' : 'pointer', fontSize: '1rem', fontWeight: 700 }}>
            ← Précédente
          </button>
          {/* Pastilles de navigation */}
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', justifyContent: 'center', maxWidth: 400 }}>
            {questions.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} style={{ width: 10, height: 10, borderRadius: '50%', border: 'none', cursor: 'pointer', background: i === idx ? '#3b82f6' : '#334155', padding: 0, transition: 'background 0.15s' }} />
            ))}
          </div>
          <button onClick={() => setIdx(i => Math.min(i + 1, questions.length - 1))} disabled={!onCover && idx === questions.length - 1}
            style={{ padding: '0.5rem 1.4rem', borderRadius: '8px', border: '1px solid #334155', background: (!onCover && idx === questions.length - 1) ? '#0f172a' : '#1e293b', color: (!onCover && idx === questions.length - 1) ? '#334155' : '#e2e8f0', cursor: (!onCover && idx === questions.length - 1) ? 'default' : 'pointer', fontSize: '1rem', fontWeight: 700 }}>
            {onCover ? 'Commencer →' : 'Suivante →'}
          </button>
          <button onClick={() => setIdx(questions.length - 1)} disabled={!onCover && idx === questions.length - 1}
            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #334155', background: (!onCover && idx === questions.length - 1) ? '#0f172a' : '#1e293b', color: (!onCover && idx === questions.length - 1) ? '#334155' : '#94a3b8', cursor: (!onCover && idx === questions.length - 1) ? 'default' : 'pointer', fontSize: '0.85rem' }}>
            Fin ↦
          </button>
        </div>
      )}
    </div>
  );
}

// ── Panneau QR Questionnaire ──────────────────────────────────────────────────
function QRQuestionnairesPanel({ sessionId, formationId, nomFormation, occurrences = [], groupes = [], presences = [], inscriptions = [], session = {}, modules = [], typesQ = TYPES_Q_DEFAULT }) {
  const { isFormateur, formateurId: ctxFormateurId } = useAuth();
  const [config, setConfig] = useState({ type_actif: 'none', questionnaire_id: null, module_id: null });
  const [tokens, setTokens] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [presenting, setPresenting] = useState(false);

  // Émargement
  const [emargements, setEmargements] = useState([]);
  const [emargQrVisible, setEmargQrVisible] = useState(null);
  const [emargGenerating, setEmargGenerating] = useState(false);
  const [emargForm, setEmargForm] = useState({ type: 'presence', date: '', slot: 'AM', sous_session_id: '', expires_minutes: 480 });
  const [pdfJournalierLoading, setPdfJournalierLoading] = useState({});
  const [emargDocLoading, setEmargDocLoading] = useState({});
  const [dateEmargFiltree, setDateEmargFiltree] = useState(null);

  // Panneau gestion des présences
  const [gestionEmargId, setGestionEmargId] = useState(null);
  const [gestionDetails, setGestionDetails] = useState(null);
  const [gestionLoading, setGestionLoading] = useState(false);
  const [gestionQrVisible, setGestionQrVisible] = useState(false);

  // Dates accessibles au formateur connecté (null = pas de filtre pour admin/utilisateur)
  const formateurDates = React.useMemo(() => {
    if (!isFormateur || !ctxFormateurId) return null;
    const dates = new Set();
    const isPrincipal = ctxFormateurId === session?.formateur_id;
    occurrences.forEach(occ => {
      const d = occ.date_iso || String(occ.date || '').substring(0, 10);
      if (!d) return;
      if (isPrincipal) {
        if (!occ.formateur_id || occ.formateur_id === ctxFormateurId) dates.add(d);
      } else {
        if (occ.formateur_id === ctxFormateurId) dates.add(d);
      }
    });
    return dates;
  }, [isFormateur, ctxFormateurId, session, occurrences]);
  const canManageDate = (dateStr) => !formateurDates || formateurDates.has(dateStr);

  // Module du formateur connecté dans cette session (null si formateur principal ou admin)
  const formateurModule = React.useMemo(() => {
    if (!isFormateur || !ctxFormateurId) return null;
    const isPrincipal = ctxFormateurId === session?.formateur_id;
    if (isPrincipal) return null; // formateur principal = accès complet
    return modules.find(m => m.formateur_id === ctxFormateurId) || null;
  }, [isFormateur, ctxFormateurId, session, modules]);

  // Fiche progrès — sélecteur de formateur (vue admin, session multi-formateurs)
  const formateursSession = React.useMemo(() => {
    const list = [];
    const seen = new Set();
    if (session?.formateur_id) {
      list.push({ formateur_id: session.formateur_id, nom: session.nom_formateur || session.formateur_id });
      seen.add(session.formateur_id);
    }
    (session?.occurrences || []).forEach(occ => {
      if (occ.formateur_id && !seen.has(occ.formateur_id)) {
        seen.add(occ.formateur_id);
        list.push({ formateur_id: occ.formateur_id, nom: occ.occurrence_formateur_nom || occ.formateur_id });
      }
    });
    return list;
  }, [session]);
  // Formateur interne : compte admin/utilisateur avec un formateur_id assigné à une occurrence
  const isFormateurInterne = !isFormateur && !!ctxFormateurId && formateursSession.some(f => String(f.formateur_id) === String(ctxFormateurId));
  const [selectedFPFormateur, setSelectedFPFormateur] = useState(() => {
    if (ctxFormateurId && formateursSession.some(f => String(f.formateur_id) === String(ctxFormateurId))) return ctxFormateurId;
    return formateursSession[0]?.formateur_id || null;
  });

  // Construit l'URL de tokens en fonction du type/questionnaire actif
  function buildTokenUrl(cfg, modId) {
    const base = '/sessions/' + sessionId + '/qr-tokens';
    const effectiveModId = modId !== undefined ? modId : (formateurModule?.id || cfg.module_id || null);
    const modSuffix = effectiveModId ? '&module_id=' + effectiveModId : '';
    if (cfg.type_actif === 'none') return base + (effectiveModId ? '?module_id=' + effectiveModId : '');
    if (cfg.questionnaire_id) return base + '?questionnaire_id=' + cfg.questionnaire_id + modSuffix;
    if (typeIsCommonFn(cfg.type_actif)) return base + '?questionnaire_id=null' + modSuffix;
    return base + (effectiveModId ? '?module_id=' + effectiveModId : '');
  }

  const charger = useCallback(async () => {
    try {
      const [cfg, qrs] = await Promise.all([
        apiFetch('/sessions/' + sessionId + '/qr-config'),
        apiFetch('/questionnaires' + (formationId ? '?formation_id=' + formationId : '')).catch(() => ({ data: [] })),
      ]);
      setConfig(cfg);
      setQuestionnaires(qrs.data || []);
      if (!estTypeEmargement(cfg.type_actif)) {
        const toks = await apiFetch(buildTokenUrl(cfg));
        setTokens(toks.data || []);
      }
    } catch (e) { console.error(e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, formationId, formateurModule]);

  const chargerEmargements = useCallback(async () => {
    try {
      const r = await apiFetch('/emargement/session/' + sessionId);
      setEmargements(r.data || []);
    } catch(e) { console.error(e); }
  }, [sessionId]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    if (estTypeEmargement(config.type_actif)) chargerEmargements();
  }, [config.type_actif, chargerEmargements]);

  // Actualisation automatique toutes les 5s quand un questionnaire est actif
  useEffect(() => {
    if (config.type_actif === 'none' || estTypeEmargement(config.type_actif)) return;
    const hasQ = typeIsCommonFn(config.type_actif) || !!config.questionnaire_id;
    if (!hasQ) return;
    const url = buildTokenUrl(config);
    const interval = setInterval(async () => {
      try {
        const toks = await apiFetch(url);
        setTokens(toks.data || []);
      } catch(e) {}
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.type_actif, config.questionnaire_id, config.module_id, sessionId, formateurModule]);

  async function sauvegarderConfig(type_actif, questionnaire_id) {
    setSaving(true);
    const module_id = formateurModule?.id || null;
    try {
      await apiFetch('/sessions/' + sessionId + '/qr-config', {
        method: 'PUT',
        body: JSON.stringify({ type_actif, questionnaire_id, module_id }),
      });
      const newCfg = { type_actif, questionnaire_id, module_id };
      setConfig(newCfg);
      if (estTypeEmargement(type_actif)) {
        chargerEmargements();
      } else {
        const toks = await apiFetch(buildTokenUrl(newCfg, module_id));
        setTokens(toks.data || []);
      }
    } catch (e) { alert('Erreur : ' + e.message); }
    finally { setSaving(false); }
  }

  async function resetToken(token) {
    setResetting(token);
    try {
      await apiFetch('/qr-tokens/' + token + '/reset', {
        method: 'POST',
        body: JSON.stringify({ questionnaire_id: config.questionnaire_id || null }),
      });
      const toks = await apiFetch(buildTokenUrl(config));
      setTokens(toks.data || []);
    } catch (e) { alert('Erreur : ' + e.message); }
    finally { setResetting(null); }
  }

  async function genererEmargement() {
    if (!emargForm.date && emargForm.type !== 'amenagement') { alert('Veuillez sélectionner une date'); return; }
    setEmargGenerating(true);
    try {
      await apiFetch('/emargement/generer', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          sous_session_id: emargForm.type === 'amenagement' ? null : (emargForm.sous_session_id || null),
          date: emargForm.type === 'amenagement' ? null : emargForm.date,
          slot: emargForm.type === 'amenagement' ? null : (emargForm.slot || null),
          type: emargForm.type,
          expires_minutes: parseInt(emargForm.expires_minutes) || 480,
        }),
      });
      chargerEmargements();
    } catch(e) { alert('Erreur : ' + e.message); }
    finally { setEmargGenerating(false); }
  }

  async function cloreEmargement(token) {
    if (!window.confirm('Clore cet émargement ? Il ne sera plus possible de signer.')) return;
    try {
      await apiFetch('/emargement/' + token + '/clore', { method: 'POST' });
      chargerEmargements();
    } catch(e) { alert('Erreur : ' + e.message); }
  }

  async function supprimerEmargement(id) {
    if (!window.confirm('Supprimer définitivement cet émargement ?')) return;
    try {
      await apiFetch('/emargement/' + id, { method: 'DELETE' });
      chargerEmargements();
    } catch(e) { alert('Erreur : ' + e.message); }
  }

  async function chargerGestion(id) {
    setGestionEmargId(id);
    setGestionDetails(null);
    setGestionLoading(true);
    setGestionQrVisible(false);
    try {
      const d = await apiFetch('/emargement/' + id + '/details');
      setGestionDetails(d);
    } catch(e) { alert('Erreur : ' + e.message); }
    finally { setGestionLoading(false); }
  }

  async function marquerPresent(emId, stagiaire_id, nom_complet) {
    try {
      await apiFetch('/emargement/' + emId + '/presence-manuelle', {
        method: 'POST',
        body: JSON.stringify({ stagiaire_id, nom_signataire: nom_complet, statut_presence: 'Présent' }),
      });
      chargerGestion(emId);
      chargerEmargements();
    } catch(e) { alert('Erreur : ' + e.message); }
  }

  async function marquerAbsent(emId, stagiaire_id, nom_complet) {
    const motif = window.prompt('Motif d\'absence (optionnel) :');
    if (motif === null) return;
    try {
      await apiFetch('/emargement/' + emId + '/presence-manuelle', {
        method: 'POST',
        body: JSON.stringify({ stagiaire_id, nom_signataire: nom_complet, statut_presence: 'absent', motif_absence: motif || null }),
      });
      chargerGestion(emId);
      chargerEmargements();
    } catch(e) { alert('Erreur : ' + e.message); }
  }

  async function annulerPresence(emId, stagiaire_id) {
    const motif = window.prompt('Motif d\'absence (optionnel) :');
    if (motif === null) return; // annulé
    try {
      await apiFetch('/emargement/' + emId + '/presence/' + stagiaire_id, {
        method: 'DELETE',
        body: JSON.stringify({ motif_absence: motif || null }),
      });
      chargerGestion(emId);
      chargerEmargements();
    } catch(e) { alert('Erreur : ' + e.message); }
  }

  async function annulerSignatureFormateur(emId) {
    if (!window.confirm('Annuler la signature du formateur ? Il pourra resigner via son lien d\'accès.')) return;
    try {
      await apiFetch('/emargement/' + emId + '/signature-formateur', { method: 'DELETE' });
      chargerGestion(emId);
      chargerEmargements();
    } catch(e) { alert('Erreur : ' + e.message); }
  }

  const typeIsCommonFn = (val) => typesQ.find(t => t.val === val)?.is_common ?? (val === 'satisfaction');
  const TYPES = [
    { val: 'none',       label: '— Aucun (désactivé)' },
    ...typesQ.map(t => ({ val: t.val, label: t.label + (t.is_common ? ' (commun)' : '') })),
    { val: 'emargement', label: 'Émargement' },
    { val: 'amenagement', label: "Aménagement d'horaires" },
  ];

  const inputS = { padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem' };

  const qrsFiltered = questionnaires.filter(q => q.type === config.type_actif);
  const isEmargement = estTypeEmargement(config.type_actif);
  const aTypeActif = config.type_actif !== 'none';
  const hasQuestionnaire = typeIsCommonFn(config.type_actif) || !!config.questionnaire_id;
  const presentId = !isEmargement && config.type_actif !== 'none' ? config.questionnaire_id : null;

  return (
    <div>
      {/* Présentation plein écran */}
      {presenting && presentId && (
        <QuestionnairePresentation questionnaireId={presentId} nomFormation={nomFormation} onClose={() => setPresenting(false)} />
      )}

      <SectionRepliable title="📋 Fiche de Progrès Formateur" defaultOpen={false} borderColor="#7c3aed">
        <div style={{ padding: '0 1rem' }}>
          {!isFormateur && formateursSession.length > 1 && (
            <div style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Formateur :</span>
              <select
                value={selectedFPFormateur || ''}
                onChange={e => setSelectedFPFormateur(e.target.value || null)}
                style={{ padding: '0.25rem 0.5rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                {formateursSession.map(f => (
                  <option key={f.formateur_id} value={f.formateur_id} style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}>{f.nom}</option>
                ))}
              </select>
            </div>
          )}
          <FicheProgresTab
            sessionId={sessionId}
            session={session}
            formateurId={isFormateur ? ctxFormateurId : (selectedFPFormateur || null)}
          />
        </div>
      </SectionRepliable>

      <SectionRepliable title="📡 Diffusion" defaultOpen={false}>

      {/* Sélection du type actif */}
      <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '1rem 1.2rem', border: '1px solid var(--border)', marginBottom: '1rem', margin: '0.75rem' }}>
        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.8rem' }}>
          {isEmargement
            ? (config.type_actif === 'amenagement'
              ? "Aménagement d'horaires actif — les stagiaires concernés signent via leur QR code chevalet, une fois l'accord de leur employeur reçu"
              : 'Émargement actif — les stagiaires signent via leur QR code chevalet')
            : 'Questionnaire actif — les stagiaires verront ce questionnaire en scannant leur QR code'}
        </div>

        {/* ── Formateur de module : vue restreinte ── */}
        {formateurModule ? (
          <div>
            <div style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', marginBottom: '0.8rem' }}>
              Module : <strong>{formateurModule.intitule_module}</strong>
            </div>
            {!formateurModule.questionnaire_id && !formateurModule.satisfaction_questionnaire_id ? (
              <div style={{ padding: '0.5rem 0.8rem', background: '#fef9c3', borderRadius: '6px', fontSize: '0.82rem', color: '#854d0e' }}>
                ⚠ Aucun questionnaire assigné à votre module. Contactez l'administrateur.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {/* Ligne évaluation */}
                {formateurModule.questionnaire_id && (() => {
                  const qEval = questionnaires.find(q => String(q.questionnaire_id) === String(formateurModule.questionnaire_id));
                  const isActive = String(config.questionnaire_id) === String(formateurModule.questionnaire_id) && config.type_actif === 'evaluation';
                  return (
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center', padding: '0.5rem 0.7rem', borderRadius: '8px', border: `1px solid ${isActive ? '#16a34a55' : 'var(--border)'}`, background: isActive ? '#f0fdf4' : 'var(--bg)' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: '10px', background: '#eff6ff', color: '#2563eb' }}>Évaluation</span>
                      <span style={{ fontSize: '0.85rem', flex: 1 }}>{qEval?.titre || `Questionnaire #${formateurModule.questionnaire_id}`}</span>
                      {isActive ? (
                        <button onClick={() => sauvegarderConfig('none', null)} disabled={saving}
                          style={{ padding: '0.28rem 0.7rem', borderRadius: '6px', border: '1px solid #f97316', background: '#fff7ed', color: '#f97316', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          ⏹ Désactiver
                        </button>
                      ) : (
                        <button onClick={() => sauvegarderConfig('evaluation', formateurModule.questionnaire_id)} disabled={saving}
                          style={{ padding: '0.28rem 0.7rem', borderRadius: '6px', border: '1px solid #16a34a', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          ▶ Activer
                        </button>
                      )}
                      {isActive && presentId && (
                        <button onClick={() => setPresenting(true)}
                          style={{ padding: '0.28rem 0.7rem', borderRadius: '6px', background: '#1e40af', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' }}>
                          🖥 Diffuser
                        </button>
                      )}
                    </div>
                  );
                })()}
                {/* Ligne satisfaction */}
                {formateurModule.satisfaction_questionnaire_id && (() => {
                  const qSat = questionnaires.find(q => String(q.questionnaire_id) === String(formateurModule.satisfaction_questionnaire_id));
                  const isActive = String(config.questionnaire_id) === String(formateurModule.satisfaction_questionnaire_id) && config.type_actif === 'satisfaction';
                  return (
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center', padding: '0.5rem 0.7rem', borderRadius: '8px', border: `1px solid ${isActive ? '#16a34a55' : 'var(--border)'}`, background: isActive ? '#f0fdf4' : 'var(--bg)' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: '10px', background: '#fef3c7', color: '#b45309' }}>Satisfaction</span>
                      <span style={{ fontSize: '0.85rem', flex: 1 }}>{qSat?.titre || `Questionnaire #${formateurModule.satisfaction_questionnaire_id}`}</span>
                      {isActive ? (
                        <button onClick={() => sauvegarderConfig('none', null)} disabled={saving}
                          style={{ padding: '0.28rem 0.7rem', borderRadius: '6px', border: '1px solid #f97316', background: '#fff7ed', color: '#f97316', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          ⏹ Désactiver
                        </button>
                      ) : (
                        <button onClick={() => sauvegarderConfig('satisfaction', formateurModule.satisfaction_questionnaire_id)} disabled={saving}
                          style={{ padding: '0.28rem 0.7rem', borderRadius: '6px', border: '1px solid #16a34a', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          ▶ Activer
                        </button>
                      )}
                      {isActive && presentId && (
                        <button onClick={() => setPresenting(true)}
                          style={{ padding: '0.28rem 0.7rem', borderRadius: '6px', background: '#1e40af', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' }}>
                          🖥 Diffuser
                        </button>
                      )}
                    </div>
                  );
                })()}
                {saving && <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Sauvegarde...</span>}
              </div>
            )}
          </div>
        ) : (
          /* ── Admin / utilisateur / formateur principal : vue complète ── */
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 2 }}>Type</label>
              <select value={config.type_actif} onChange={e => sauvegarderConfig(e.target.value, null)} style={inputS} disabled={saving}>
                {TYPES.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
              </select>
            </div>
            {!isEmargement && config.type_actif !== 'none' && qrsFiltered.length > 0 && (
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 2 }}>Questionnaire</label>
                <select value={config.questionnaire_id || ''} onChange={e => sauvegarderConfig(config.type_actif, e.target.value || null)} style={inputS} disabled={saving}>
                  <option value="">— Sélectionner —</option>
                  {qrsFiltered.map(q => <option key={q.questionnaire_id} value={q.questionnaire_id}>{q.titre}</option>)}
                </select>
              </div>
            )}
            {saving && <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Sauvegarde...</span>}
            {!isEmargement && presentId && (
              <button onClick={() => setPresenting(true)}
                style={{ padding: '0.45rem 1rem', borderRadius: '8px', background: '#1e40af', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                🖥 Diffuser à l'écran
              </button>
            )}
          </div>
        )}

        {aTypeActif && (
          <div style={{ marginTop: '0.6rem', padding: '0.5rem 0.8rem', background: '#f0fdf4', borderRadius: '6px', fontSize: '0.8rem', color: '#16a34a', fontWeight: 600 }}>
            ✓ {isEmargement ? (config.type_actif === 'amenagement' ? 'Signature active' : 'Émargement actif') : 'Questionnaire actif'} : {TYPES.find(t => t.val === config.type_actif)?.label}
            {!isEmargement && (() => {
              const qSel = config.questionnaire_id ? questionnaires.find(q => String(q.questionnaire_id) === String(config.questionnaire_id)) : null;
              if (qSel) return <span style={{ fontWeight: 400, marginLeft: 6 }}>— {qSel.titre}</span>;
              if (!config.questionnaire_id && qrsFiltered.length > 0 && !formateurModule) return <span style={{ color: '#f59e0b', fontWeight: 400, marginLeft: 6 }}>⚠ Sélectionnez un questionnaire ci-dessus</span>;
              return null;
            })()}
          </div>
        )}
      </div>

      {/* ── Émargement : formulaire de génération ── */}
      {isEmargement && (
        <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '1rem 1.2rem', border: '1px solid var(--border)', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.8rem' }}>
            Générer un émargement
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 2 }}>Type</label>
              <select value={emargForm.type} onChange={e => setEmargForm(f => ({ ...f, type: e.target.value }))} style={inputS}>
                <option value="presence">Présence</option>
                <option value="certificat">Certificat</option>
                <option value="amenagement">Aménagement d'horaires</option>
              </select>
            </div>
            <div style={emargForm.type === 'amenagement' ? { display: 'none' } : undefined}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 2 }}>Date</label>
              {(() => {
                const allDates = [...new Set(occurrences.map(o => o.date_iso || String(o.date || '').substring(0, 10)))].filter(Boolean).sort();
                const datesDisponibles = formateurDates ? allDates.filter(d => formateurDates.has(d)) : allDates;
                return datesDisponibles.length > 0 ? (
                  <select value={emargForm.date} onChange={e => setEmargForm(f => ({ ...f, date: e.target.value }))} style={inputS}>
                    <option value="">— Sélectionner —</option>
                    {datesDisponibles.map(d => (
                      <option key={d} value={d}>{formatDate(d)}</option>
                    ))}
                  </select>
                ) : isFormateur ? (
                  <div style={{ padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    Aucune date assignée
                  </div>
                ) : (
                  <input type="date" value={emargForm.date} onChange={e => setEmargForm(f => ({ ...f, date: e.target.value }))} style={inputS} />
                );
              })()}
            </div>
            <div style={emargForm.type === 'amenagement' ? { display: 'none' } : undefined}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 2 }}>Créneau</label>
              <select value={emargForm.slot} onChange={e => setEmargForm(f => ({ ...f, slot: e.target.value }))} style={inputS}>
                <option value="AM">Matin</option>
                <option value="PM">Après-midi</option>
                <option value="">Journée entière</option>
              </select>
            </div>
            {groupes.length > 0 && emargForm.type !== 'amenagement' && (
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 2 }}>Groupe</label>
                <select value={emargForm.sous_session_id} onChange={e => setEmargForm(f => ({ ...f, sous_session_id: e.target.value }))} style={inputS}>
                  <option value="">Tous</option>
                  {groupes.map(g => <option key={g.sous_session_id} value={g.sous_session_id}>{g.groupe || g.sous_session_id}</option>)}
                </select>
              </div>
            )}
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 2 }}>Validité</label>
              <select value={emargForm.expires_minutes} onChange={e => setEmargForm(f => ({ ...f, expires_minutes: e.target.value }))} style={inputS}>
                <option value={30}>30 min</option>
                <option value={60}>1h</option>
                <option value={120}>2h</option>
                <option value={240}>4h</option>
                <option value={480}>8h (journée)</option>
                <option value={1440}>24h</option>
              </select>
            </div>
            <button onClick={genererEmargement} disabled={emargGenerating || (emargForm.type !== 'amenagement' && !emargForm.date)}
              style={{ padding: '0.45rem 1rem', borderRadius: '8px', background: emargGenerating || (emargForm.type !== 'amenagement' && !emargForm.date) ? '#94a3b8' : '#1e40af', color: '#fff', border: 'none', cursor: emargGenerating || (emargForm.type !== 'amenagement' && !emargForm.date) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
              {emargGenerating ? 'Génération...' : '+ Générer'}
            </button>
          </div>
        </div>
      )}

      {/* ── Feuille d'émargement complète (1 jour/page, portrait A4) ── */}
      {isEmargement && (() => {
        const btnJournalier = (loadKey, url, label, style = {}) => (
          <button
            key={loadKey}
            disabled={!!pdfJournalierLoading[loadKey]}
            onClick={async () => {
              setPdfJournalierLoading(p => ({ ...p, [loadKey]: true }));
              try { await imprimerUrl(url, `Émargement — ${label.replace(/^⬇\s*/, '')}`); }
              catch(e) { alert('Erreur : ' + e.message); }
              finally { setPdfJournalierLoading(p => ({ ...p, [loadKey]: false })); }
            }}
            style={{
              padding: '0.4rem 0.9rem', borderRadius: '7px', fontWeight: 600, fontSize: '0.82rem',
              cursor: 'pointer', opacity: pdfJournalierLoading[loadKey] ? 0.6 : 1,
              ...style,
            }}>
            {pdfJournalierLoading[loadKey] ? '⏳...' : label}
          </button>
        );

        // Dates distinctes issues des émargements de présence
        const datesDisponibles = [...new Set(
          emargements
            .filter(e => e.type === 'presence' && e.date_emargt)
            .map(e => String(e.date_emargt).substring(0, 10))
            .filter(Boolean)
        )].sort();

        return (
          <>
          <div style={{
            background: '#f0f5ff', border: '1px solid #bfdbfe', borderRadius: '10px',
            padding: '0.9rem 1.2rem', marginBottom: '1rem',
          }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a3668', marginBottom: '0.7rem' }}>
              📋 Feuilles d'émargement — format journalier (1 jour / page A4 portrait)
            </div>

            {/* ── Ligne 1 : session complète ── */}
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.78rem', color: '#4b5563', fontWeight: 600, marginRight: '0.5rem' }}>Session complète :</span>
              <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0.4rem', verticalAlign: 'middle' }}>
                {btnJournalier('session',
                  `/api/emargement/session/${sessionId}/pdf-journalier`,
                  '⬇ Tous groupes',
                  { border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', fontWeight: 700 }
                )}
                {groupes.map(g => btnJournalier(
                  `grp_${g.sous_session_id}`,
                  `/api/emargement/session/${sessionId}/pdf-journalier?sous_session_id=${g.sous_session_id}`,
                  `⬇ ${g.nom_client || g.groupe || g.sous_session_id}`,
                  { border: '1px solid #1a3668', background: '#eff6ff', color: '#1a3668' }
                ))}
              </div>
            </div>

            {/* ── Ligne 2 : par date ── */}
            {datesDisponibles.length > 0 && (
              <div style={{ borderTop: '1px solid #bfdbfe', paddingTop: '0.6rem', marginTop: '0.3rem' }}>
                <span style={{ fontSize: '0.78rem', color: '#4b5563', fontWeight: 600 }}>Par date : </span>
                <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.35rem' }}>
                  {datesDisponibles.map(d => (
                    <button
                      key={d}
                      onClick={() => setDateEmargFiltree(dateEmargFiltree === d ? null : d)}
                      style={{
                        padding: '0.25rem 0.65rem', borderRadius: '5px', fontSize: '0.78rem', fontWeight: 600,
                        border: `1px solid ${dateEmargFiltree === d ? '#1d4ed8' : '#93c5fd'}`,
                        background: dateEmargFiltree === d ? '#1d4ed8' : '#fff',
                        color: dateEmargFiltree === d ? '#fff' : '#1a3668',
                        cursor: 'pointer',
                      }}>
                      {formatDate(d)}
                    </button>
                  ))}
                </div>

                {/* Boutons de téléchargement pour la date sélectionnée */}
                {dateEmargFiltree && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Télécharger le {formatDate(dateEmargFiltree)} :</span>
                    {btnJournalier(
                      `date_${dateEmargFiltree}`,
                      `/api/emargement/session/${sessionId}/pdf-journalier?date=${dateEmargFiltree}`,
                      '⬇ Tous groupes',
                      { border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', fontWeight: 700 }
                    )}
                    {groupes.map(g => btnJournalier(
                      `date_${dateEmargFiltree}_${g.sous_session_id}`,
                      `/api/emargement/session/${sessionId}/pdf-journalier?sous_session_id=${g.sous_session_id}&date=${dateEmargFiltree}`,
                      `⬇ ${g.nom_client || g.groupe || g.sous_session_id}`,
                      { border: '1px solid #1a3668', background: '#eff6ff', color: '#1a3668' }
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.6rem' }}>
              Toutes les signatures · Colonne certificat sur la dernière page · Numérotation automatique
            </div>
          </div>

          {/* ── Feuille de remise des certificats ── */}
          <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: '10px', padding: '0.9rem 1.2rem', marginBottom: '1rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#5b21b6', marginBottom: '0.7rem' }}>
              📜 Feuille de remise des certificats
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
              {btnJournalier('certif_session',
                `/api/emargement/session/${sessionId}/feuille-certificat`,
                '⬇ Tous groupes',
                { border: '1px solid #7c3aed', background: '#7c3aed', color: '#fff', fontWeight: 700 }
              )}
              {groupes.map(g => btnJournalier(
                `certif_grp_${g.sous_session_id}`,
                `/api/emargement/session/${sessionId}/feuille-certificat?sous_session_id=${g.sous_session_id}`,
                `⬇ ${g.nom_client || g.groupe || g.sous_session_id}`,
                { border: '1px solid #5b21b6', background: '#ede9fe', color: '#5b21b6' }
              ))}
            </div>
          </div>
        </>
        );
      })()}

      {/* ── Émargement : liste ── */}
      {isEmargement && (
        emargements.length === 0 ? (
          <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            Aucun émargement généré.
          </div>
        ) : (
          <div>
            {emargements.map(em => {
              const actif = !em.clos && new Date(em.expires_at) > new Date();
              const lien = `${APP_URL_BASE}/emargement/${em.token}`;
              const emDate = em.date_emargt_iso || String(em.date_emargt || '').substring(0, 10);
              const estAmen = em.type === 'amenagement';
              const canManage = estAmen || canManageDate(emDate);
              return (
                <div key={em.id} style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '1rem 1.2rem', border: `1px solid ${actif && canManage ? '#16a34a33' : 'var(--border)'}`, marginBottom: '0.8rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                        {estAmen ? "Aménagement d'horaires" : em.type === 'presence' ? 'Présence' : 'Certificat'} — {formatDate(em.date_emargt)}{em.slot ? ` (${em.slot})` : ''}
                        {em.sous_session_id && <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>· Groupe {em.sous_session_id}</span>}
                      </div>
                      <div style={{ marginTop: 4, display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '0.15rem 0.6rem', borderRadius: '10px',
                          background: em.clos ? '#f1f5f9' : actif ? '#f0fdf4' : '#fef2f2',
                          color: em.clos ? '#94a3b8' : actif ? '#16a34a' : '#dc2626' }}>
                          {em.clos ? '⏹ Clos' : actif ? '🟢 Actif' : '⌛ Expiré'}
                        </span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{estAmen ? `${em.nb_signataires} / ${em.nb_a_signer} attestation(s) signée(s)` : `${em.nb_signataires} signataire(s)`}</span>
                        {!em.clos && <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Expire {new Date(em.expires_at).toLocaleString('fr-FR')}</span>}
                        {isFormateur && !canManage && (
                          <span style={{ fontSize: '0.72rem', padding: '0.1rem 0.5rem', borderRadius: '10px', background: '#f1f5f9', color: '#94a3b8', fontWeight: 600 }}>
                            🔒 Lecture seule
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      {/* Bouton Gérer les présences — admin/utilisateur uniquement */}
                      {!isFormateur && !estAmen && (
                        <button onClick={() => gestionEmargId === em.id ? (setGestionEmargId(null), setGestionDetails(null)) : chargerGestion(em.id)}
                          style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: `1px solid ${gestionEmargId === em.id ? '#7c3aed' : '#6b7280'}`, background: gestionEmargId === em.id ? '#f5f3ff' : 'var(--bg)', color: gestionEmargId === em.id ? '#7c3aed' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          👥 {gestionEmargId === em.id ? 'Fermer' : 'Gérer'}
                        </button>
                      )}
                      {/* Boutons Feuille HTML + PDF — toujours visibles (sauf aménagement d'horaires : pas de feuille) */}
                      {!estAmen && <>
                      <button
                        disabled={!!emargDocLoading[`feuille_${em.id}`]}
                        onClick={async () => {
                          setEmargDocLoading(p => ({ ...p, [`feuille_${em.id}`]: true }));
                          try { await imprimerUrl(`/api/emargement/${em.id}/feuille`, 'Feuille d\'émargement'); }
                          catch(e) { alert('Erreur : ' + e.message); }
                          finally { setEmargDocLoading(p => ({ ...p, [`feuille_${em.id}`]: false })); }
                        }}
                        style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid #0369a1', background: '#f0f9ff', color: '#0369a1', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, opacity: emargDocLoading[`feuille_${em.id}`] ? 0.6 : 1 }}>
                        {emargDocLoading[`feuille_${em.id}`] ? '⏳' : '👁 Feuille'}
                      </button>
                      <button
                        disabled={!!emargDocLoading[`pdf_${em.id}`]}
                        onClick={async () => {
                          setEmargDocLoading(p => ({ ...p, [`pdf_${em.id}`]: true }));
                          try { await imprimerUrl(`/api/emargement/${em.id}/pdf`, 'Émargement'); }
                          catch(e) { alert('Erreur : ' + e.message); }
                          finally { setEmargDocLoading(p => ({ ...p, [`pdf_${em.id}`]: false })); }
                        }}
                        style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid #0369a1', background: '#0369a1', color: '#fff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, opacity: emargDocLoading[`pdf_${em.id}`] ? 0.6 : 1 }}>
                        {emargDocLoading[`pdf_${em.id}`] ? '⏳' : '⬇ PDF'}
                      </button>
                      </>}
                      {actif && canManage && (
                        <>
                          <button onClick={() => setEmargQrVisible(emargQrVisible === em.id ? null : em.id)}
                            style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid #2563eb', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                            {emargQrVisible === em.id ? '✕ QR' : '📷 QR'}
                          </button>
                          {!estAmen && (
                            <a href={lien} target="_blank" rel="noopener noreferrer"
                              style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid #16a34a', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'none' }}>
                              ✍ Signer (formateur)
                            </a>
                          )}
                        </>
                      )}
                      {actif && isFormateur && !canManage && (
                        <a href={lien} target="_blank" rel="noopener noreferrer"
                          style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid #94a3b8', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'none' }}>
                          👁 Voir
                        </a>
                      )}
                      {!em.clos && canManage && (
                        <button onClick={() => cloreEmargement(em.token)}
                          style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid #f97316', background: '#fff7ed', color: '#f97316', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          ⏹ Clôturer
                        </button>
                      )}
                      {canManage && (
                        <button onClick={() => supprimerEmargement(em.id)}
                          style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#f87171', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          🗑
                        </button>
                      )}
                    </div>
                  </div>

                  {/* QR code de l'émargement (affichage rapide) */}
                  {emargQrVisible === em.id && actif && (
                    <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1rem', background: 'var(--bg)', borderRadius: 8 }}>
                      <QRCodeImg url={lien} size={180} />
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', wordBreak: 'break-all', textAlign: 'center' }}>{lien}</div>
                      <button onClick={() => copierPressePapier(lien)}
                        style={{ fontSize: '0.78rem', padding: '0.25rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer' }}>
                        📋 Copier le lien
                      </button>
                    </div>
                  )}

                  {/* Panneau de gestion des présences */}
                  {gestionEmargId === em.id && (
                    <div style={{ marginTop: '1rem', border: '1px solid #7c3aed44', borderRadius: '10px', overflow: 'hidden' }}>
                      <div style={{ padding: '0.6rem 1rem', background: '#7c3aed', color: '#fff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.88rem', flex: 1 }}>
                          👥 Gestion des présences
                        </span>
                        {gestionDetails && (
                          <span style={{ fontSize: '0.78rem', opacity: 0.85 }}>
                            {gestionDetails.stagiaires.filter(s => s.a_signe && s.signature?.statut_presence !== 'absent').length} / {gestionDetails.stagiaires.length} signé(s)
                            {gestionDetails.stagiaires.filter(s => s.signature?.statut_presence === 'absent').length > 0 && ` · ${gestionDetails.stagiaires.filter(s => s.signature?.statut_presence === 'absent').length} absent(s)`}
                          </span>
                        )}
                      </div>
                      {gestionLoading && (
                        <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Chargement…</div>
                      )}
                      {gestionDetails && (
                        <div style={{ padding: '0.5rem 0' }}>
                          {/* QR code pour faire signer */}
                          {gestionQrVisible && (
                            <div style={{ margin: '0.5rem 1rem', padding: '1rem', background: 'var(--bg-elevated)', borderRadius: 8, border: '1px solid #7c3aed33', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#7c3aed' }}>Le stagiaire scanne ce QR code pour signer</div>
                              <QRCodeImg url={lien} size={200} />
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', wordBreak: 'break-all', textAlign: 'center' }}>{lien}</div>
                              <button onClick={() => setGestionQrVisible(false)}
                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg)', cursor: 'pointer' }}>
                                ✕ Fermer le QR
                              </button>
                            </div>
                          )}
                          {gestionDetails.stagiaires.map(st => {
                            const isAbsent = st.signature?.statut_presence === 'absent';
                            return (
                            <div key={st.stagiaire_id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 1rem', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                              <span style={{ flex: 1, fontWeight: 600, fontSize: '0.88rem', minWidth: 160 }}>{st.nom_complet}</span>
                              {isAbsent ? (
                                <span style={{ padding: '0.15rem 0.55rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700, background: '#fff7ed', color: '#f97316', border: '1px solid #f9731633', whiteSpace: 'nowrap' }}>
                                  ✗ Absent{st.signature?.motif_absence ? ` — ${st.signature.motif_absence}` : ''}
                                </span>
                              ) : st.a_signe ? (
                                <span style={{ padding: '0.15rem 0.55rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700, background: '#f0fdf4', color: '#16a34a', border: '1px solid #16a34a33', whiteSpace: 'nowrap' }}>
                                  {st.signature?.signataire_type === 'manuel' ? '✓ Présent (manuel)' : '✍ Signé'}
                                  {st.signature?.signe_at ? ` ${new Date(st.signature.signe_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}
                                </span>
                              ) : (
                                <span style={{ padding: '0.15rem 0.55rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700, background: '#fef2f2', color: '#dc2626', border: '1px solid #dc262633' }}>
                                  ○ Non signé
                                </span>
                              )}
                              <div style={{ display: 'flex', gap: '0.3rem' }}>
                                {!st.a_signe && (
                                  <>
                                    <button onClick={() => { setGestionQrVisible(true); }}
                                      style={{ padding: '0.22rem 0.55rem', borderRadius: '6px', border: '1px solid #2563eb', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontSize: '0.73rem', fontWeight: 600 }}>
                                      📷 Faire signer
                                    </button>
                                    <button onClick={() => marquerPresent(em.id, st.stagiaire_id, st.nom_complet)}
                                      style={{ padding: '0.22rem 0.55rem', borderRadius: '6px', border: '1px solid #16a34a', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', fontSize: '0.73rem', fontWeight: 600 }}>
                                      ✓ Marquer présent
                                    </button>
                                    <button onClick={() => marquerAbsent(em.id, st.stagiaire_id, st.nom_complet)}
                                      style={{ padding: '0.22rem 0.55rem', borderRadius: '6px', border: '1px solid #f97316', background: '#fff7ed', color: '#f97316', cursor: 'pointer', fontSize: '0.73rem', fontWeight: 600 }}>
                                      ✗ Absent
                                    </button>
                                  </>
                                )}
                                {st.a_signe && (
                                  <button onClick={() => annulerPresence(em.id, st.stagiaire_id)}
                                    style={{ padding: '0.22rem 0.55rem', borderRadius: '6px', border: '1px solid #f97316', background: '#fff7ed', color: '#f97316', cursor: 'pointer', fontSize: '0.73rem', fontWeight: 600 }}>
                                    ✗ Annuler
                                  </button>
                                )}
                              </div>
                            </div>
                            );
                          })}
                          {/* Ligne formateur */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 1rem', background: 'var(--bg-elevated)', flexWrap: 'wrap' }}>
                            <span style={{ flex: 1, fontWeight: 600, fontSize: '0.88rem', minWidth: 160, color: '#7c3aed' }}>🎓 Formateur</span>
                            {gestionDetails.formateur_signature ? (
                              <>
                                <span style={{ padding: '0.15rem 0.55rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700, background: '#f5f3ff', color: '#7c3aed', border: '1px solid #7c3aed33', whiteSpace: 'nowrap' }}>
                                  ✍ Signé
                                  {gestionDetails.formateur_signature.signe_at ? ` ${new Date(gestionDetails.formateur_signature.signe_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}
                                </span>
                                <button onClick={() => annulerSignatureFormateur(em.id)}
                                  style={{ padding: '0.22rem 0.55rem', borderRadius: '6px', border: '1px solid #f97316', background: '#fff7ed', color: '#f97316', cursor: 'pointer', fontSize: '0.73rem', fontWeight: 600 }}>
                                  ✗ Annuler
                                </button>
                              </>
                            ) : (
                              <span style={{ padding: '0.15rem 0.55rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700, background: '#fef2f2', color: '#dc2626', border: '1px solid #dc262633' }}>
                                ○ Non signé
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      </SectionRepliable>

      {!isEmargement && aTypeActif && (
      <SectionRepliable title="👀 Suivi en direct" defaultOpen={true}>
      {!hasQuestionnaire ? (
        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          Sélectionnez un questionnaire dans la section Diffusion pour activer le suivi en direct.
        </div>
      ) : (
      <>
      {/* ── Barre de progression en direct ── */}
      {tokens.length > 0 && (
        <div style={{ margin: '0.75rem', padding: '0.7rem 1rem', background: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#16a34a', boxShadow: '0 0 0 2px #bbf7d0', animation: 'pulse 1.5s infinite' }} />
              En direct — {tokens.filter(t => t.a_repondu).length} / {tokens.filter(t => !t.est_absent).length} réponse(s)
            </span>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Actualisation auto. 5s</span>
          </div>
          <div style={{ height: '7px', background: 'var(--border)', borderRadius: '999px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: (tokens.filter(t => !t.est_absent).length > 0 ? Math.round(tokens.filter(t => t.a_repondu).length / tokens.filter(t => !t.est_absent).length * 100) : 0) + '%',
              background: '#16a34a', borderRadius: '999px', transition: 'width 0.5s ease',
            }} />
          </div>
        </div>
      )}

      {/* ── Questionnaires : liste tokens + statut ── */}
      {tokens.length > 0 && (
        <div style={{ margin: '0 0.75rem 0.75rem' }}>
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Stagiaire</th>
              <th style={{ textAlign: 'center' }}>
                {`Statut — ${TYPES.find(t => t.val === config.type_actif)?.label}`}
              </th>
              <th style={{ textAlign: 'center' }}>Répondu le</th>
              <th style={{ textAlign: 'center' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map(t => {
              const repondu = t.a_repondu;
              const reponduAt = t.repondu_at;
              const absent = t.est_absent;
              return (
                <tr key={t.token}>
                  <td style={{ fontWeight: 600 }}>{[t.civilite, t.prenoms, t.nom].filter(Boolean).join(' ') || t.nom_complet || ''}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 700,
                      background: absent ? '#f3f4f6' : repondu ? '#fef2f2' : '#f0fdf4',
                      color: absent ? '#6b7280' : repondu ? '#dc2626' : '#16a34a',
                    }}>
                      {absent ? '✗ Absent' : repondu ? '✓ Répondu' : '○ En attente'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    {reponduAt ? new Date(reponduAt).toLocaleString('fr-FR') : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {repondu && !absent && (
                      <button onClick={() => resetToken(t.token)} disabled={resetting === t.token}
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid #f97316', background: '#fff7ed', color: '#f97316', cursor: 'pointer', fontWeight: 600 }}>
                        {resetting === t.token ? '...' : '↩ Réinitialiser'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
      {tokens.length === 0 && (
        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          Aucun QR code généré. Allez dans l'onglet "Documents stagiaires" pour générer les chevalets.
        </div>
      )}
      </>
      )}
      </SectionRepliable>
      )}

      {/* ── Section Présences ── */}
      <div style={{ borderTop: '2px solid var(--border)', marginTop: '1.5rem', paddingTop: '1.2rem' }}>
        <PresencesTab
          sessionId={sessionId}
          presences={presences}
          occurrences={occurrences}
          session={session}
          inscriptions={inscriptions}
        />
      </div>

    </div>
  );
}

function DocumentsTab({ data, sessionId }) {
  const [typeDoc,        setTypeDoc]        = useState('certificat');
  const [loading,        setLoading]        = useState(false);
  const [confirm,        setConfirm]        = useState(null); // { label, urls }
  const [ligneDebut,     setLigneDebut]     = useState(1);   // 1-7 : première ligne d'impression des étiquettes
  const [colonneDebut,   setColonneDebut]   = useState(1);   // 1-3 : première colonne
  const [nbParStagiaire, setNbParStagiaire] = useState(4);   // nombre d'étiquettes par stagiaire
  const [qrTokens,       setQrTokens]       = useState([]);
  const [qrLoading,      setQrLoading]      = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});


  function toggleGroup(id) {
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  }

  const typeLabel = TYPES_DOC.find(t => t.value === typeDoc)?.label || '';
  const modeChevalet = typeDoc === 'chevalet';

  // Charger les tokens QR quand on passe en mode chevalet
  useEffect(() => {
    if (!modeChevalet) return;
    apiFetch('/sessions/' + sessionId + '/qr-tokens')
      .then(d => setQrTokens(d.data || []))
      .catch(() => {});
  }, [modeChevalet, sessionId]);

  async function genererTokens() {
    setQrLoading(true);
    try {
      await apiFetch('/sessions/' + sessionId + '/qr-tokens/generer', { method: 'POST' });
      const d = await apiFetch('/sessions/' + sessionId + '/qr-tokens');
      setQrTokens(d.data || []);
    } catch (e) { alert('Erreur : ' + e.message); }
    finally { setQrLoading(false); }
  }

  async function telechargerChevalet(scope, id, label) {
    setLoading(true);
    try {
      // APP_URL_BASE contient l'IP locale réelle du frontend pour que le QR code
      // soit scannable depuis un smartphone sur le même réseau WiFi
      const baseUrl = APP_URL_BASE;
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
      const token = localStorage.getItem('gf_formateur_token') || localStorage.getItem('gf_token');
      let url = API_BASE + '/sessions/' + sessionId + '/chevalets?baseUrl=' + encodeURIComponent(baseUrl);
      if (scope === 'stagiaire') url += '&stagiaire_id=' + id;
      if (scope === 'groupe')    url += '&sous_session_id=' + id;
      const resp = await fetch(url, { headers: token ? { Authorization: 'Bearer ' + token } : {} });
      if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.error || resp.statusText); }
      const contentType = resp.headers.get('content-type') || '';
      if (!contentType.includes('pdf')) {
        const text = await resp.text();
        throw new Error('Réponse inattendue (' + contentType + ') : ' + text.substring(0, 300));
      }
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'chevalets_' + (label || sessionId).replace(/\s+/g, '_') + '.pdf';
      a.click();
    } catch (e) { alert('Erreur : ' + e.message); }
    finally { setLoading(false); }
  }

  // Retourne un tableau d'entrées { url } pour le type sélectionné
  function urlsFor(scope, id) {
    const etiqQs = new URLSearchParams({ debut: ligneDebut, colonne: colonneDebut, nb: nbParStagiaire }).toString();
    const etiq = scope === 'session'   ? { url: `/api/documents/etiquettes/session/${id}?${etiqQs}` }
               : scope === 'groupe'    ? { url: `/api/documents/etiquettes/sous-session/${id}?${etiqQs}` }
               :                        { url: `/api/documents/etiquettes/stagiaire/${sessionId}/${id}?${etiqQs}` };
    const cert = scope === 'session'   ? { url: `/api/documents/certificat/session/${id}` }
               : scope === 'groupe'    ? { url: `/api/documents/certificat/sous-session/${id}` }
               :                        { url: `/api/documents/certificat/${sessionId}/${id}` };
    const atte = scope === 'session'   ? { url: `/api/documents/attestation/session/${id}` }
               : scope === 'groupe'    ? { url: `/api/documents/attestation/groupe/${id}` }
               :                        { url: `/api/documents/attestation/${sessionId}/${id}` };

    const baseUrlEnc = encodeURIComponent(process.env.REACT_APP_FRONTEND_URL || window.location.origin);
    const chev = scope === 'session'
               ? { url: `/api/sessions/${id}/chevalets?baseUrl=${baseUrlEnc}` }
               : scope === 'groupe'
               ? { url: `/api/sessions/${sessionId}/chevalets?baseUrl=${baseUrlEnc}&sous_session_id=${id}` }
               : { url: `/api/sessions/${sessionId}/chevalets?baseUrl=${baseUrlEnc}&stagiaire_id=${id}` };

    if (typeDoc === 'tout')       return [etiq, cert, atte, chev];
    if (typeDoc === 'etiquettes') return [etiq];
    if (typeDoc === 'certificat') return [cert];
    if (typeDoc === 'chevalet')   return [chev];
    return [atte];
  }

  function demandePrint(label, scope, id) {
    setConfirm({ label, urls: urlsFor(scope, id) });
  }

  async function executerPrint() {
    if (!confirm) return;
    setLoading(true);
    try {
      if (confirm.urls.length <= 1) {
        await imprimerUrl(confirm.urls[0].url, confirm.label);
      } else {
        // Fusionner plusieurs PDFs en un seul fichier compilé
        await ouvrirDocument({ titre: confirm.label, url: '/documents/merge-pdfs', method: 'POST', body: { urls: confirm.urls } });
      }
    } catch (e) { alert(`Erreur : ${e.message}`); }
    finally { setLoading(false); setConfirm(null); }
  }


  const btnPrintStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem',
    width: '100%', padding: '0.28rem 0.4rem', borderRadius: '6px',
    border: '1px solid #bbb', background: '#f5f5f5', color: '#333',
    cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
    whiteSpace: 'nowrap', lineHeight: 1.4, boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '0.5rem 0' }}>

      {/* ── Barre de contrôle globale ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
        padding: '0.9rem 1rem', marginBottom: '1rem',
        background: 'var(--bg-elevated)', borderRadius: '8px',
        border: '1px solid var(--border)',
      }}>
        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          Type de document :
        </span>
        <select
          value={typeDoc}
          onChange={e => setTypeDoc(e.target.value)}
          style={{
            padding: '0.45rem 0.7rem', borderRadius: '6px', fontSize: '0.9rem',
            border: '1px solid #aaa', background: '#fff',
            color: '#111', cursor: 'pointer', flex: '0 0 auto',
          }}>
          {TYPES_DOC.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        {/* ── Sélecteurs étiquettes (ligne, colonne, nb) ── */}
        {(typeDoc === 'etiquettes' || typeDoc === 'tout') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
            padding: '0.35rem 0.75rem', background: '#fef9ec', border: '1px solid #f59e0b',
            borderRadius: '6px', fontSize: '0.85rem', color: '#92400e', fontWeight: 500 }}>
            <span title="Planche d'étiquettes entamée : positionnez le point de départ">🏷️</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              Ligne :
              <select value={ligneDebut} onChange={e => setLigneDebut(Number(e.target.value))}
                style={{ padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.85rem',
                  border: '1px solid #f59e0b', background: '#fff', color: '#111', cursor: 'pointer' }}>
                {[1,2,3,4,5,6,7].map(n => (
                  <option key={n} value={n}>{n}{n === 1 ? ' (début)' : ''}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              Col. :
              <select value={colonneDebut} onChange={e => setColonneDebut(Number(e.target.value))}
                style={{ padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.85rem',
                  border: '1px solid #f59e0b', background: '#fff', color: '#111', cursor: 'pointer' }}>
                {[1,2,3].map(n => (
                  <option key={n} value={n}>{n}{n === 1 ? ' (gauche)' : ''}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              Nb/stagiaire :
              <select value={nbParStagiaire} onChange={e => setNbParStagiaire(Number(e.target.value))}
                style={{ padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.85rem',
                  border: '1px solid #f59e0b', background: '#fff', color: '#111', cursor: 'pointer' }}>
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            {(ligneDebut > 1 || colonneDebut > 1) && (
              <span style={{ fontSize: '0.78rem', color: '#b45309' }}>
                ({(ligneDebut - 1) * 3 + (colonneDebut - 1)} case{(ligneDebut - 1) * 3 + (colonneDebut - 1) > 1 ? 's' : ''} ignorée{(ligneDebut - 1) * 3 + (colonneDebut - 1) > 1 ? 's' : ''})
              </span>
            )}
          </div>
        )}

        {/* ── Rappel impression 100 % (étiquettes uniquement) ── */}
        {(typeDoc === 'etiquettes' || typeDoc === 'tout') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.35rem 0.75rem', background: '#eff6ff', border: '1px solid #93c5fd',
            borderRadius: '6px', fontSize: '0.82rem', color: '#1e40af' }}>
            🖨️ Imprimer à <strong style={{ margin: '0 2px' }}>100 %</strong> (sans ajustement à la page)
          </div>
        )}

        {modeChevalet ? (
          <>
            {qrTokens.length === 0 ? (
              <button className="btn-primary" onClick={genererTokens} disabled={qrLoading}
                style={{ padding: '0.5rem 1.2rem', fontSize: '0.95rem', background: '#f59e0b', borderColor: '#f59e0b' }}>
                {qrLoading ? '⏳ Génération...' : '🔑 Générer les QR codes d\'abord'}
              </button>
            ) : (
              <button className="btn-primary" disabled={loading}
                onClick={() => telechargerChevalet('session', sessionId, data.intitule_formation)}
                style={{ padding: '0.5rem 1.2rem', fontSize: '0.95rem' }}>
                {loading ? '⏳ PDF...' : '📄 Télécharger — session complète'}
              </button>
            )}
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {qrTokens.length} QR code{qrTokens.length > 1 ? 's' : ''} généré{qrTokens.length > 1 ? 's' : ''}
            </span>
          </>
        ) : (
          <button className="btn-primary" disabled={loading}
            onClick={() => demandePrint(`${data.intitule_formation} (${sessionId}) — session complète`, 'session', sessionId)}
            style={{ padding: '0.5rem 1.2rem', fontSize: '0.95rem' }}>
            {loading ? '⏳ Génération...' : '🖨 Imprimer — session complète'}
          </button>
        )}

      </div>

      {/* ── Tableau groupes / stagiaires ── */}
      <table className="data-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: '110px' }}>Imprimer</th>
            <th>Groupe / Stagiaire</th>
          </tr>
        </thead>
        <tbody>

          {/* ── SESSION (ligne récapitulative, bouton redondant avec le bouton principal) ── */}
          <tr style={{ background: 'var(--bg-elevated)' }}>
            <td>
              <button style={{ ...btnPrintStyle, opacity: modeChevalet && qrTokens.length === 0 ? 0.4 : 1 }}
                disabled={modeChevalet && qrTokens.length === 0}
                onClick={() => demandePrint(`${data.intitule_formation} (${sessionId})`, 'session', sessionId)}>
                {modeChevalet ? '📄 PDF' : '🖨 Session'}
              </button>
            </td>
            <td style={{ fontWeight: 700 }}>
              {data.intitule_formation}
              <span style={{ color: 'var(--text-tertiary)', fontWeight: 400, fontSize: '0.8rem', marginLeft: '0.5rem' }}>({sessionId})</span>
            </td>
          </tr>

          {/* ── GROUPES + STAGIAIRES ── */}
          {(data.groupes || []).map(g => {
            const stagiaires = (data.inscriptions || []).filter(i => i.sous_session_id === g.sous_session_id);
            const expanded = !!expandedGroups[g.sous_session_id];
            return (
              <React.Fragment key={g.sous_session_id}>
                <tr style={{ background: 'var(--bg-hover)' }}>
                  <td>
                    <button style={{ ...btnPrintStyle, opacity: modeChevalet && qrTokens.length === 0 ? 0.4 : 1 }}
                      disabled={modeChevalet && qrTokens.length === 0}
                      onClick={() => demandePrint(`${g.groupe} — ${g.nom_client}`, 'groupe', g.sous_session_id)}>
                      {modeChevalet ? '📄 PDF' : '🖨 Groupe'}
                    </button>
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {stagiaires.length > 0 && (
                        <button
                          onClick={() => toggleGroup(g.sous_session_id)}
                          title={expanded ? 'Masquer les stagiaires' : 'Afficher les stagiaires'}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: '0.2rem 0.4rem', color: 'var(--text-secondary)',
                            fontSize: '0.85rem', lineHeight: 1, flexShrink: 0,
                            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform 0.2s ease',
                          }}>
                          ▼
                        </button>
                      )}
                      <span>
                        {g.groupe} — {g.nom_client}
                        <span style={{ color: 'var(--text-tertiary)', fontWeight: 400, fontSize: '0.78rem', marginLeft: '0.4rem' }}>({stagiaires.length} stagiaire{stagiaires.length > 1 ? 's' : ''})</span>
                      </span>
                    </div>
                  </td>
                </tr>
                {expanded && stagiaires.map((insc, i) => (
                  <tr key={i}>
                    <td>
                      <button style={{ ...btnPrintStyle, opacity: modeChevalet && qrTokens.length === 0 ? 0.4 : 1 }}
                        disabled={modeChevalet && qrTokens.length === 0}
                        onClick={() => demandePrint(insc.nom_stagiaire_snapshot, 'stagiaire', insc.stagiaire_id)}>
                        {modeChevalet ? '📄 PDF' : '🖨 Stagiaire'}
                      </button>
                    </td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', paddingLeft: '1.5rem' }}>
                      {insc.nom_stagiaire_snapshot}
                      {insc.nom_client_snapshot && <span style={{ color: 'var(--text-tertiary)' }}> — {insc.nom_client_snapshot}</span>}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}

        </tbody>
      </table>

      {/* ── Chevalets QR ── */}
      <ChevaletsSection sessionId={sessionId} data={data} />

      {/* ── Modal de confirmation ── */}
      {confirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#ffffff', border: '1px solid #d0d0d0',
            borderRadius: '12px', padding: '1.8rem 2rem', maxWidth: '440px', width: '90vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          }}>
            <p style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.6rem', color: '#111' }}>
              Confirmer l'impression
            </p>
            <p style={{ fontSize: '0.9rem', color: '#444', marginBottom: (typeDoc === 'etiquettes' || typeDoc === 'tout') ? '0.8rem' : '1.4rem', lineHeight: 1.5 }}>
              Générer <strong>{typeLabel}</strong> pour :<br />
              <span style={{ color: '#111' }}>{confirm.label}</span>
            </p>
            {(typeDoc === 'etiquettes' || typeDoc === 'tout') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
                padding: '0.6rem 0.8rem', background: '#fef9ec', border: '1px solid #f59e0b',
                borderRadius: '8px', marginBottom: '1.2rem', fontSize: '0.88rem', color: '#92400e', fontWeight: 600 }}>
                <span>🏷️</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  Ligne :
                  <select value={ligneDebut} onChange={e => setLigneDebut(Number(e.target.value))}
                    style={{ padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.88rem',
                      border: '1px solid #f59e0b', background: '#fff', color: '#111', cursor: 'pointer', fontWeight: 'normal' }}>
                    {[1,2,3,4,5,6,7].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  Col. :
                  <select value={colonneDebut} onChange={e => setColonneDebut(Number(e.target.value))}
                    style={{ padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.88rem',
                      border: '1px solid #f59e0b', background: '#fff', color: '#111', cursor: 'pointer', fontWeight: 'normal' }}>
                    {[1,2,3].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  Nb/stagiaire :
                  <select value={nbParStagiaire} onChange={e => setNbParStagiaire(Number(e.target.value))}
                    style={{ padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.88rem',
                      border: '1px solid #f59e0b', background: '#fff', color: '#111', cursor: 'pointer', fontWeight: 'normal' }}>
                    {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setConfirm(null)} disabled={loading}>
                Annuler
              </button>
              <button className="btn-primary" onClick={executerPrint} disabled={loading}>
                {loading ? '⏳ Génération...' : '🖨 Imprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function DevisEtConventions({ sessionId, formationId, hideConventions, groupes = [], inscriptions = [] }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [devis, setDevis] = useState([]);
  const [conventions, setConventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [devisSearch, setDevisSearch] = useState('');
  const [devisSuggestions, setDevisSuggestions] = useState([]);
  const [linkingDevis, setLinkingDevis] = useState(false);
  const [confirmDevis, setConfirmDevis] = useState(null); // devis à confirmer si déjà lié

  const dejaCharge = useRef(false);
  useEffect(() => { dejaCharge.current = false; charger(); }, [sessionId]);

  async function charger() {
    // « Chargement… » seulement au premier affichage : lors d'un rechargement (ex. après « Envoyer par e-mail »), les cartes
    // restent affichées — sinon elles sont démontées et leurs fenêtres ouvertes (envoi de la convention, prix) disparaissent
    if (!dejaCharge.current) setLoading(true);
    try {
      const [rd, rc] = await Promise.all([
        fetch(`${API_BASE}/sessions/${sessionId}/devis`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_BASE}/sessions/${sessionId}/conventions`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setDevis(rd.data || []);
      setConventions(rc.data || []);
    } catch(e) { console.error(e); }
    finally { dejaCharge.current = true; setLoading(false); }
  }

  useEffect(() => {
    if (!devisSearch || devisSearch.length < 2) { setDevisSuggestions([]); return; }
    const params = new URLSearchParams({
      search: devisSearch,
      limit: 50,
      statut: 'Validé',
      ...(formationId ? { code_formation: formationId } : {}),
    });
    fetch(`${API_BASE}/devis?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        let liste = d.data || [];
        if (formationId) {
          const filtered = liste.filter(dv => dv.code_formation === formationId || dv.code_formation === parseInt(formationId));
          liste = filtered.length > 0 ? filtered : liste;
        }
        // Exclure les devis déjà liés à cette session
        const dejàLiés = new Set(devis.map(d => d.devis_id));
        setDevisSuggestions(liste.filter(dv => !dejàLiés.has(dv.devis_id)).slice(0, 10));
      }).catch(() => {});
  }, [devisSearch]);

  async function associerDevis(devisId) {
    setLinkingDevis(true);
    try {
      await fetch(`${API_BASE}/devis/${devisId}/session`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId }),
      });
      setDevisSearch(''); setDevisSuggestions([]);
      await charger();
    } catch(e) { alert(e.message); }
    finally { setLinkingDevis(false); }
  }

  async function dissocierDevis(devisId) {
    if (!window.confirm(`Dissocier le devis ${devisId} de cette session ?`)) return;
    try {
      await fetch(`${API_BASE}/devis/${encodeURIComponent(devisId)}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await charger();
    } catch(e) { alert(e.message); }
  }

  if (loading) return <p style={{ color: 'var(--text-tertiary)', padding: '1rem' }}>Chargement...</p>;

  // Une carte « Convention » par groupe (session × client) : la convention en vigueur + ses versions précédentes
  const normNom = x => String(x || '').toLowerCase().trim();
  const conventionsDe = g => conventions.filter(c =>
    (c.client_id && g.client_id) ? c.client_id === g.client_id : normNom(c.client_name) === normNom(g.nom_client));
  const dejaAffichees = new Set();
  const cartes = (groupes || []).map(g => {
    const siennes = conventionsDe(g);
    siennes.forEach(c => dejaAffichees.add(c.convention_id));
    const courante = siennes.find(c => !c.obsolete) || siennes[0] || null;
    const dvClient = devis.find(d => (d.client_id && g.client_id) ? d.client_id === g.client_id : normNom(d.client_name) === normNom(g.nom_client));
    const nbInscrits = (inscriptions || []).filter(i => i.sous_session_id === g.sous_session_id && (!i.statut || i.statut === 'active')).length;
    return { g, courante, anciennes: siennes.filter(c => c !== courante), devisId: dvClient?.devis_id || null, nbInscrits };
  });
  const sansGroupe = conventions.filter(c => !dejaAffichees.has(c.convention_id));

  function apercuDevis(devisId) {
    ouvrirApercuDevis(devisId, {
      actions: (
        <button onClick={() => { associerDevis(devisId); fermerDocument(); }}
          className="btn-primary" style={{ fontSize: '0.85rem', padding: '0.4rem 0.9rem' }}>
          ✓ Associer ce devis
        </button>
      ),
    });
  }


  return (
    <div>

      {/* ─ Confirmation association devis déjà lié ─ */}
      {confirmDevis && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '2rem', width: 480, maxWidth: '95vw' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>⚠️</div>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Devis déjà associé</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              Le devis <strong>{confirmDevis.devis_id}</strong> — {confirmDevis.client_name} est déjà lié à la session <strong>{confirmDevis.session_id}</strong>.
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Confirmer l'association à cette session également ?
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setConfirmDevis(null)}>Annuler</button>
              <button className="btn-primary" onClick={() => { associerDevis(confirmDevis.devis_id); setConfirmDevis(null); }}>
                ✓ Confirmer l'association
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─ Devis liés ─ */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h4 style={{ color: 'var(--text-primary)', fontSize: '0.95rem', fontWeight: 700 }}>💶 Devis liés ({devis.length})</h4>
          <button className="btn-primary" style={{ fontSize: '0.82rem', padding: '0.35rem 0.8rem' }} onClick={() => navigate(`/devis/nouveau?session=${sessionId}`)}>+ Nouveau devis</button>
        </div>

        {/* Recherche pour associer un devis existant */}
        <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
          <input type="text" placeholder="Associer un devis existant (recherche par N° ou client)…"
            value={devisSearch} onChange={e => setDevisSearch(e.target.value)}
            style={{ width: '100%', padding: '0.45rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', boxSizing: 'border-box' }} />
          {devisSuggestions.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 100, width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '200px', overflowY: 'auto', marginTop: '2px' }}>
              {devisSuggestions.map(d => (
                <div key={d.devis_id}
                  style={{ padding: '0.5rem 0.8rem', borderBottom: '1px solid var(--border)', fontSize: '0.83rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => {
                    if (d.session_id && d.session_id !== sessionId) {
                      setConfirmDevis(d); setDevisSuggestions([]);
                    } else {
                      associerDevis(d.devis_id); setDevisSuggestions([]);
                    }
                  }}>
                    <strong>{d.devis_id}</strong> — {d.client_name} — {d.intitule_formation?.substring(0, 35)}
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', color: 'var(--accent-emerald)' }}>✓ Validé</span>
                    {d.session_id && d.session_id !== sessionId && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', color: '#f59e0b' }}>⚠ lié à {d.session_id}</span>
                    )}
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); apercuDevis(d.devis_id); }}
                    style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    👁 Aperçu
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {devis.length > 0 ? (
          <table className="data-table" style={{ width: '100%' }}>
            <thead><tr><th>N° Devis</th><th>Client</th><th>Formation</th><th>Total HT</th><th>Statut</th><th></th></tr></thead>
            <tbody>
              {devis.map(d => (
                <tr key={d.devis_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/devis/${d.devis_id}`)}>
                  <td><strong style={{ color: 'var(--accent-blue)' }}>{d.devis_id}</strong></td>
                  <td>{d.client_name}</td>
                  <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.intitule_formation}</td>
                  <td>{d.total_ht ? `${parseFloat(d.total_ht).toFixed(2)} €` : '—'}</td>
                  <td><Badge>{d.statut_devis}</Badge></td>
                  <td onClick={e => e.stopPropagation()}>
                    <button onClick={() => dissocierDevis(d.devis_id)}
                      style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border-color)', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer' }}
                      title="Dissocier ce devis">
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: 'var(--text-tertiary)', fontSize: '0.88rem' }}>Aucun devis associé à cette session.</p>
        )}
      </div>

      {/* ─ Conventions : une carte par groupe (session × client) ─ */}
      {hideConventions ? (
        <div style={{ padding: '0.8rem 1rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
          📋 Convention établie hors application pour cette session — étape masquée.
        </div>
      ) : (
      <div>
        <h4 style={{ color: 'var(--text-primary)', fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.75rem' }}>
          📋 Conventions ({conventions.filter(c => !c.obsolete).length})
        </h4>
        {cartes.length === 0 && (
          <p style={{ color: 'var(--text-tertiary)', fontSize: '0.88rem' }}>
            Aucun groupe dans cette session : inscrivez d'abord des stagiaires pour créer une convention.
          </p>
        )}
        {cartes.map(({ g, courante, anciennes, devisId, nbInscrits }) => (
          <CarteConvention key={g.sous_session_id} sessionId={sessionId} groupe={g} convention={courante}
            anciennes={anciennes} devisId={devisId} nbInscrits={nbInscrits} onChange={charger} />
        ))}
        {sansGroupe.length > 0 && (
          <div style={{ marginTop: '0.5rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Autres conventions de la session (sans groupe correspondant)</div>
            {sansGroupe.map(c => (
              <div key={c.convention_id} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', fontSize: '0.83rem', marginBottom: '0.25rem', opacity: c.obsolete ? 0.6 : 1 }}>
                <strong>{c.reference || c.convention_id}</strong>
                <span>{c.client_name || '—'}</span>
                <span style={{ color: c.statut === 'Validée' ? 'var(--accent-emerald)' : 'var(--text-secondary)' }}>
                  {c.statut === 'Validée' ? '✓ Signée' : c.statut === 'Envoyée' ? '📨 Envoyée' : '🔖 Brouillon'}{c.obsolete ? ' — version obsolète' : ''}
                </span>
                {c.devis_id && <span style={{ color: 'var(--accent-blue)', cursor: 'pointer' }} onClick={() => navigate(`/devis/${c.devis_id}`)}>{c.devis_id}</span>}
                <button className="btn-secondary" style={{ padding: '0.1rem 0.6rem', fontSize: '0.78rem' }}
                  onClick={() => ouvrirApercuConvention(c.convention_id)}>👁 Aperçu</button>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// ─── Modal documents session d'un stagiaire ──────────────────────────────────
const TYPES_DOCS_SESSION = ['Justificatif d\'absence', 'Certificat médical', 'Convocation', 'Attestation', 'Accord de prise en charge', 'Autre'];

function DocSessionModal({ sessionId, stagiaireId, stagiaireNom, onClose }) {
  const { token } = useAuth();
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
  const fileRef = useRef();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [form, setForm] = useState({ type_document: TYPES_DOCS_SESSION[0], notes: '' });
  const [err, setErr] = useState('');

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/stagiaires/${stagiaireId}/documents?session_id=${encodeURIComponent(sessionId)}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDocs(await r.json());
    } catch { setDocs([]); } finally { setLoading(false); }
  }, [API_BASE, token, stagiaireId, sessionId]);

  useEffect(() => { charger(); }, [charger]);

  async function supprimer(docId) {
    if (!window.confirm('Supprimer ce document ?')) return;
    await fetch(`${API_BASE}/stagiaires/${stagiaireId}/documents/${docId}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    charger();
  }

  async function upload() {
    if (!pendingFile) { setErr('Sélectionnez un fichier'); return; }
    setUploading(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('fichier', pendingFile);
      fd.append('type_document', form.type_document);
      fd.append('session_id', sessionId);
      if (form.notes) fd.append('notes', form.notes);
      const r = await fetch(`${API_BASE}/stagiaires/${stagiaireId}/documents`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setPendingFile(null);
      setForm({ type_document: TYPES_DOCS_SESSION[0], notes: '' });
      charger();
    } catch(e) { setErr(e.message); }
    finally { setUploading(false); }
  }

  const fmtSize = b => b < 1024*1024 ? `${(b/1024).toFixed(0)} Ko` : `${(b/1024/1024).toFixed(1)} Mo`;
  const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', width: '560px', maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>📎 Documents de session</h3>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginTop: '0.2rem' }}>{stagiaireNom}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>✕</button>
        </div>

        {/* Liste des documents existants */}
        {loading ? <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Chargement…</div> : docs.length === 0 ? (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', marginBottom: '1rem' }}>Aucun document pour cette session.</div>
        ) : (
          <div style={{ marginBottom: '1.2rem' }}>
            {docs.map(doc => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.7rem', borderRadius: '7px', border: '1px solid var(--border)', marginBottom: '0.4rem', background: 'var(--bg-elevated)' }}>
                <span style={{ background: 'rgba(232,119,34,0.12)', color: '#E87722', borderRadius: '4px', padding: '0.1rem 0.4rem', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{doc.type_document}</span>
                <span style={{ flex: 1, fontSize: '0.82rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.nom_original}>{doc.nom_original || doc.nom_fichier}</span>
                {doc.notes && <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{doc.notes}</span>}
                <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtDate(doc.created_at)}</span>
                <button onClick={() => ouvrirDocument({ titre: doc.nom_original || doc.nom_fichier, url: `/stagiaires/${stagiaireId}/documents/${doc.id}/download`, nomFichier: doc.nom_original })} style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--accent-blue)', cursor: 'pointer', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                  ⬇
                </button>
                <button onClick={() => supprimer(doc.id)} style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid rgba(239,68,68,0.35)', background: 'transparent', color: '#f87171', cursor: 'pointer', fontSize: '0.75rem' }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Formulaire d'ajout */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.7rem' }}>Ajouter un document</div>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
            <select value={form.type_document} onChange={e => setForm(f => ({ ...f, type_document: e.target.value }))}
              style={{ flex: '1 1 180px', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem' }}>
              {TYPES_DOCS_SESSION.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Commentaire (optionnel)" style={{ flex: '1 1 160px', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
          </div>
          <div onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) setPendingFile(f); }}
            style={{ border: `2px dashed ${dragOver ? 'var(--accent-blue)' : 'var(--border)'}`, borderRadius: '8px', padding: '1rem', textAlign: 'center', cursor: 'pointer', background: dragOver ? 'rgba(59,130,246,0.05)' : 'transparent', transition: 'all 0.15s', marginBottom: '0.7rem' }}>
            <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) setPendingFile(e.target.files[0]); }} />
            {pendingFile ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                📄 <strong>{pendingFile.name}</strong> <span style={{ color: 'var(--text-tertiary)' }}>({fmtSize(pendingFile.size)})</span>
                <button onClick={e => { e.stopPropagation(); setPendingFile(null); }} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '0.85rem' }}>✕</button>
              </div>
            ) : (
              <div style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)' }}>Glisser-déposer un fichier ou cliquer pour choisir<br/><span style={{ fontSize: '0.75rem' }}>PDF, Word, image — 100 Mo max</span></div>
            )}
          </div>
          {err && <div style={{ color: '#f87171', fontSize: '0.82rem', marginBottom: '0.5rem' }}>{err}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>Fermer</button>
            <button onClick={upload} disabled={uploading || !pendingFile}
              style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: 'none', background: pendingFile ? '#1a3668' : 'var(--border)', color: pendingFile ? '#fff' : 'var(--text-tertiary)', cursor: pendingFile ? 'pointer' : 'default', fontSize: '0.85rem', fontWeight: 600 }}>
              {uploading ? 'Envoi…' : '⬆ Déposer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modal Transfert de session ──────────────────────────────────────────────
function ModalTransfert({ sessionId, stagiaireId, stagiaireNom, onClose, reload }) {
  const { token } = useAuth();
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/sessions?limit=500`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const list = (Array.isArray(d) ? d : d?.data || [])
          .filter(s => String(s.session_id) !== String(sessionId));
        setSessions(list);
      })
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, []);

  const sessionsFiltered = sessions.filter(s => {
    const q = search.toLowerCase();
    return !q || (s.intitule_formation || '').toLowerCase().includes(q) || String(s.session_id).includes(q);
  });

  async function confirmer() {
    if (!selectedSession) return;
    setTransferring(true);
    try {
      const r = await fetch(`${API_BASE}/sessions/${sessionId}/inscriptions/${stagiaireId}/transfert`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_session_id: selectedSession }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error || 'Erreur serveur');
      setResult({ ok: true, email_statut: d.email_statut, new_session_id: d.new_session_id, groupe: d.groupe, nom_client: d.nom_client, groupe_cree: d.groupe_cree });
      reload();
    } catch (e) {
      setResult({ ok: false, error: e.message });
    } finally {
      setTransferring(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={onClose}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '480px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.3rem' }}>↔ Transférer un stagiaire</div>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Stagiaire : <strong>{stagiaireNom}</strong>
        </div>

        {result ? (
          <>
            {result.ok ? (
              <div style={{ padding: '0.8rem', borderRadius: '8px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: 'var(--accent-emerald)', marginBottom: '1rem', fontSize: '0.85rem' }}>
                ✓ Stagiaire transféré vers la session {result.new_session_id}
                {result.groupe ? ` — groupe ${result.groupe}${result.nom_client ? ` (${result.nom_client})` : ''}${result.groupe_cree ? ', créé pour l\'occasion' : ''}` : ' — sans groupe (client non identifié)'}.
                {result.email_statut === 'envoyé' && ' Email envoyé au client.'}
                {result.email_statut === 'echec' && " L'email n'a pas pu être envoyé."}
                {result.email_statut === 'aucun_destinataire' && ' Aucun destinataire email configuré.'}
              </div>
            ) : (
              <div style={{ padding: '0.8rem', borderRadius: '8px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', marginBottom: '1rem', fontSize: '0.85rem' }}>
                ❌ {result.error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: 'none', background: '#1a3668', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>Fermer</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: '0.6rem' }}>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher une session (formation, n°)…"
                style={{ width: '100%', padding: '0.45rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', boxSizing: 'border-box' }} />
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Chargement…</div>
            ) : (
              <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '1rem' }}>
                {sessionsFiltered.length === 0 ? (
                  <div style={{ padding: '0.8rem', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-tertiary)' }}>Aucune session trouvée</div>
                ) : sessionsFiltered.map(s => {
                  const dateStr = s.date_debut ? new Date(s.date_debut).toLocaleDateString('fr-FR') : '—';
                  const isSelected = String(s.session_id) === String(selectedSession);
                  return (
                    <div key={s.session_id} onClick={() => setSelectedSession(s.session_id)}
                      style={{ padding: '0.55rem 0.8rem', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: isSelected ? 'rgba(26,54,104,0.12)' : 'transparent', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: isSelected ? 600 : 400, color: 'var(--text-primary)' }}>{s.intitule_formation || '(sans intitulé)'}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Session {s.session_id} — {dateStr}</div>
                      </div>
                      {isSelected && <span style={{ color: '#0891b2', fontWeight: 700 }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '0.45rem 0.9rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>Annuler</button>
              <button onClick={confirmer} disabled={!selectedSession || transferring}
                style={{ padding: '0.45rem 0.9rem', borderRadius: '7px', border: 'none', background: selectedSession && !transferring ? '#0891b2' : 'var(--border)', color: selectedSession && !transferring ? '#fff' : 'var(--text-tertiary)', cursor: selectedSession && !transferring ? 'pointer' : 'default', fontSize: '0.85rem', fontWeight: 600 }}>
                {transferring ? 'Transfert…' : '↔ Transférer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Onglet Inscrits (groupes + stagiaires fusionnés) ────────────────────────
function InscritsCombinedTab({ sessionId, inscriptions, groupes, reload, hasRole, onAddGroupe, session }) {
  const [selectedGroupe, setSelectedGroupe] = useState('');
  const [selectedStagiaire, setSelectedStagiaire] = useState('');
  const [stagiairesDisponibles, setStagiairesDisponibles] = useState([]);
  const [loadingStag, setLoadingStag] = useState(false);
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [warnEmail, setWarnEmail] = useState('');
  const [docModal, setDocModal] = useState(null); // null | { stagiaireId, nom }
  const [transfertModal, setTransfertModal] = useState(null); // null | { stagiaireId, nom }

  // Nouvelle Fiche sans devis
  const [showFicheModal, setShowFicheModal] = useState(false);
  const [ficheClients, setFicheClients] = useState([]);
  const [ficheClientId, setFicheClientId] = useState('');
  const [ficheEmail, setFicheEmail] = useState('');
  const [ficheNotes, setFicheNotes] = useState('');
  const [ficheEmailBody, setFicheEmailBody] = useState('');
  const [ficheSending, setFicheSending] = useState(false);
  const [ficheMsg, setFicheMsg] = useState('');
  const [ficheLien, setFicheLien] = useState(null);
  const [fichesRefresh, setFichesRefresh] = useState(0); // recharge la liste des fiches directes de la session

  const groupeSelectionne = groupes.find(g => g.sous_session_id === selectedGroupe);
  const clientIdFiltre = groupeSelectionne?.client_id || null;
  const clientNomFiltre = groupeSelectionne?.nom_client || null;

  useEffect(() => {
    setStagiairesDisponibles([]); setSelectedStagiaire('');
    if (!selectedGroupe) return;
    setLoadingStag(true);
    const parts = [];
    if (clientIdFiltre) parts.push(`client_id=${encodeURIComponent(clientIdFiltre)}`);
    if (clientNomFiltre) parts.push(`nom_client_groupe=${encodeURIComponent(clientNomFiltre)}`);
    apiFetch(`/stagiaires?limit=500${parts.length ? '&' + parts.join('&') : ''}`)
      .then(d => {
        const liste = d?.data || [];
        const dejàInscrits = new Set(inscriptions.map(i => i.stagiaire_id));
        setStagiairesDisponibles(liste.filter(s => !dejàInscrits.has(s.stagiaire_id)));
      })
      .catch(() => setStagiairesDisponibles([]))
      .finally(() => setLoadingStag(false));
  }, [selectedGroupe, clientIdFiltre]);

  // Charger les clients à l'ouverture du modal fiche
  useEffect(() => {
    if (!showFicheModal) return;
    apiFetch('/clients?limit=500').then(d => setFicheClients(d.data || [])).catch(() => {});
  }, [showFicheModal]);

  // Auto-remplir email quand le client change
  useEffect(() => {
    if (ficheLien) return; // lien déjà créé / à renvoyer : on garde l'adresse déjà choisie
    const client = ficheClients.find(c => c.client_id === ficheClientId);
    setFicheEmail(client?.contact_email || '');
  }, [ficheClientId, ficheClients]); // eslint-disable-line react-hooks/exhaustive-deps

  // Générer le corps du mail auto
  useEffect(() => {
    if (ficheLien) return;
    const dd = session?.date_debut ? new Date(session.date_debut).toLocaleDateString('fr-FR') : '';
    const df = session?.date_fin   ? new Date(session.date_fin).toLocaleDateString('fr-FR')   : '';
    const dateInfo = dd ? ` — session du ${dd}${df && df !== dd ? ' au ' + df : ''}${session?.lieu ? ', ' + session.lieu : ''}` : '';
    const formationTxt = session?.intitule_formation || '';
    setFicheEmailBody(
`Bonjour,

Veuillez trouver ci-dessous le lien pour compléter votre fiche d'inscription${formationTxt ? ` pour la formation "${formationTxt}"` : ''}${dateInfo}.

Merci de renseigner toutes les informations demandées et de valider votre inscription via le lien ci-dessous.

Cordialement`
    );
  }, [session, ficheLien]);

  function ouvrirFicheModal() {
    setFicheClientId(''); setFicheEmail(''); setFicheNotes('');
    setFicheMsg(''); setFicheLien(null);
    setShowFicheModal(true);
  }

  // Renvoyer le lien d'une fiche déjà créée (bouton du cadre « Fiches d'inscription directes »)
  function renvoyerFiche(il) {
    setFicheClientId(il.client_id || ''); setFicheEmail(il.email_client || ''); setFicheNotes('');
    setFicheMsg(''); setFicheLien(il);
    setShowFicheModal(true);
  }

  async function genererFicheLien() {
    if (!ficheClientId) return null;
    setFicheSending(true); setFicheMsg('');
    try {
      const d = await apiFetch('/inscription-liens', {
        method: 'POST',
        body: JSON.stringify({
          client_id: ficheClientId,
          intitule_formation: session?.intitule_formation || '',
          session_id: sessionId,
          notes_client: ficheNotes,
          email_client: ficheEmail,
        }),
      });
      setFicheLien(d);
      setFichesRefresh(k => k + 1);
      setFicheMsg('Lien créé avec succès.');
      return d;
    } catch (e) { setFicheMsg('Erreur : ' + e.message); return null; }
    finally { setFicheSending(false); }
  }

  async function envoyerFicheEmail(lienObj) {
    if (!ficheEmail) { setFicheMsg('Veuillez saisir un email.'); return; }
    setFicheSending(true); setFicheMsg('');
    try {
      await apiFetch(`/inscription-liens/${lienObj.id}/envoyer-email`, {
        method: 'POST',
        body: JSON.stringify({ email: ficheEmail, lien: lienObj.lien, message_personnalise: ficheEmailBody }),
      });
      setFicheMsg('Email envoyé avec succès.');
      setFichesRefresh(k => k + 1);
    } catch (e) { setFicheMsg('Erreur : ' + e.message); }
    finally { setFicheSending(false); }
  }

  async function genererEtEnvoyerFiche() {
    const lien = await genererFicheLien();
    if (lien) await envoyerFicheEmail(lien);
  }

  async function ajouter(confirmer = false) {
    if (!selectedStagiaire) return;
    setAdding(true); setMsg('');
    const stag = stagiairesDisponibles.find(s => s.stagiaire_id === selectedStagiaire);
    try {
      await apiFetch(`/sessions/${sessionId}/inscriptions`, {
        method: 'POST',
        body: JSON.stringify({ stagiaire_id: selectedStagiaire, sous_session_id: selectedGroupe || null, ...(confirmer ? { confirmer: true } : {}) }),
      });
      setSelectedStagiaire('');
      setMsg(`✓ ${stag?.nom_complet || selectedStagiaire} ajouté(e)`);
      setTimeout(() => setMsg(''), 3000);
      reload();
    } catch (e) {
      // Déjà inscrit ailleurs (même formation à venir ou dates qui se chevauchent) : alerte avec confirmation
      if (e.status === 409 && e.data?.code === 'deja_inscrit_ailleurs') {
        const lignes = (e.data.ailleurs || []).map(a =>
          `• ${a.session_id} — ${a.intitule_formation || ''} (${formatDate(a.date_debut)}${a.date_fin && a.date_fin !== a.date_debut ? ` au ${formatDate(a.date_fin)}` : ''}) — ${a.motif === 'meme_formation' ? 'même formation à venir' : 'dates qui se chevauchent'}`
        ).join('\n');
        setAdding(false);
        if (window.confirm(`${stag?.nom_complet || 'Ce stagiaire'} est déjà inscrit(e) ailleurs :\n\n${lignes}\n\nL'inscrire quand même à cette session ?`)) {
          await ajouter(true);
        } else {
          setMsg('');
          setTimeout(() => setMsg(''), 3000);
        }
        return;
      }
      setMsg('❌ ' + e.message);
    }
    finally { setAdding(false); }
  }

  async function retirer(stagiaireId, nom) {
    if (!window.confirm(`Désinscrire "${nom}" de cette session ?`)) return;
    try {
      const result = await apiFetch(`/sessions/${sessionId}/inscriptions/${stagiaireId}`, { method: 'DELETE' });
      reload();
      if (result?.email_statut === 'echec') {
        setWarnEmail(`L'email de confirmation de désinscription n'a pas pu être envoyé${result.email_erreur ? ' : ' + result.email_erreur : ''}.`);
      }
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  async function supprimerGroupe(sous_session_id) {
    if (!window.confirm(`Supprimer le groupe ${sous_session_id} ?`)) return;
    try {
      await apiFetch(`/sessions/${sessionId}/groupes/${sous_session_id}`, { method: 'DELETE' });
      reload();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  function getSourceColor(source) {
    if (source === 'manuel') return '#ef4444';
    if (source === 'devis')  return '#f97316';
    return null;
  }

  function toggleGroup(id) {
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  }

  const selectStyle = { width: '100%', padding: '0.45rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.85rem', boxSizing: 'border-box' };
  const retirerBtnStyle = { padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid rgba(239,68,68,0.35)', background: 'transparent', color: '#f87171', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap' };
  const transfererBtnStyle = { padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid rgba(8,145,178,0.4)', background: 'transparent', color: '#0891b2', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap' };

  const inscSansGroupe = inscriptions.filter(i => !i.sous_session_id);

  return (
    <div style={{ padding: '0.5rem 0' }}>
      {/* Légende */}
      <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'center', marginBottom: '0.75rem', padding: '0.5rem 0.8rem', background: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Légende :</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444', flexShrink: 0, display: 'inline-block' }} />
          <span style={{ color: 'var(--text-secondary)' }}>Ajout manuel (projet)</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#f97316', flexShrink: 0, display: 'inline-block' }} />
          <span style={{ color: 'var(--text-secondary)' }}>Fiche validée — convention en attente</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: 'var(--accent-emerald)', flexShrink: 0, display: 'inline-block' }} />
          <span style={{ color: 'var(--text-secondary)' }}>Convention signée (confirmé)</span>
        </span>
      </div>

      {/* Barre d'actions */}
      {hasRole('utilisateur') && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={() => setShowAddForm(p => !p)} style={{ fontSize: '0.85rem' }}>
            {showAddForm ? '▲ Fermer' : '+ Ajouter un stagiaire'}
          </button>
          <button className="btn-primary" onClick={onAddGroupe}>
            + Nouveau groupe / Client
          </button>
          <button onClick={ouvrirFicheModal}
            style={{ padding: '0.45rem 1rem', borderRadius: '8px', border: 'none', background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
            Fiche d'inscription
          </button>
        </div>
      )}

      {/* Formulaire d'ajout (collapsible) */}
      {showAddForm && hasRole('utilisateur') && (
        <div style={{ marginBottom: '1.2rem', padding: '1rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>+ Ajouter un stagiaire</div>
          <div style={{ display: 'grid', gridTemplateColumns: groupes.length > 0 ? '1fr 1fr auto' : '1fr auto', gap: '0.6rem', alignItems: 'center' }}>
            {groupes.length > 0 && (
              <select value={selectedGroupe} onChange={e => setSelectedGroupe(e.target.value)} style={selectStyle}>
                <option value="">— Sélectionner un groupe —</option>
                {groupes.map(g => (
                  <option key={g.sous_session_id} value={g.sous_session_id}>
                    {g.groupe} — {g.nom_client || g.sous_session_id}
                  </option>
                ))}
              </select>
            )}
            <select value={selectedStagiaire} onChange={e => setSelectedStagiaire(e.target.value)}
              disabled={groupes.length > 0 && !selectedGroupe}
              style={{ ...selectStyle, opacity: groupes.length > 0 && !selectedGroupe ? 0.5 : 1 }}>
              <option value="">
                {loadingStag ? 'Chargement…' : groupes.length > 0 && !selectedGroupe ? '← Choisir un groupe d\'abord' : '— Sélectionner un stagiaire —'}
              </option>
              {stagiairesDisponibles.map(s => (
                <option key={s.stagiaire_id} value={s.stagiaire_id}>
                  {s.nom_complet || [s.prenoms, s.nom].filter(Boolean).join(' ')} ({s.stagiaire_id})
                </option>
              ))}
            </select>
            <button className="btn-primary" onClick={() => ajouter()} disabled={!selectedStagiaire || adding}
              style={{ whiteSpace: 'nowrap', padding: '0.45rem 1rem' }}>
              {adding ? '…' : '+ Ajouter'}
            </button>
          </div>
          {msg && <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: couleurMessage(niveauMessage(msg)), fontWeight: 600 }}>{msg}</div>}
        </div>
      )}

      {/* Avertissement échec email désinscription */}
      {warnEmail && (
        <div style={{ marginBottom: '0.75rem', padding: '0.6rem 1rem', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem', color: '#d97706' }}>
          <span>⚠️</span>
          <span style={{ flex: 1 }}>{warnEmail}</span>
          <button onClick={() => setWarnEmail('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d97706', fontSize: '1rem', lineHeight: 1, padding: '0 0.2rem' }}>✕</button>
        </div>
      )}

      {/* Tableau groupes + stagiaires */}
      {groupes.length === 0 && inscriptions.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '8px', fontSize: '0.9rem' }}>
          Aucun groupe ni stagiaire inscrit pour le moment.
        </div>
      ) : (
        <table className="data-table" style={{ width: '100%' }}>
          <tbody>
            {/* ── Groupes ── */}
            {groupes.map(g => {
              const stagsDuGroupe = inscriptions.filter(i => i.sous_session_id === g.sous_session_id);
              const expanded = !!expandedGroups[g.sous_session_id];
              const srcColor = getSourceColor(g.source);
              const dotColor = stagsDuGroupe.length === 0 ? null : (srcColor || 'var(--accent-emerald)');
              return (
                <React.Fragment key={g.sous_session_id}>
                  <tr style={{ background: 'var(--bg-hover)' }}>
                    <td style={{ fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {stagsDuGroupe.length > 0 && (
                            <button onClick={() => toggleGroup(g.sous_session_id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem 0.4rem', color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1, flexShrink: 0, transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease' }}>
                              ▼
                            </button>
                          )}
                          <span style={{
                            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                            background: dotColor || 'transparent',
                            border: dotColor ? 'none' : '1.5px solid rgba(255,255,255,0.45)',
                            display: 'inline-block',
                          }} />
                          <span style={srcColor ? { color: srcColor } : {}}>{g.groupe}</span>
                          {g.nom_client && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> — {g.nom_client}</span>}
                          <span style={{ color: 'var(--text-tertiary)', fontWeight: 400, fontSize: '0.78rem' }}>
                            ({stagsDuGroupe.length} stagiaire{stagsDuGroupe.length !== 1 ? 's' : ''})
                          </span>
                        </span>
                        {hasRole('utilisateur') && stagsDuGroupe.length === 0 && (
                          <button onClick={e => { e.stopPropagation(); supprimerGroupe(g.sous_session_id); }}
                            style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#f87171', fontSize: '0.78rem', cursor: 'pointer' }}>
                            🗑
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded && stagsDuGroupe.map((insc, i) => {
                    const color = getSourceColor(insc.source);
                    return (
                      <tr key={insc.stagiaire_id + (insc.sous_session_id || '') + i}>
                        <td style={{ paddingLeft: '2rem', fontSize: '0.85rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <Link to={`/stagiaires/${insc.stagiaire_id}`} style={{ color: color || 'var(--text-primary)', fontWeight: 700, textDecoration: 'none' }}
                                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                                {insc.nom_stagiaire_snapshot}
                              </Link>
                              <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginLeft: '0.4rem' }}>{insc.stagiaire_id}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                              <button onClick={() => setDocModal({ stagiaireId: insc.stagiaire_id, nom: insc.nom_stagiaire_snapshot })}
                                style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}
                                title="Documents de session">📎</button>
                              {hasRole('utilisateur') && (
                                <>
                                  <button onClick={() => setTransfertModal({ stagiaireId: insc.stagiaire_id, nom: insc.nom_stagiaire_snapshot })} style={transfererBtnStyle} title="Transférer dans une autre session">↔ Transférer</button>
                                  <button onClick={() => retirer(insc.stagiaire_id, insc.nom_stagiaire_snapshot)} style={retirerBtnStyle}>✕ Retirer</button>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* ── Sans groupe ── */}
            {inscSansGroupe.length > 0 && (
              <React.Fragment>
                <tr style={{ background: 'var(--bg-hover)' }}>
                  <td style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
                    Sans groupe
                    <span style={{ color: 'var(--text-tertiary)', fontWeight: 400, fontSize: '0.78rem', marginLeft: '0.4rem' }}>
                      ({inscSansGroupe.length} stagiaire{inscSansGroupe.length !== 1 ? 's' : ''})
                    </span>
                  </td>
                </tr>
                {inscSansGroupe.map((insc, i) => {
                  const color = getSourceColor(insc.source);
                  return (
                    <tr key={insc.stagiaire_id + i}>
                      <td style={{ paddingLeft: '2rem', fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <Link to={`/stagiaires/${insc.stagiaire_id}`} style={{ color: color || 'var(--text-primary)', fontWeight: 700, textDecoration: 'none' }}
                              onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                              onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                              {insc.nom_stagiaire_snapshot}
                            </Link>
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginLeft: '0.4rem' }}>{insc.stagiaire_id}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                            <button onClick={() => setDocModal({ stagiaireId: insc.stagiaire_id, nom: insc.nom_stagiaire_snapshot })}
                              style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}
                              title="Documents de session">📎</button>
                            {hasRole('utilisateur') && (
                              <>
                                <button onClick={() => setTransfertModal({ stagiaireId: insc.stagiaire_id, nom: insc.nom_stagiaire_snapshot })} style={transfererBtnStyle} title="Transférer dans une autre session">↔ Transférer</button>
                                <button onClick={() => retirer(insc.stagiaire_id, insc.nom_stagiaire_snapshot)} style={retirerBtnStyle}>✕ Retirer</button>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            )}
          </tbody>
        </table>
      )}

      {inscriptions.length > 0 && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
          {inscriptions.length} stagiaire{inscriptions.length > 1 ? 's' : ''} inscrit{inscriptions.length > 1 ? 's' : ''}
        </div>
      )}

      {/* Fiches d'inscription directes (sans devis) de cette session : suivi et validation ici, sous les inscrits */}
      {hasRole('utilisateur') && (
        <FichesDirectesPanel
          sessionId={sessionId}
          titre="Fiches d'inscription"
          sousTitre="envoyées pour cette session (sans devis)"
          refreshKey={fichesRefresh}
          onRenvoyer={renvoyerFiche}
          onValide={reload}
          masquerSiVide
        />
      )}

      {docModal && (
        <DocSessionModal
          sessionId={sessionId}
          stagiaireId={docModal.stagiaireId}
          stagiaireNom={docModal.nom}
          onClose={() => setDocModal(null)}
        />
      )}
      {transfertModal && (
        <ModalTransfert
          sessionId={sessionId}
          stagiaireId={transfertModal.stagiaireId}
          stagiaireNom={transfertModal.nom}
          onClose={() => setTransfertModal(null)}
          reload={reload}
        />
      )}

      {/* ── Modal Nouvelle Fiche sans devis ── */}
      {showFicheModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={e => { if (e.target === e.currentTarget) { if (ficheLien) reload(); setShowFicheModal(false); } }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '14px', padding: '1.8rem', width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#7c3aed' }}>
              {ficheLien ? 'Renvoyer la fiche d\'inscription' : 'Nouvelle fiche d\'inscription'}
            </h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 1.4rem' }}>
              Session : <strong>{sessionId}</strong>{session?.intitule_formation ? ` — ${session.intitule_formation}` : ''}
            </p>

            {/* ─ Client ─ */}
            {!ficheLien && (
              <div style={{ marginBottom: '0.8rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.3rem' }}>
                  Client <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select value={ficheClientId} onChange={e => setFicheClientId(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box' }}>
                  <option value="">— Choisir un client —</option>
                  {ficheClients.map(c => (
                    <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>
                  ))}
                </select>
              </div>
            )}

            {/* ─ Email ─ */}
            <div style={{ marginBottom: '0.8rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.3rem' }}>
                Email du destinataire <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input value={ficheEmail} onChange={e => setFicheEmail(e.target.value)}
                placeholder="email@client.fr"
                style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box' }} />
            </div>

            {/* ─ Notes ─ */}
            {!ficheLien && (
              <div style={{ marginBottom: '0.8rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.3rem' }}>
                  Notes <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optionnel)</span>
                </label>
                <input value={ficheNotes} onChange={e => setFicheNotes(e.target.value)}
                  placeholder="Informations complémentaires…"
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box' }} />
              </div>
            )}

            {/* ─ Lien créé ─ */}
            {ficheLien && (() => {
              const lienFrontend = `${window.location.origin}/fiche-inscription-directe/${ficheLien.token}`;
              return (
                <div style={{ marginBottom: '0.8rem', padding: '0.6rem 0.9rem', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: '0.8rem', wordBreak: 'break-all', color: '#6b7280' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.2rem', color: 'var(--text-primary)' }}>{ficheLien.intitule_formation}</div>
                  {lienFrontend}
                </div>
              );
            })()}

            {/* ─ Message email ─ */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.3rem' }}>
                Message envoyé au client <span style={{ fontWeight: 400, color: '#94a3b8' }}>(modifiable)</span>
              </label>
              <textarea value={ficheEmailBody} onChange={e => setFicheEmailBody(e.target.value)}
                rows={6}
                style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
              <p style={{ fontSize: '0.74rem', color: '#94a3b8', margin: '0.2rem 0 0' }}>
                Le bouton "Compléter ma fiche d'inscription" et la signature sont ajoutés automatiquement.
              </p>
            </div>

            {ficheMsg && (
              <div style={{ marginBottom: '0.8rem', padding: '0.5rem 0.8rem', borderRadius: '6px',
                ...styleMessage(niveauMessage(ficheMsg)), fontSize: '0.85rem' }}>
                {ficheMsg}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={() => { if (ficheLien) reload(); setShowFicheModal(false); }}
                style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.88rem' }}>
                {ficheLien ? 'Fermer' : 'Annuler'}
              </button>
              {ficheLien ? (
                <>
                  <a href={`${window.location.origin}/fiche-inscription-directe/${ficheLien.token}`} target="_blank" rel="noreferrer"
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none', display: 'inline-block' }}>
                    ✏️ Compléter la fiche
                  </a>
                  <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/fiche-inscription-directe/${ficheLien.token}`); setFicheMsg('Lien copié !'); }}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #8b5cf6', background: 'transparent', color: '#8b5cf6', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 }}>
                    📋 Copier le lien
                  </button>
                  <button onClick={() => envoyerFicheEmail(ficheLien)} disabled={ficheSending || !ficheEmail}
                    style={{ padding: '0.5rem 1.2rem', borderRadius: '8px', border: '1px solid #8b5cf6', background: 'transparent', color: '#8b5cf6', cursor: (!ficheSending && ficheEmail) ? 'pointer' : 'default', fontWeight: 600, fontSize: '0.88rem', opacity: (!ficheSending && ficheEmail) ? 1 : 0.6 }}>
                    {ficheSending ? 'Envoi…' : '✉ Envoyer par email'}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={genererFicheLien} disabled={ficheSending || !ficheClientId}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #8b5cf6', background: 'transparent', color: '#8b5cf6', cursor: (ficheSending || !ficheClientId) ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.88rem', opacity: (ficheSending || !ficheClientId) ? 0.5 : 1 }}>
                    {ficheSending ? '…' : 'Créer le lien seul'}
                  </button>
                  <button onClick={genererEtEnvoyerFiche} disabled={ficheSending || !ficheEmail || !ficheClientId}
                    style={{ padding: '0.5rem 1.2rem', borderRadius: '8px', border: 'none', background: '#8b5cf6', color: '#fff', cursor: (ficheSending || !ficheEmail || !ficheClientId) ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.88rem', opacity: (ficheSending || !ficheEmail || !ficheClientId) ? 0.5 : 1 }}>
                    {ficheSending ? 'En cours…' : '✉ Créer et envoyer'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Financement ───────────────────────────────────────────────────────
function FinancementTab({ sessionId, session, hasRole }) {
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
  const { token } = useAuth();
  const [financements, setFinancements] = useState([]);
  const [clients, setClients] = useState([]);
  const [organismes, setOrganismes] = useState([]);
  const [typesDB, setTypesDB] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { client_id: '', organisme_id: '', groupe_id: '', type_financeur: '', montant_ht: '', pourcentage: '', notes: '' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  function load() {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/sessions/${sessionId}/financements`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
      fetch(`${API_BASE}/clients?limit=500`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
      fetch(`${API_BASE}/types-financeurs`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
      fetch(`${API_BASE}/organismes-financeurs`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
    ]).then(([f, c, t, o]) => {
      setFinancements(Array.isArray(f) ? f : []);
      setClients(c?.data || []);
      setTypesDB(Array.isArray(t) ? t : []);
      setOrganismes(Array.isArray(o) ? o : []);
    }).finally(() => setLoading(false));
  }
  useEffect(load, [sessionId]);

  const activeTypes = typesDB.length > 0 ? typesDB : [
    { type_id: 1, label: 'Client' }, { type_id: 2, label: 'OPCO' },
    { type_id: 3, label: 'France Travail' }, { type_id: 4, label: 'Region' },
    { type_id: 5, label: 'CPF' }, { type_id: 6, label: 'Autre' },
  ];

  const clientsFiltres = React.useMemo(() => {
    if (!form.type_financeur || form.type_financeur === 'Client') return clients;
    return clients.filter(c => c.type_client &&
      c.type_client.toLowerCase().includes(form.type_financeur.toLowerCase()));
  }, [clients, form.type_financeur]);

  const totalPct = financements.reduce((s, f) => s + (parseFloat(f.pourcentage) || 0), 0);
  const totalMt  = financements.reduce((s, f) => s + (parseFloat(f.montant_ht)  || 0), 0);
  const groupes  = session?.groupes || [];

  const isClient = !form.type_financeur || form.type_financeur === 'Client';

  const organismesFiltres = React.useMemo(() => {
    if (isClient) return [];
    return organismes.filter(o =>
      !form.type_financeur || o.type === form.type_financeur || !o.type
    );
  }, [organismes, form.type_financeur, isClient]);

  function openEdit(f) {
    setEditing(f.financement_id);
    setForm({
      client_id:      f.client_id      || '',
      organisme_id:   f.organisme_id   != null ? String(f.organisme_id) : '',
      groupe_id:      f.groupe_id      != null ? String(f.groupe_id) : '',
      type_financeur: f.type_financeur || '',
      montant_ht:     f.montant_ht     || '',
      pourcentage:    f.pourcentage    || '',
      notes:          f.notes          || '',
    });
    setShowForm(true);
  }

  async function handleSave() {
    const financeurOk = isClient ? !!form.client_id : !!form.organisme_id;
    if (!financeurOk) return setMsg(isClient ? 'Veuillez sélectionner un client' : 'Veuillez sélectionner un organisme');
    setSaving(true); setMsg('');
    try {
      const url = editing
        ? `${API_BASE}/sessions/${sessionId}/financements/${editing}`
        : `${API_BASE}/sessions/${sessionId}/financements`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          client_id:    isClient ? (form.client_id || null) : null,
          organisme_id: isClient ? null : (form.organisme_id ? parseInt(form.organisme_id) : null),
          groupe_id:    form.groupe_id   ? parseInt(form.groupe_id)   : null,
          montant_ht:   form.montant_ht  ? parseFloat(form.montant_ht)  : null,
          pourcentage:  form.pourcentage ? parseFloat(form.pourcentage) : null,
        }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      setMsg('Enregistre'); setShowForm(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) { setMsg('Erreur : ' + e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer ce financement ?')) return;
    await fetch(`${API_BASE}/sessions/${sessionId}/financements/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  }

  const selectStyle = { width: '100%', padding: '0.45rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.85rem' };
  const inputStyle  = { ...selectStyle };
  const labelStyle  = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' };

  if (loading) return <Loading />;

  return (
    <div style={{ padding: '0.5rem 0' }}>

      {financements.length > 0 && (
        <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Repartition totale</span>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: Math.abs(totalPct - 100) < 0.1 ? 'var(--accent-emerald)' : '#f97316' }}>
              {totalPct.toFixed(1)}%{Math.abs(totalPct - 100) < 0.1 ? ' OK' : ' (inegal 100%)'}
            </span>
          </div>
          <div style={{ height: 8, background: 'var(--bg-card)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(totalPct, 100)}%`, background: Math.abs(totalPct - 100) < 0.1 ? 'var(--accent-emerald)' : '#f97316', transition: 'width 0.3s' }} />
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.3rem' }}>
            Montant total financeurs : {totalMt.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR
          </div>
        </div>
      )}

      {hasRole('utilisateur') && !showForm && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
          <button className="btn-primary" onClick={() => { setForm(emptyForm); setEditing(null); setShowForm(true); }}>+ Ajouter un financeur</button>
        </div>
      )}

      {showForm && (
        <div style={{ marginBottom: '1.2rem', padding: '1rem 1.2rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>
            {editing ? 'Modifier le financeur' : '+ Ajouter un financeur'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>

            {groupes.length > 0 && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Groupe <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(laisser vide si ce financeur couvre toute la session)</span></label>
                <select value={form.groupe_id} onChange={e => setForm(f => ({ ...f, groupe_id: e.target.value }))} style={selectStyle}>
                  <option value="">Session entiere</option>
                  {groupes.map(g => (
                    <option key={g.sous_session_id} value={g.id || ''}>
                      Groupe {g.groupe}{g.nom_client ? ` - ${g.nom_client}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label style={labelStyle}>Type de financeur</label>
              <select value={form.type_financeur} onChange={e => setForm(f => ({ ...f, type_financeur: e.target.value, client_id: '' }))} style={selectStyle}>
                <option value="">Selectionner</option>
                {activeTypes.map(t => <option key={t.type_id} value={t.label}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Financeur *</label>
              {isClient ? (
                <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} style={selectStyle}>
                  <option value="">Sélectionner un client</option>
                  {clientsFiltres.map(c => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.nom_client}{c.type_client ? ` (${c.type_client})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <select value={form.organisme_id} onChange={e => setForm(f => ({ ...f, organisme_id: e.target.value }))} style={selectStyle}>
                  <option value="">Sélectionner un organisme</option>
                  {organismesFiltres.length > 0
                    ? organismesFiltres.map(o => (
                        <option key={o.organisme_id} value={o.organisme_id}>
                          {o.nom}{o.type && o.type !== form.type_financeur ? ` (${o.type})` : ''}
                        </option>
                      ))
                    : organismes.map(o => (
                        <option key={o.organisme_id} value={o.organisme_id}>
                          {o.nom}{o.type ? ` (${o.type})` : ''}
                        </option>
                      ))
                  }
                </select>
              )}
            </div>

            <div>
              <label style={labelStyle}>Montant HT (EUR)</label>
              <input type="number" min="0" step="0.01" value={form.montant_ht}
                onChange={e => setForm(f => ({ ...f, montant_ht: e.target.value }))} style={inputStyle} placeholder="0.00" />
            </div>
            <div>
              <label style={labelStyle}>Pourcentage (%)</label>
              <input type="number" min="0" max="100" step="0.5" value={form.pourcentage}
                onChange={e => setForm(f => ({ ...f, pourcentage: e.target.value }))} style={inputStyle} placeholder="ex : 60" />
            </div>

          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <label style={labelStyle}>Notes</label>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={inputStyle} placeholder="Optionnel" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm); }}>Annuler</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? '...' : 'Enregistrer'}</button>
          </div>
          {msg && <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: couleurMessage(niveauMessage(msg)), fontWeight: 600 }}>{msg}</div>}
        </div>
      )}

      {financements.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '8px', fontSize: '0.9rem' }}>
          Aucun financeur defini. Par defaut chaque client paie la totalite de sa formation.
        </div>
      ) : (
        <div>
          {financements.map(f => {
            const typeLabel = f.type_financeur || '';
            return (
              <div key={f.financement_id} style={{ marginBottom: '0.75rem', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: 'var(--bg-elevated)' }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{f.client_nom || f.organisme_nom || 'Financeur inconnu'}</span>
                    {typeLabel && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', padding: '0.15rem 0.5rem', borderRadius: '10px', background: 'var(--bg-card)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
                        {typeLabel}
                      </span>
                    )}
                    {f.groupe_libelle && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', padding: '0.1rem 0.5rem', borderRadius: '10px', background: 'rgba(232,119,34,0.12)', color: 'var(--accent-orange)', border: '1px solid rgba(232,119,34,0.25)', fontWeight: 600 }}>
                        Grp {f.groupe_libelle}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ textAlign: 'right' }}>
                      {f.montant_ht && <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{parseFloat(f.montant_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR</div>}
                      {f.pourcentage && <div style={{ fontSize: '0.8rem', color: 'var(--accent-blue)', fontWeight: 600 }}>{f.pourcentage}%</div>}
                    </div>
                    {hasRole('utilisateur') && (
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => openEdit(f)} style={{ padding: '0.25rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: '0.75rem', cursor: 'pointer' }}>
                          Edit
                        </button>
                        {(!f.nb_prefactures || f.nb_prefactures == 0) && (
                          <button onClick={() => handleDelete(f.financement_id)} style={{ padding: '0.25rem 0.5rem', borderRadius: '5px', border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#f87171', fontSize: '0.75rem', cursor: 'pointer' }}>
                            Suppr
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {f.notes && <div style={{ padding: '0 1rem 0.6rem', fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>{f.notes}</div>}
              </div>
            );
          })}
        </div>
      )}
      {msg && !showForm && <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: couleurMessage(niveauMessage(msg)), fontWeight: 600 }}>{msg}</div>}
    </div>
  );
}

function ModalNouveauGroupe({ sessionId, groupesExistants, onSave, onClose }) {
  const { token } = useAuth();
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

  // Proposer la prochaine lettre disponible
  const lettresUtilisees = (groupesExistants || []).map(g => (g.groupe || '').toUpperCase());
  const nextLettre = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').find(l => !lettresUtilisees.includes(l)) || '';

  const [groupe, setGroupe] = useState(nextLettre);
  const [nomClient, setNomClient] = useState('');
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/clients?limit=500`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setClients(d.data || []))
      .catch(() => {});
  }, []);

  async function handleSave() {
    if (!groupe.trim()) { setError('Le groupe est requis'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/sessions/${sessionId}/groupes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ groupe: groupe.trim().toUpperCase(), nom_client: nomClient || null }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const inputStyle = { width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '420px', maxWidth: '95vw' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Nouveau groupe / Client</h3>

        <div style={{ marginBottom: '0.9rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
            Groupe * <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(lettre ou identifiant court)</span>
          </label>
          <input value={groupe} onChange={e => setGroupe(e.target.value.toUpperCase())}
            maxLength={5} placeholder="A" style={inputStyle} />
          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.25rem' }}>
            → Sous-session : <strong>{sessionId}{groupe.trim().toUpperCase() || '…'}</strong>
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Client</label>
          <input list="clients-list" value={nomClient} onChange={e => setNomClient(e.target.value)}
            placeholder="Nom du client (optionnel)" style={inputStyle} />
          <datalist id="clients-list">
            {clients.map(c => <option key={c.client_id} value={c.nom_client} />)}
          </datalist>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '0.5rem 0.8rem', marginBottom: '1rem', color: '#f87171', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Création...' : '+ Créer le groupe'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Parcours de formation — onglet modules ───────────────────────────────────
// ── Modal gestion des sous-groupes d'un module ───────────────────────────
function ModalGroupesModule({ mod, sessionInscriptions, formateursData, onClose }) {
  const API_BASE_G = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
  const token = localStorage.getItem('gf_formateur_token') || localStorage.getItem('gf_token');
  const [groupes, setGroupes] = useState(mod.groupes || []);
  const [modeForm, setModeForm] = useState(null); // null | { id?, nom, formateur_id, nom_formateur, dates_groupe, stagiaires }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Émargement par groupe
  const [emargtPanel, setEmargtPanel] = useState(null);  // groupeId ouvert
  const [emargtForms, setEmargtForms] = useState({});    // { [gid]: { date, slot, expires_minutes } }
  const [emargtLiens, setEmargtLiens] = useState({});    // { [gid]: lien }
  const [emargtGenerating, setEmargtGenerating] = useState(false);

  const datesMod = mod.dates_module
    ? (typeof mod.dates_module === 'string' ? JSON.parse(mod.dates_module) : mod.dates_module)
    : [];

  const fmtJour = d => new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });

  const inputSt = { width: '100%', padding: '0.45rem 0.65rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box' };
  const labelSt = { display: 'block', fontSize: '0.77rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' };

  async function rechargerGroupes() {
    try {
      const r = await fetch(`${API_BASE_G}/sessions/${mod.session_id || ''}/modules/${mod.id}/groupes`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setGroupes(Array.isArray(data) ? data : []);
    } catch {}
  }

  function initNouveauGroupe() {
    setModeForm({ nom: `Groupe ${groupes.length + 1}`, formateur_id: '', nom_formateur: '', dates_groupe: [], stagiaires: [] });
    setError('');
  }

  function initEditerGroupe(g) {
    const dates = g.dates_groupe ? (typeof g.dates_groupe === 'string' ? JSON.parse(g.dates_groupe) : g.dates_groupe) : [];
    setModeForm({ id: g.id, nom: g.nom, formateur_id: g.formateur_id || '', nom_formateur: g.nom_formateur || '', dates_groupe: dates, stagiaires: (g.stagiaires || []).map(s => s.stagiaire_id) });
    setError('');
  }

  function toggleDateGroupe(d) {
    setModeForm(f => ({ ...f, dates_groupe: f.dates_groupe.includes(d) ? f.dates_groupe.filter(x => x !== d) : [...f.dates_groupe, d].sort() }));
  }

  function toggleStagiaire(stagId) {
    setModeForm(f => ({ ...f, stagiaires: f.stagiaires.includes(stagId) ? f.stagiaires.filter(x => x !== stagId) : [...f.stagiaires, stagId] }));
  }

  async function sauvegarderGroupe() {
    if (!modeForm.nom.trim()) return setError('Le nom du groupe est requis');
    setSaving(true); setError('');
    try {
      const body = { nom: modeForm.nom, formateur_id: modeForm.formateur_id || null, nom_formateur: modeForm.nom_formateur || null, dates_groupe: modeForm.dates_groupe };
      let groupeId = modeForm.id;
      if (modeForm.id) {
        await fetch(`${API_BASE_G}/module-groupes/${modeForm.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      } else {
        const r = await fetch(`${API_BASE_G}/sessions/${mod.session_id || ''}/modules/${mod.id}/groupes`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
        const data = await r.json();
        groupeId = data.id;
      }
      // Affecter les stagiaires
      const stags = modeForm.stagiaires.map(sid => {
        const insc = sessionInscriptions.find(i => i.stagiaire_id === sid);
        return { stagiaire_id: sid, inscription_id: insc?.id || null };
      });
      await fetch(`${API_BASE_G}/module-groupes/${groupeId}/stagiaires`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ stagiaires: stags }) });
      setModeForm(null);
      await rechargerGroupes();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function supprimerGroupe(gid) {
    if (!window.confirm('Supprimer ce groupe et l\'affectation de ses stagiaires ?')) return;
    await fetch(`${API_BASE_G}/module-groupes/${gid}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    await rechargerGroupes();
  }

  function getEmargtForm(gid, gDates) {
    return emargtForms[gid] || { date: gDates[0] || '', slot: 'AM', expires_minutes: 480 };
  }
  function setEmargtFormField(gid, gDates, changes) {
    setEmargtForms(prev => ({ ...prev, [gid]: { ...getEmargtForm(gid, gDates), ...changes } }));
  }

  async function lancerEmargement(g, gDates) {
    const form = getEmargtForm(g.id, gDates);
    if (!form.date) { setError('Veuillez sélectionner une date'); return; }
    setEmargtGenerating(true); setError('');
    try {
      const r = await fetch(`${API_BASE_G}/emargement/generer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          session_id: mod.session_id,
          date: form.date,
          slot: form.slot || null,
          type: 'presence',
          module_groupe_id: g.id,
          expires_minutes: parseInt(form.expires_minutes) || 480,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erreur serveur');
      setEmargtLiens(prev => ({ ...prev, [g.id]: data.lien }));
    } catch (e) { setError(e.message); }
    finally { setEmargtGenerating(false); }
  }

  // Stagiaires inscrits à la session (liste pour les cases à cocher)
  const stagInscrits = React.useMemo(() => {
    const seen = new Set();
    return (sessionInscriptions || []).filter(i => {
      if (!i.stagiaire_id || seen.has(i.stagiaire_id)) return false;
      seen.add(i.stagiaire_id); return true;
    }).sort((a, b) => (a.nom_stagiaire_snapshot || '').localeCompare(b.nom_stagiaire_snapshot || '', 'fr'));
  }, [sessionInscriptions]);

  const couleurGroupe = i => ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'][i % 6];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', width: '780px', maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.2rem' }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Sous-groupes — {mod.intitule_module}</h3>
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
              Créez des groupes pour affecter les stagiaires à des dates spécifiques de ce module.
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0 0.3rem' }}>✕</button>
        </div>

        {/* Liste des groupes existants */}
        {!modeForm && (
          <>
            {groupes.length === 0 && (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-tertiary)', background: 'var(--bg-elevated)', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.88rem' }}>
                Aucun groupe. Tous les stagiaires inscrits participent ensemble aux mêmes dates.
              </div>
            )}
            {groupes.map((g, gi) => {
              const gDates = g.dates_groupe ? (typeof g.dates_groupe === 'string' ? JSON.parse(g.dates_groupe) : g.dates_groupe) : [];
              const coul = couleurGroupe(gi);
              const ef = getEmargtForm(g.id, gDates);
              const panelOpen = emargtPanel === g.id;
              const lien = emargtLiens[g.id];
              return (
                <div key={g.id} style={{ background: 'var(--bg-elevated)', border: `1px solid var(--border)`, borderLeft: `4px solid ${coul}`, borderRadius: '8px', padding: '0.8rem 1rem', marginBottom: '0.6rem' }}>
                  {/* Ligne principale */}
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.3rem' }}>{g.nom}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {(g.formateur_nom || g.nom_formateur) && <span>👤 {g.formateur_nom || g.nom_formateur}</span>}
                        {gDates.length > 0 && <span>📅 {gDates.map(d => fmtJour(d)).join(', ')}</span>}
                        <span>👥 {(g.stagiaires || []).length} stagiaire{(g.stagiaires || []).length !== 1 ? 's' : ''} : {(g.stagiaires || []).map(s => s.nom_complet).join(', ')}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                      <button className="btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }} onClick={() => initEditerGroupe(g)}>✏</button>
                      <button
                        onClick={() => { setEmargtPanel(panelOpen ? null : g.id); setError(''); }}
                        style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', border: `1px solid ${panelOpen ? '#1d4ed8' : '#3b82f6'}`, background: panelOpen ? '#1d4ed8' : 'transparent', color: panelOpen ? '#fff' : '#1d4ed8', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                        ▶ Émargement
                      </button>
                      <button onClick={() => supprimerGroupe(g.id)} style={{ background: 'none', border: '1px solid #ef4444', color: '#ef4444', padding: '0.25rem 0.55rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' }}>✕</button>
                    </div>
                  </div>

                  {/* Panneau émargement */}
                  {panelOpen && (
                    <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                      {lien ? (
                        /* QR code + lien */
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <QRCodeImg url={lien} size={130} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a3668', marginBottom: '0.35rem' }}>Lien émargement — {g.nom}</div>
                            <div
                              onClick={() => navigator.clipboard?.writeText(lien)}
                              title="Cliquer pour copier"
                              style={{ fontSize: '0.72rem', wordBreak: 'break-all', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '5px', padding: '0.4rem 0.6rem', cursor: 'copy', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                              {lien}
                            </div>
                            <button
                              onClick={() => setEmargtLiens(prev => { const c = { ...prev }; delete c[g.id]; return c; })}
                              className="btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.65rem' }}>
                              + Nouvel émargement
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Formulaire de génération */
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.15rem' }}>Date</label>
                            {gDates.length > 0 ? (
                              <select value={ef.date} onChange={e => setEmargtFormField(g.id, gDates, { date: e.target.value })} style={inputSt}>
                                <option value="">— Sélectionner —</option>
                                {gDates.map(d => <option key={d} value={d}>{fmtJour(d)}</option>)}
                              </select>
                            ) : (
                              <input type="date" value={ef.date} onChange={e => setEmargtFormField(g.id, gDates, { date: e.target.value })} style={inputSt} />
                            )}
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.15rem' }}>Créneau</label>
                            <select value={ef.slot} onChange={e => setEmargtFormField(g.id, gDates, { slot: e.target.value })} style={inputSt}>
                              <option value="AM">Matin</option>
                              <option value="PM">Après-midi</option>
                              <option value="">Journée</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.15rem' }}>Validité</label>
                            <select value={ef.expires_minutes} onChange={e => setEmargtFormField(g.id, gDates, { expires_minutes: e.target.value })} style={inputSt}>
                              <option value={60}>1h</option>
                              <option value={240}>4h</option>
                              <option value={480}>8h</option>
                              <option value={1440}>24h</option>
                            </select>
                          </div>
                          <button
                            onClick={() => lancerEmargement(g, gDates)}
                            disabled={emargtGenerating || !ef.date}
                            style={{ padding: '0.4rem 1rem', borderRadius: '6px', border: 'none', background: emargtGenerating || !ef.date ? '#94a3b8' : '#1e40af', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: emargtGenerating || !ef.date ? 'not-allowed' : 'pointer' }}>
                            {emargtGenerating ? '⏳...' : '▶ Générer'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <button className="btn-primary" onClick={initNouveauGroupe} style={{ marginTop: '0.5rem' }}>+ Ajouter un groupe</button>
          </>
        )}

        {/* Formulaire nouveau/édition groupe */}
        {modeForm && (
          <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1.2rem', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 1rem', color: 'var(--text-primary)' }}>{modeForm.id ? 'Modifier le groupe' : 'Nouveau groupe'}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.7rem', marginBottom: '0.7rem' }}>
              <div>
                <label style={labelSt}>Nom du groupe *</label>
                <input style={inputSt} value={modeForm.nom} onChange={e => setModeForm(f => ({ ...f, nom: e.target.value }))} placeholder="Ex : Groupe 1, Lundi/Mardi…" />
              </div>
              <div>
                <label style={labelSt}>Formateur (optionnel)</label>
                <select style={inputSt} value={modeForm.formateur_id} onChange={e => {
                  const fo = (formateursData?.data || []).find(f => f.formateur_id === e.target.value);
                  setModeForm(f => ({ ...f, formateur_id: e.target.value, nom_formateur: fo?.nom_formateur || '' }));
                }}>
                  <option value="">— Même que le module —</option>
                  {(formateursData?.data || []).map(fo => <option key={fo.formateur_id} value={fo.formateur_id}>{fo.nom_formateur}</option>)}
                </select>
              </div>
            </div>

            {/* Dates du groupe (parmi les dates du module) */}
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={labelSt}>Dates assignées à ce groupe</label>
              {datesMod.length === 0 && <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>Aucune date définie sur le module.</p>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.3rem' }}>
                {datesMod.map(d => (
                  <label key={d} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.6rem', borderRadius: '6px', border: `1px solid ${modeForm.dates_groupe.includes(d) ? '#3b82f6' : 'var(--border)'}`, background: modeForm.dates_groupe.includes(d) ? '#eff6ff' : 'var(--bg-card)', cursor: 'pointer', fontSize: '0.8rem' }}>
                    <input type="checkbox" checked={modeForm.dates_groupe.includes(d)} onChange={() => toggleDateGroupe(d)} style={{ margin: 0 }} />
                    {fmtJour(d)}
                  </label>
                ))}
              </div>
            </div>

            {/* Stagiaires (liste à cocher) */}
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={labelSt}>Stagiaires affectés à ce groupe ({modeForm.stagiaires.length}/{stagInscrits.length})</label>
              <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.5rem', background: 'var(--bg-card)', marginTop: '0.3rem' }}>
                {stagInscrits.length === 0 && <p style={{ color: 'var(--text-tertiary)', fontSize: '0.82rem', margin: 0 }}>Aucun stagiaire inscrit à cette session.</p>}
                {stagInscrits.map(insc => (
                  <label key={insc.stagiaire_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0.4rem', cursor: 'pointer', borderRadius: '4px', fontSize: '0.85rem' }}>
                    <input type="checkbox" checked={modeForm.stagiaires.includes(insc.stagiaire_id)} onChange={() => toggleStagiaire(insc.stagiaire_id)} />
                    {insc.nom_stagiaire_snapshot || insc.stagiaire_id}
                    {insc.nom_client_snapshot && <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>— {insc.nom_client_snapshot}</span>}
                  </label>
                ))}
              </div>
            </div>

            {error && <div style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171', padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.83rem', marginBottom: '0.7rem' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => { setModeForm(null); setError(''); }} disabled={saving}>Annuler</button>
              <button className="btn-primary" onClick={sauvegarderGroupe} disabled={saving}>{saving ? 'Enregistrement…' : '✓ Enregistrer'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ParcoursTab({ sessionId, session }) {
  const API_BASE_P = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
  const token = localStorage.getItem('gf_formateur_token') || localStorage.getItem('gf_token');
  const { data: formationsData } = useApi('/formations', { limit: 500 }, []);
  const { data: formateursData } = useApi('/formateurs', {}, []);
  const { data: sallesRaw } = useApi('/salles');
  const sallesData = Array.isArray(sallesRaw) ? sallesRaw : (sallesRaw?.data || []);
  const { data: questionnairesRaw } = useApi('/questionnaires' + (session.formation_id ? '?formation_id=' + session.formation_id : ''));
  const questionnairesData = questionnairesRaw?.data || [];

  const [modules, setModules] = useState(session.modules || []);
  const [modal, setModal] = useState(null);
  const [modalGroupes, setModalGroupes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [genPdf, setGenPdf] = useState(null);
  // Accès formateur après sauvegarde module
  const [pendingAccesModule, setPendingAccesModule] = useState(null); // { formateurId, formateurNom }
  const [sendingAccesModule, setSendingAccesModule] = useState(false);
  const [accesModuleResult, setAccesModuleResult] = useState(null); // 'ok' | 'error'
  // Renvoi accès depuis la carte module
  const [sendingAccesMap, setSendingAccesMap] = useState({}); // { [formateurId]: bool }
  const [accesMapResult, setAccesMapResult] = useState({});   // { [formateurId]: 'ok'|'error' }
  // PDF émargement parcours avec plage de dates
  const [pdfDateDebut, setPdfDateDebut] = useState('');
  const [pdfDateFin, setPdfDateFin] = useState('');

  // Dates disponibles dans la session (issues des occurrences)
  const datesSession = React.useMemo(() => {
    const occ = session.occurrences || [];
    return [...new Set(occ.map(o => o.date_iso || (o.date ? String(o.date).substring(0, 10) : null)).filter(Boolean))].sort();
  }, [session.occurrences]);

  const fmtD = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }) : '?';
  const fmtDateCourt = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '?';

  async function recharger() {
    try {
      const r = await fetch(`${API_BASE_P}/sessions/${sessionId}/modules`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setModules(Array.isArray(data) ? data : []);
    } catch {}
  }

  async function sauvegarder(form) {
    setSaving(true);
    try {
      const body = {
        intitule_module:  form.intitule_module,
        formation_id:     form.formation_id || null,
        ordre:            parseInt(form.ordre) || 1,
        formateur_id:     form.formateur_id || null,
        salle_id:         form.salle_id || null,
        dates_module:     form.dates_module || [],
        duree_heures:     form.duree_heures ? parseFloat(form.duree_heures) : null,
        notes:            form.notes || null,
        questionnaire_id:              form.questionnaire_id ? parseInt(form.questionnaire_id) : null,
        satisfaction_questionnaire_id: form.satisfaction_questionnaire_id ? parseInt(form.satisfaction_questionnaire_id) : null,
      };
      const url = form.id
        ? `${API_BASE_P}/sessions/${sessionId}/modules/${form.id}`
        : `${API_BASE_P}/sessions/${sessionId}/modules`;
      const method = form.id ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error); }
      setModal(null);
      await recharger();
      if (form.formateur_id) {
        const fmt = formateursData?.data?.find(f => f.formateur_id === form.formateur_id);
        setPendingAccesModule({ formateurId: form.formateur_id, formateurNom: fmt?.nom_formateur || 'le formateur' });
        setAccesModuleResult(null);
      }
    } catch (e) { alert('Erreur : ' + e.message); }
    setSaving(false);
  }

  async function envoyerAccesModule(formateurId) {
    setSendingAccesModule(true);
    try {
      const r = await fetch(`${API_BASE_P}/sessions/${sessionId}/formateur-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ formateur_id: formateurId }),
      });
      if (!r.ok) throw new Error((await r.json()).error);
      setAccesModuleResult('ok');
    } catch { setAccesModuleResult('error'); }
    setSendingAccesModule(false);
  }

  async function renvoyerAccesModule(formateurId) {
    setSendingAccesMap(m => ({ ...m, [formateurId]: true }));
    setAccesMapResult(m => ({ ...m, [formateurId]: null }));
    try {
      const r = await fetch(`${API_BASE_P}/sessions/${sessionId}/formateur-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ formateur_id: formateurId }),
      });
      if (!r.ok) throw new Error((await r.json()).error);
      setAccesMapResult(m => ({ ...m, [formateurId]: 'ok' }));
    } catch { setAccesMapResult(m => ({ ...m, [formateurId]: 'error' })); }
    setSendingAccesMap(m => ({ ...m, [formateurId]: false }));
    setTimeout(() => setAccesMapResult(m => ({ ...m, [formateurId]: null })), 3000);
  }

  async function supprimer(mid) {
    if (!window.confirm('Supprimer ce module ?')) return;
    await fetch(`${API_BASE_P}/sessions/${sessionId}/modules/${mid}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    await recharger();
  }

  async function ouvrirAttestations(mod) {
    setGenPdf(mod.id);
    await ouvrirDocument({ titre: `Attestations — ${mod.intitule_module || 'module'}`, url: `/documents/attestation/module/${mod.id}` });
    setGenPdf(null);
  }

  const couleurModule = i => ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'][i % 8];

  // Générer l'URL PDF parcours avec filtre dates
  function urlPdfParcours() {
    const base = `/emargement/session/${sessionId}/pdf-journalier`;
    const params = new URLSearchParams();
    if (pdfDateDebut) params.set('date_debut', pdfDateDebut);
    if (pdfDateFin)   params.set('date_fin',   pdfDateFin);
    return params.toString() ? `${base}?${params}` : base;
  }

  return (
    <div style={{ padding: '1rem 0' }}>

      {/* ── Section feuilles d'émargement du parcours ── */}
      <div style={{ background: '#f0f5ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '0.9rem 1.2rem', marginBottom: '1.2rem' }}>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a3668', marginBottom: '0.7rem' }}>
          📋 Feuilles d'émargement — parcours complet
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center' }}>
          <button onClick={() => ouvrirDocument({ titre: 'Émargement — parcours complet', url: `/emargement/session/${sessionId}/pdf-journalier` })}
             style={{ padding: '0.35rem 0.8rem', borderRadius: '6px', background: '#1d4ed8', color: '#fff', fontWeight: 700, fontSize: '0.82rem', border: 'none', cursor: 'pointer' }}>
            ⬇ Tout imprimer
          </button>
          <button onClick={() => ouvrirDocument({ titre: 'Feuille certificats', url: `/emargement/session/${sessionId}/feuille-certificat` })}
             style={{ padding: '0.35rem 0.8rem', borderRadius: '6px', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: '0.82rem', border: 'none', cursor: 'pointer' }}>
            📜 Feuille certificats
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', color: '#4b5563', fontWeight: 600 }}>De :</span>
            <input type="date" value={pdfDateDebut} onChange={e => setPdfDateDebut(e.target.value)}
              style={{ padding: '0.25rem 0.5rem', borderRadius: '5px', border: '1px solid #93c5fd', fontSize: '0.8rem', background: '#fff' }} />
            <span style={{ fontSize: '0.78rem', color: '#4b5563', fontWeight: 600 }}>À :</span>
            <input type="date" value={pdfDateFin} onChange={e => setPdfDateFin(e.target.value)}
              style={{ padding: '0.25rem 0.5rem', borderRadius: '5px', border: '1px solid #93c5fd', fontSize: '0.8rem', background: '#fff' }} />
            {(pdfDateDebut || pdfDateFin) && (
              <button onClick={() => ouvrirDocument({ titre: 'Émargement — plage de dates', url: urlPdfParcours() })}
                 style={{ padding: '0.3rem 0.75rem', borderRadius: '6px', background: '#eff6ff', border: '1px solid #1d4ed8', color: '#1d4ed8', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                ⬇ Télécharger la plage
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
          Définissez les modules qui composent ce parcours. Chaque module génère ses propres attestations.
        </p>
        <button className="btn-primary" onClick={() => setModal({ intitule_module: '', formation_id: '', ordre: modules.length + 1, formateur_id: '', dates_module: [], duree_heures: '', notes: '' })}>
          + Ajouter un module
        </button>
      </div>

      {modules.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-tertiary)', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px dashed var(--border)' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📚</div>
          <div>Aucun module défini. Ajoutez des modules pour créer un parcours de formation.</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {modules.map((mod, i) => {
          const dates = mod.dates_module ? (typeof mod.dates_module === 'string' ? JSON.parse(mod.dates_module) : mod.dates_module) : [];
          const groupes = mod.groupes || [];
          const couleur = couleurModule(i);
          return (
            <div key={mod.id} style={{ background: 'var(--bg-elevated)', border: `1px solid var(--border)`, borderLeft: `4px solid ${couleur}`, borderRadius: '10px', padding: '1rem 1.2rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                {/* Numéro */}
                <div style={{ minWidth: '28px', height: '28px', borderRadius: '50%', background: couleur, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 }}>{mod.ordre}</div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.97rem', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>{mod.intitule_module}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {mod.duree_heures && <span>⏱ {mod.duree_heures}h</span>}
                    {(mod.formateur_nom || mod.nom_formateur) && <span>👤 {mod.formateur_nom || mod.nom_formateur}</span>}
                    {mod.module_salle_nom && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                        {mod.module_salle_couleur && <span style={{ width: 8, height: 8, borderRadius: '50%', background: mod.module_salle_couleur, display: 'inline-block', flexShrink: 0 }} />}
                        📍 {mod.module_salle_nom}
                      </span>
                    )}
                    {dates.length > 0 && (
                      <span>📅 {dates.length === 1 ? fmtD(dates[0]) : `${fmtDateCourt(dates[0])} → ${fmtDateCourt(dates[dates.length-1])} (${dates.length}j)`}</span>
                    )}
                    {mod.notes && <span style={{ fontStyle: 'italic', color: 'var(--text-tertiary)' }}>{mod.notes}</span>}
                  </div>
                  {dates.length > 1 && (
                    <div style={{ marginTop: '0.3rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {dates.map(d => <span key={d} style={{ fontSize: '0.72rem', background: `${couleur}22`, color: couleur, border: `1px solid ${couleur}44`, borderRadius: '4px', padding: '0.1rem 0.35rem' }}>{fmtD(d)}</span>)}
                    </div>
                  )}
                  {/* Résumé des sous-groupes */}
                  {groupes.length > 0 && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                      {groupes.map(g => (
                        <span key={g.id} style={{ fontSize: '0.73rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.1rem 0.45rem', color: 'var(--text-secondary)' }}>
                          👥 {g.nom} ({(g.stagiaires || []).length} stag.)
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem' }}
                    onClick={() => setModal({ ...mod, dates_module: dates })}>✏ Modifier</button>
                  <button
                    onClick={() => setModalGroupes({ ...mod, dates_module: dates, session_id: sessionId })}
                    style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid #10b981', background: groupes.length > 0 ? '#d1fae5' : 'transparent', color: '#065f46', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem' }}>
                    👥 Sous-groupes{groupes.length > 0 ? ` (${groupes.length})` : ''}
                  </button>
                  <button
                    onClick={() => ouvrirAttestations(mod)}
                    disabled={genPdf === mod.id}
                    style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: 'none', background: '#1a3668', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem', opacity: genPdf === mod.id ? 0.6 : 1 }}>
                    {genPdf === mod.id ? '⏳' : '📜'} Attestations
                  </button>
                  {mod.formateur_id && (
                    <button
                      onClick={() => renvoyerAccesModule(mod.formateur_id)}
                      disabled={sendingAccesMap[mod.formateur_id]}
                      title="Envoyer / renvoyer le lien d'accès formateur (QR code + PIN)"
                      style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: `1px solid ${accesMapResult[mod.formateur_id] === 'ok' ? '#16a34a' : accesMapResult[mod.formateur_id] === 'error' ? '#ef4444' : '#E87722'}`, background: accesMapResult[mod.formateur_id] === 'ok' ? 'rgba(22,163,74,0.1)' : 'transparent', color: accesMapResult[mod.formateur_id] === 'ok' ? '#16a34a' : accesMapResult[mod.formateur_id] === 'error' ? '#ef4444' : '#E87722', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem', opacity: sendingAccesMap[mod.formateur_id] ? 0.6 : 1 }}>
                      {sendingAccesMap[mod.formateur_id] ? '⏳' : accesMapResult[mod.formateur_id] === 'ok' ? '✓ Envoyé' : accesMapResult[mod.formateur_id] === 'error' ? '✗ Erreur' : '📧 Accès'}
                    </button>
                  )}
                  <button onClick={() => supprimer(mod.id)} style={{ background: 'none', border: '1px solid #ef4444', color: '#ef4444', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem' }}>✕</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Modal accès formateur après sauvegarde module ─────────────────── */}
      {pendingAccesModule && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.75rem', width: '420px', maxWidth: '90vw' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.6rem' }}>📧 Accès formateur</div>
            <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginBottom: '1.2rem', lineHeight: 1.6 }}>
              Souhaitez-vous envoyer maintenant le lien d'accès à la session (QR code + PIN) à <strong>{pendingAccesModule.formateurNom}</strong> ?
            </p>
            {accesModuleResult && (
              <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '1rem', color: couleurMessage(accesModuleResult === 'ok' ? 'succes' : 'attention') }}>
                {accesModuleResult === 'ok'
                  ? '✓ Email envoyé avec succès.'
                  : "⚠ Envoi échoué — vous pourrez le renvoyer via le bouton 📧 Accès du module."}
              </div>
            )}
            {!accesModuleResult && (
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={() => setPendingAccesModule(null)} disabled={sendingAccesModule}>Pas maintenant</button>
                <button className="btn-primary" disabled={sendingAccesModule} onClick={() => envoyerAccesModule(pendingAccesModule.formateurId)}>
                  {sendingAccesModule ? '⏳ Envoi…' : "📧 Envoyer le lien d'accès"}
                </button>
              </div>
            )}
            {accesModuleResult && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn-primary" onClick={() => setPendingAccesModule(null)}>Fermer</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal ajout/édition module ─────────────────────────────────────── */}
      {modal && (
        <ModalModule
          mod={modal}
          datesSession={datesSession}
          formationsData={formationsData}
          formateursData={formateursData}
          sallesData={sallesData}
          questionnairesData={questionnairesData}
          saving={saving}
          onSave={sauvegarder}
          onClose={() => setModal(null)}
        />
      )}

      {/* ── Modal gestion sous-groupes ─────────────────────────────────────── */}
      {modalGroupes && (
        <ModalGroupesModule
          mod={modalGroupes}
          sessionInscriptions={session.inscriptions || []}
          formateursData={formateursData}
          onClose={async () => { setModalGroupes(null); await recharger(); }}
        />
      )}
    </div>
  );
}

function ModalModule({ mod, datesSession, formationsData, formateursData, sallesData, questionnairesData = [], saving, onSave, onClose }) {
  const [form, setForm] = useState({
    id:               mod.id || null,
    intitule_module:  mod.intitule_module || '',
    formation_id:     mod.formation_id || '',
    ordre:            mod.ordre || 1,
    formateur_id:     mod.formateur_id || '',
    salle_id:         mod.salle_id || '',
    dates_module:     Array.isArray(mod.dates_module) ? mod.dates_module : (mod.dates_module ? JSON.parse(mod.dates_module) : []),
    duree_heures:     mod.duree_heures || '',
    notes:            mod.notes || '',
    questionnaire_id:              mod.questionnaire_id || '',
    satisfaction_questionnaire_id: mod.satisfaction_questionnaire_id || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = { width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' };

  function toggleDate(d) {
    set('dates_module', form.dates_module.includes(d) ? form.dates_module.filter(x => x !== d) : [...form.dates_module, d].sort());
  }

  const fmtJour = d => new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '560px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>{form.id ? '✏ Modifier le module' : '+ Nouveau module'}</h3>

        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Formation du catalogue</label>
          <select value={form.formation_id} onChange={e => {
            const f = formationsData?.data?.find(f => f.formation_id === parseInt(e.target.value));
            setForm(prev => ({ ...prev, formation_id: e.target.value, intitule_module: f ? f.intitule : prev.intitule_module, duree_heures: f ? (f.duree_heures || prev.duree_heures) : prev.duree_heures }));
          }} style={inputStyle}>
            <option value="">— Choisir ou laisser libre —</option>
            {formationsData?.data?.map(f => <option key={f.formation_id} value={f.formation_id}>[{f.numero || f.formation_id}] {f.intitule?.substring(0, 60)}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Intitulé du module *</label>
          <input type="text" value={form.intitule_module} onChange={e => set('intitule_module', e.target.value)} style={inputStyle} placeholder="Ex: Formation SST, Module Amiante SS4..." />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.7rem', marginBottom: '0.7rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Ordre</label>
            <input type="number" value={form.ordre} onChange={e => set('ordre', e.target.value)} min="1" style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Durée (heures)</label>
            <input type="number" step="0.5" value={form.duree_heures} onChange={e => set('duree_heures', e.target.value)} style={inputStyle} placeholder="Ex: 14" />
          </div>
        </div>

        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Formateur du module</label>
          <select value={form.formateur_id} onChange={e => set('formateur_id', e.target.value)} style={inputStyle}>
            <option value="">— Même que la session —</option>
            {formateursData?.data?.map(f => <option key={f.formateur_id} value={f.formateur_id}>{[f.prenom, f.nom_formateur].filter(Boolean).join(' ')}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Salle du module</label>
          <select value={form.salle_id} onChange={e => set('salle_id', e.target.value)} style={inputStyle}>
            <option value="">— Salle de la session —</option>
            {(sallesData || []).map(s => <option key={s.salle_id} value={s.salle_id}>{s.nom_salle}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
            Dates du module
            {form.dates_module.length > 0 && <span style={{ marginLeft: '0.5rem', fontWeight: 400, color: 'var(--text-tertiary)' }}>({form.dates_module.length} jour{form.dates_module.length > 1 ? 's' : ''} sélectionné{form.dates_module.length > 1 ? 's' : ''})</span>}
          </label>
          {datesSession.length > 0 ? (
            <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', maxHeight: '200px', overflowY: 'auto' }}>
              {datesSession.map((d, i) => {
                const selected = form.dates_module.includes(d);
                return (
                  <div key={d} onClick={() => toggleDate(d)} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.7rem', cursor: 'pointer', background: selected ? 'rgba(59,130,246,0.12)' : (i % 2 === 0 ? 'var(--bg-elevated)' : 'transparent'), borderBottom: i < datesSession.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.15s' }}>
                    <span style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${selected ? 'var(--accent-blue)' : 'var(--border)'}`, background: selected ? 'var(--accent-blue)' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.7rem' }}>{selected ? '✓' : ''}</span>
                    <span style={{ fontSize: '0.85rem', color: selected ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{fmtJour(d)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: '0.5rem', color: 'var(--text-tertiary)', fontSize: '0.82rem', fontStyle: 'italic' }}>Aucune occurrence définie sur cette session (générez d'abord les créneaux)</div>
          )}
        </div>

        {questionnairesData.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.7rem', marginBottom: '0.7rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Questionnaire d'évaluation</label>
              <select value={form.questionnaire_id} onChange={e => set('questionnaire_id', e.target.value)} style={inputStyle}>
                <option value="">— Aucun —</option>
                {questionnairesData.filter(q => q.type !== 'satisfaction').map(q => <option key={q.questionnaire_id} value={q.questionnaire_id}>{q.titre}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Questionnaire de satisfaction</label>
              <select value={form.satisfaction_questionnaire_id} onChange={e => set('satisfaction_questionnaire_id', e.target.value)} style={inputStyle}>
                <option value="">— Aucun —</option>
                {questionnairesData.filter(q => q.type === 'satisfaction').map(q => <option key={q.questionnaire_id} value={q.questionnaire_id}>{q.titre}</option>)}
              </select>
            </div>
          </div>
        )}

        <div style={{ marginBottom: '1.2rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Notes</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={() => { if (!form.intitule_module) return alert('L\'intitulé est requis'); onSave(form); }} disabled={saving}>
            {saving ? 'Enregistrement...' : form.id ? '✓ Modifier' : '✓ Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Onglet Créneaux : salle et formateur par jour (défini au niveau module pour stabilité React) ─
const OccurrencesSallesTab = React.memo(({ sessionId, occurrences, salleSessionId, formateurSessionId, nomFormateurSession }) => {
  const [salles, setSalles] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [salleParDate, setSalleParDate] = useState({});     // { 'YYYY-MM-DD': salle_id }
  const [formateurParDate, setFormateurParDate] = useState({}); // { 'YYYY-MM-DD': formateur_id }
  const [saving, setSaving] = useState({});                 // { 'date-salle': bool, 'date-formateur': bool }
  const [conflictModal, setConflictModal] = useState({ open: false, conflicts: [], label: '', onConfirm: null });

  useEffect(() => {
    apiFetch('/salles').then(d => setSalles(d.data || d || [])).catch(() => {});
    apiFetch('/formateurs').then(d => setFormateurs(d.data || d || [])).catch(() => {});
  }, []);

  // Initialiser depuis les occurrences (salle_id et formateur_id par date)
  useEffect(() => {
    const salleMap = {}, fmtMap = {};
    (occurrences || []).forEach(o => {
      const d = o.date_iso || String(o.date || '').substring(0, 10);
      if (d) {
        if (o.salle_id     && !salleMap[d]) salleMap[d] = String(o.salle_id);
        if (o.formateur_id && !fmtMap[d])   fmtMap[d]   = String(o.formateur_id);
      }
    });
    setSalleParDate(salleMap);
    setFormateurParDate(fmtMap);
  }, [occurrences]);

  // Dates uniques triées
  const dates = [...new Set((occurrences || []).map(o =>
    o.date_iso || String(o.date || '').substring(0, 10)
  ).filter(Boolean))].sort();

  // Vérification de conflits pour une date précise
  async function verifierConflits({ date, salle_id, formateur_id }) {
    try {
      const res = await apiFetch('/sessions/check-conflicts', {
        method: 'POST',
        body: JSON.stringify({
          date_debut:          date,
          date_fin:            date,
          salle_id:            salle_id    || null,
          formateur_id:        formateur_id || null,
          exclude_session_id:  sessionId,
        }),
      });
      return res.conflicts || [];
    } catch (e) {
      console.warn('[verifierConflits] Erreur API check-conflicts :', e);
      return [];
    }
  }

  // Sauvegarde effective (après confirmation éventuelle)
  async function doSaveSalle(date, salle_id) {
    setConflictModal({ open: false, conflicts: [], label: '', onConfirm: null });
    setSaving(s => ({ ...s, [`${date}-salle`]: true }));
    try {
      await apiFetch(`/sessions/${sessionId}/occurrences-salle`, {
        method: 'PATCH',
        body: JSON.stringify({ date, salle_id: salle_id || null }),
      });
      setSalleParDate(m => ({ ...m, [date]: salle_id || '' }));
    } catch(e) { alert('Erreur : ' + e.message); }
    setSaving(s => ({ ...s, [`${date}-salle`]: false }));
  }

  async function doSaveFormateur(date, formateur_id) {
    setConflictModal({ open: false, conflicts: [], label: '', onConfirm: null });
    setSaving(s => ({ ...s, [`${date}-formateur`]: true }));
    try {
      await apiFetch(`/sessions/${sessionId}/occurrences-formateur`, {
        method: 'PATCH',
        body: JSON.stringify({ date, formateur_id: formateur_id || null }),
      });
      setFormateurParDate(m => ({ ...m, [date]: formateur_id || '' }));
    } catch(e) { alert('Erreur : ' + e.message); }
    setSaving(s => ({ ...s, [`${date}-formateur`]: false }));
  }

  // Points d'entrée avec vérification de conflits
  async function saveSalle(date, salle_id) {
    if (salle_id) {
      const conflicts = await verifierConflits({ date, salle_id });
      if (conflicts.length > 0) {
        setConflictModal({ open: true, conflicts, label: fmtJour(date), onConfirm: () => doSaveSalle(date, salle_id) });
        return;
      }
    }
    await doSaveSalle(date, salle_id);
  }

  async function saveFormateur(date, formateur_id) {
    if (formateur_id) {
      const conflicts = await verifierConflits({ date, formateur_id });
      if (conflicts.length > 0) {
        setConflictModal({ open: true, conflicts, label: fmtJour(date), onConfirm: () => doSaveFormateur(date, formateur_id) });
        return;
      }
    }
    await doSaveFormateur(date, formateur_id);
  }

  const fmtJour = d => new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
  const slotsParDate = {};
  (occurrences || []).forEach(o => {
    const d = o.date_iso || String(o.date || '').substring(0, 10);
    if (d) { if (!slotsParDate[d]) slotsParDate[d] = []; slotsParDate[d].push(o.slot); }
  });

  const selectStyle = { flex: 1, padding: '0.3rem 0.5rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.83rem', cursor: 'pointer' };

  const fmtDateCourt = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '?';

  return (
    <div style={{ padding: '0.5rem 0' }}>

      {/* ── Modale de confirmation de conflits ── */}
      {conflictModal.open && (
        <ConflictModal
          conflicts={conflictModal.conflicts}
          label={conflictModal.label}
          onCancel={() => setConflictModal({ open: false, conflicts: [], label: '', onConfirm: null })}
          onConfirm={conflictModal.onConfirm}
          onModifier={conflictModal.conflicts.some(c => c.type === 'salle')
            ? () => setConflictModal({ open: false, conflicts: [], label: '', onConfirm: null })
            : undefined}
          modifierLabel={conflictModal.conflicts.some(c => c.type === 'salle') ? '🏫 Modifier la salle' : '👤 Modifier le formateur'}
        />
      )}

      <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        Définissez une salle et/ou un formateur spécifiques pour chaque jour (absence, remplacement…). Laissez vide pour utiliser la valeur par défaut de la session.
      </p>
      {dates.length === 0 ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', fontStyle: 'italic' }}>Aucune occurrence générée.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-elevated)', borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', width: '28%' }}>Jour</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', width: '10%' }}>Créneaux</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', width: '31%' }}>🏫 Salle du jour</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', width: '31%' }}>👤 Formateur du jour</th>
            </tr>
          </thead>
          <tbody>
            {dates.map((date, idx) => {
              const salle_id   = salleParDate[date]     || '';
              const fmt_id     = formateurParDate[date]  || '';
              const salle      = salles.find(s => String(s.salle_id)      === String(salle_id));
              const slots      = [...new Set(slotsParDate[date] || [])].join(', ');
              const bg         = idx % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-elevated)';
              const savingSalle = saving[`${date}-salle`];
              const savingFmt   = saving[`${date}-formateur`];
              return (
                <tr key={date} style={{ background: bg, borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{fmtJour(date)}</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{slots || '—'}</td>

                  {/* ── Salle ── */}
                  <td style={{ padding: '0.4rem 0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {salle?.couleur && (
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: salle.couleur, flexShrink: 0, display: 'inline-block' }} />
                      )}
                      <select value={salle_id} onChange={e => saveSalle(date, e.target.value)}
                        disabled={savingSalle} style={selectStyle}>
                        <option value="">— Session —</option>
                        {salles.map(s => <option key={s.salle_id} value={s.salle_id}>{s.nom_salle}</option>)}
                      </select>
                      {savingSalle && <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>…</span>}
                      {!savingSalle && salle_id && (
                        <button onClick={() => saveSalle(date, '')}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem', padding: '0 0.1rem' }} title="Remettre la salle de la session">✕</button>
                      )}
                    </div>
                  </td>

                  {/* ── Formateur ── */}
                  <td style={{ padding: '0.4rem 0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <select value={fmt_id} onChange={e => saveFormateur(date, e.target.value)}
                        disabled={savingFmt} style={selectStyle}>
                        <option value="">— Session —</option>
                        {formateurs.map(f => (
                          <option key={f.formateur_id} value={f.formateur_id}>
                            {[f.prenom, f.nom_formateur].filter(Boolean).join(' ')}
                          </option>
                        ))}
                      </select>
                      {savingFmt && <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>…</span>}
                      {!savingFmt && fmt_id && (
                        <button onClick={() => saveFormateur(date, '')}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem', padding: '0 0.1rem' }} title="Remettre le formateur de la session">✕</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
});

// ─── Onglet Fiche de Progrès Formateur ────────────────────────────────────────
// formateurId : null = fiche session (rétrocompat), sinon fiche de ce formateur spécifique
function FicheProgresTab({ sessionId, session, formateurId = null }) {
  const API_BASE_FP = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
  const { hasRole, isFormateur, formateurId: ctxFormateurId } = useAuth();
  const peutValider = hasRole('utilisateur');

  // ID effectif : pour un formateur connecté, toujours sa propre fiche
  const effectiveId = isFormateur ? (ctxFormateurId || null) : formateurId;
  const fmtQs = effectiveId ? `?formateur_id=${encodeURIComponent(effectiveId)}` : '';

  const [fiche, setFiche] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [colonnes, setColonnes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [showSig, setShowSig] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const token = localStorage.getItem('gf_formateur_token') || localStorage.getItem('gf_token');

  const charger = async () => {
    setLoading(true);
    try {
      const [ficheR, colsR] = await Promise.all([
        fetch(`${API_BASE_FP}/fiche-progres/session/${sessionId}${fmtQs}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_FP}/fiche-progres/colonnes`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const ficheData = await ficheR.json();
      const colsData = await colsR.json();
      setFiche(ficheData);
      setLignes(ficheData.lignes || []);
      setColonnes(colsData.filter(c => c.actif !== false));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { charger(); }, [sessionId, effectiveId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Signature au téléphone : sur ordinateur (pointeur non tactile) le tracé à la souris est de mauvaise qualité → QR code vers une page de signature
  const estTactile = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  const [lienSig, setLienSig] = useState(null);       // { qr, expire_at, avant } tant que le QR code est affiché
  const [lienSigErreur, setLienSigErreur] = useState('');
  const [lienSigAttente, setLienSigAttente] = useState(false);

  const ouvrirQrSignature = async () => {
    setLienSigErreur(''); setLienSigAttente(true);
    try {
      const r = await fetch(`${API_BASE_FP}/fiche-progres/${fiche.id}/lien-signature`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setLienSig({ qr: d.qr, expire_at: d.expire_at, avant: fiche.signature_formateur || null });
    } catch (e) { setLienSigErreur(e.message); }
    setLienSigAttente(false);
  };

  // Tant que le QR code est affiché : on vérifie toutes les 3 s si la signature est arrivée (sans toucher au reste de la fiche, qui peut être en cours de saisie)
  useEffect(() => {
    if (!lienSig || !fiche?.id) return undefined;
    const id = setInterval(async () => {
      if (Date.now() > new Date(lienSig.expire_at).getTime()) return;
      try {
        const r = await fetch(`${API_BASE_FP}/fiche-progres/session/${sessionId}${fmtQs}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        if (d.signature_formateur && d.signature_formateur !== lienSig.avant) {
          setFiche(f => ({ ...f, signature_formateur: d.signature_formateur }));
          setLienSig(null);
        }
      } catch { /* nouvelle tentative au prochain tour */ }
    }, 3000);
    return () => clearInterval(id);
  }, [lienSig, fiche?.id, sessionId, fmtQs, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const sauvegarder = async (sigBase64) => {
    setSaving(true);
    setSaveMsg('');
    try {
      const body = {
        date_fiche: fiche.date_fiche,
        livret_accueil: fiche.livret_accueil,
        aucune_observation: fiche.aucune_observation,
        signature_formateur: sigBase64 !== undefined ? sigBase64 : fiche.signature_formateur,
        lignes: lignes,
      };
      const r = await fetch(`${API_BASE_FP}/fiche-progres/session/${sessionId}${fmtQs}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setSaveMsg('✓ Sauvegardé');
      await charger();
      setSaving(false);
      setTimeout(() => setSaveMsg(''), 3000);
      return true;
    } catch (e) { setSaveMsg('Erreur : ' + e.message); }
    setSaving(false);
    setTimeout(() => setSaveMsg(''), 3000);
    return false;
  };

  // Au moins une observation formulée : une cellule renseignée dans une colonne active du tableau
  const aDesObservations = lignes.some(l => colonnes.some(c => String((l.valeurs || {})[c.cle] ?? '').trim() !== ''));

  const soumettre = async () => {
    if (!fiche.livret_accueil) { alert('Veuillez cocher « Je reconnais avoir pris connaissance du livret d\'accueil formateurs » avant de soumettre.'); return; }
    if (!fiche.aucune_observation && !aDesObservations) {
      alert('Veuillez formuler au moins une observation dans le tableau, ou cocher « Cocher si aucune observation n\'est à formuler », avant de soumettre.');
      return;
    }
    if (!fiche.signature_formateur) { alert('Veuillez apposer votre signature avant de soumettre.'); return; }
    if (!window.confirm('Soumettre la fiche ? Elle ne pourra plus être modifiée sans intervention de l\'administration.')) return;
    // enregistre d'abord ce qui est à l'écran : le serveur contrôle la fiche enregistrée
    if (!(await sauvegarder(undefined))) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE_FP}/fiche-progres/session/${sessionId}/soumettre${fmtQs}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      await charger();
    } catch (e) { alert('Erreur : ' + e.message); }
    setSaving(false);
  };

  const valider = async () => {
    const commentaire = window.prompt('Commentaire de validation (optionnel) :') || '';
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE_FP}/fiche-progres/session/${sessionId}/valider${fmtQs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ commentaire_admin: commentaire }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      await charger();
    } catch (e) { alert('Erreur : ' + e.message); }
    setSaving(false);
  };

  const reouvrir = async () => {
    if (!window.confirm('Remettre la fiche en brouillon ?')) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE_FP}/fiche-progres/session/${sessionId}/reouvrir${fmtQs}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      await charger();
    } catch (e) { alert('Erreur : ' + e.message); }
    setSaving(false);
  };

  const telechargerPDF = async () => {
    setPdfLoading(true);
    try {
      const r = await fetch(`${API_BASE_FP}/fiche-progres/session/${sessionId}/pdf${fmtQs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const sufFmt = effectiveId ? `_${effectiveId}` : '';
      a.href = url; a.download = `fiche_progres_${sessionId}${sufFmt}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert('Erreur PDF : ' + e.message); }
    setPdfLoading(false);
  };

  const setLigneVal = (numLigne, cle, valeur) => {
    setLignes(prev => prev.map(l =>
      l.numero_ligne === numLigne
        ? { ...l, valeurs: { ...l.valeurs, [cle]: valeur } }
        : l
    ));
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement…</div>;
  if (!fiche) return <div style={{ padding: '1rem', color: 'red' }}>Erreur de chargement</div>;

  const statut = fiche.statut || 'brouillon';
  const estModifiable = statut === 'brouillon';
  const badgeColor = { brouillon: '#6b7280', soumis: '#d97706', valide: '#16a34a' }[statut] || '#6b7280';
  const badgeLabel = { brouillon: '⬜ Brouillon', soumis: '⏳ Soumis', valide: '✅ Validé' }[statut] || statut;

  return (
    <div style={{ padding: '1rem 0' }}>

      {/* ── Barre de statut et actions ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap',
                    marginBottom: '1rem', padding: '0.7rem 1rem',
                    background: 'var(--bg-elevated)', borderRadius: 10, border: '1px solid var(--border)' }}>
        <span style={{ fontWeight: 700, fontSize: '0.88rem', padding: '0.25rem 0.7rem',
                       borderRadius: 20, background: badgeColor, color: '#fff' }}>
          {badgeLabel}
        </span>
        {fiche.date_soumission && (
          <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            Soumis le {new Date(fiche.date_soumission).toLocaleDateString('fr-FR')}
          </span>
        )}
        {statut === 'valide' && fiche.validateur && (
          <span style={{ fontSize: '0.78rem', color: '#16a34a' }}>
            Validé par {fiche.validateur.prenom} {fiche.validateur.nom}
            {fiche.date_validation ? ` le ${new Date(fiche.date_validation).toLocaleDateString('fr-FR')}` : ''}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {saveMsg && <span style={{ fontSize: '0.8rem', color: couleurMessage(niveauMessage(saveMsg)) }}>{saveMsg}</span>}
        {/* Boutons d'action */}
        {estModifiable && (
          <button onClick={() => sauvegarder(undefined)} disabled={saving}
            style={{ padding: '0.35rem 0.9rem', borderRadius: 7, border: '1px solid #1a3668',
                     background: '#eff6ff', color: '#1a3668', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
            {saving ? '⏳' : '💾 Sauvegarder'}
          </button>
        )}
        {estModifiable && (
          <button onClick={soumettre} disabled={saving}
            style={{ padding: '0.35rem 0.9rem', borderRadius: 7, border: 'none',
                     background: '#d97706', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem' }}>
            📤 Soumettre
          </button>
        )}
        {peutValider && statut === 'soumis' && (
          <button onClick={valider} disabled={saving}
            style={{ padding: '0.35rem 0.9rem', borderRadius: 7, border: 'none',
                     background: '#16a34a', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem' }}>
            ✅ Valider (niveau 2)
          </button>
        )}
        {hasRole('admin') && statut !== 'brouillon' && (
          <button onClick={reouvrir} disabled={saving}
            style={{ padding: '0.35rem 0.9rem', borderRadius: 7, border: '1px solid #f97316',
                     background: '#fff7ed', color: '#f97316', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
            🔓 Réouvrir
          </button>
        )}
        <button onClick={telechargerPDF} disabled={pdfLoading}
          style={{ padding: '0.35rem 0.9rem', borderRadius: 7, border: '1px solid #1d4ed8',
                   background: '#1d4ed8', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
          {pdfLoading ? '⏳ PDF…' : '⬇ PDF'}
        </button>
      </div>

      {/* Commentaire admin */}
      {fiche.commentaire_admin && (
        <div style={{ padding: '0.7rem 1rem', background: '#fffbeb', border: '1px solid #fde68a',
                      borderRadius: 8, marginBottom: '1rem', fontSize: '0.85rem' }}>
          <strong>💬 Commentaire :</strong> {fiche.commentaire_admin}
        </div>
      )}

      {/* ── Champs d'en-tête ── */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                    borderRadius: 10, padding: '1rem 1.2rem', marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem 1.5rem' }}>
          {/* Date */}
          <div>
            <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>
              DATE
            </label>
            <input type="date"
              value={fiche.date_fiche
                ? String(fiche.date_fiche).substring(0, 10)
                : (session?.date_fin ? String(session.date_fin).substring(0, 10) : '')}
              disabled={!estModifiable}
              onChange={e => setFiche(f => ({ ...f, date_fiche: e.target.value }))}
              style={{ padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)',
                       background: estModifiable ? 'var(--bg)' : 'var(--bg-elevated)',
                       fontSize: '0.88rem', width: '100%' }} />
          </div>
          {/* Formateur */}
          <div>
            <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>
              FORMATEUR
            </label>
            <div style={{ padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)',
                          background: 'var(--bg-elevated)', fontSize: '0.88rem' }}>
              {fiche?.formateur_nom || session.nom_formateur || '—'}
            </div>
          </div>
          {/* Formation */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>
              FORMATION
            </label>
            <div style={{ padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)',
                          background: 'var(--bg-elevated)', fontSize: '0.88rem' }}>
              {session.intitule_formation || '—'} {session.session_id ? `– ${session.session_id}` : ''}
            </div>
          </div>
        </div>

        {/* Cases à cocher + Signature — disposition côte à côte */}
        <div style={{ marginTop: '0.9rem', display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* Gauche : cases à cocher */}
          <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {/* Livret d'accueil */}
            <div style={{ padding: '0.7rem 0.9rem',
                          background: fiche.livret_accueil ? '#f0fdf4' : '#fef2f2',
                          border: `1px solid ${fiche.livret_accueil ? '#bbf7d0' : '#fecaca'}`,
                          borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: estModifiable ? 'pointer' : 'default',
                              fontSize: '0.88rem', fontWeight: 700,
                              color: fiche.livret_accueil ? '#166534' : '#b91c1c' }}>
                <input type="checkbox" checked={!!fiche.livret_accueil} disabled={!estModifiable}
                  onChange={e => setFiche(f => ({ ...f, livret_accueil: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: estModifiable ? 'pointer' : 'default' }} />
                Je reconnais avoir pris connaissance du livret d'accueil formateurs qui m'a été remis
              </label>
            </div>
            {/* Aucune observation */}
            <div style={{ padding: '0.7rem 0.9rem',
                          background: fiche.aucune_observation ? '#eff6ff' : 'var(--bg-elevated)',
                          border: `1px solid ${fiche.aucune_observation ? '#bfdbfe' : 'var(--border)'}`,
                          borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: estModifiable ? 'pointer' : 'default',
                              fontSize: '0.88rem', fontWeight: 600,
                              color: fiche.aucune_observation ? '#1d4ed8' : 'var(--text-primary)' }}>
                <input type="checkbox" checked={!!fiche.aucune_observation} disabled={!estModifiable}
                  onChange={e => setFiche(f => ({ ...f, aucune_observation: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: estModifiable ? 'pointer' : 'default' }} />
                Cocher si aucune observation n'est à formuler
              </label>
            </div>
          </div>

          {/* Droite : signature */}
          <div style={{ flex: '0 1 300px' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 5 }}>
              SIGNATURE FORMATEUR
            </div>
            {fiche.signature_formateur ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 4,
                              background: '#f8faff', display: 'inline-block' }}>
                  <img src={fiche.signature_formateur} alt="signature"
                    style={{ maxHeight: 70, maxWidth: 220, display: 'block' }} />
                </div>
                {estModifiable && !lienSig && (
                  <button onClick={() => { if (estTactile) { setFiche(f => ({ ...f, signature_formateur: null })); setShowSig(true); } else ouvrirQrSignature(); }}
                    disabled={lienSigAttente}
                    style={{ padding: '0.3rem 0.7rem', borderRadius: 6, border: '1px solid #f97316',
                             background: '#fff7ed', color: '#f97316', cursor: 'pointer', fontSize: '0.78rem' }}>
                    {estTactile ? '✏️ Modifier' : '📱 Refaire au téléphone'}
                  </button>
                )}
              </div>
            ) : estModifiable ? (
              showSig ? (
                <SignatureCanvas hauteur={170} labelValider="✓ Valider la signature"
                  onSave={async (b64) => {
                    setFiche(f => ({ ...f, signature_formateur: b64 }));
                    setShowSig(false);
                    await sauvegarder(b64);
                  }}
                  onCancel={() => setShowSig(false)}
                />
              ) : estTactile ? (
                <button onClick={() => setShowSig(true)}
                  style={{ padding: '0.4rem 1rem', borderRadius: 7, border: '2px dashed #1a3668',
                           background: '#f0f5ff', color: '#1a3668', cursor: 'pointer',
                           fontWeight: 600, fontSize: '0.85rem' }}>
                  ✍ Apposer ma signature
                </button>
              ) : !lienSig && (
                <button onClick={ouvrirQrSignature} disabled={lienSigAttente}
                  style={{ padding: '0.4rem 1rem', borderRadius: 7, border: '2px dashed #1a3668',
                           background: '#f0f5ff', color: '#1a3668', cursor: 'pointer',
                           fontWeight: 600, fontSize: '0.85rem' }}>
                  {lienSigAttente ? '⏳ …' : '📱 Signer avec mon téléphone (QR code)'}
                </button>
              )
            ) : (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', fontStyle: 'italic' }}>
                Aucune signature
              </div>
            )}
            {lienSigErreur && <div style={{ color: '#b91c1c', fontSize: '0.8rem', marginTop: 6 }}>{lienSigErreur}</div>}
            {lienSig && (
              <div style={{ marginTop: 8, padding: '0.8rem', background: '#f8faff', borderRadius: 8, border: '1px solid #bfdbfe', textAlign: 'center' }}>
                <img src={lienSig.qr} alt="QR code de signature" width="180" height="180" style={{ display: 'block', margin: '0 auto 6px' }} />
                <div style={{ fontSize: '0.82rem', color: '#334155', lineHeight: 1.4 }}>
                  Scannez ce QR code avec l'appareil photo de votre <strong>téléphone</strong> et signez avec le doigt.
                  La signature apparaîtra ici automatiquement.
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>
                  ⏳ En attente de la signature… valable jusqu'à {new Date(lienSig.expire_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 8 }}>
                  <button onClick={ouvrirQrSignature} disabled={lienSigAttente}
                    style={{ padding: '0.25rem 0.7rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>
                    🔄 Nouveau QR code
                  </button>
                  <button onClick={() => setLienSig(null)}
                    style={{ padding: '0.25rem 0.7rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Tableau des lignes ── */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr>
              {colonnes.map(col => (
                <th key={col.cle} style={{
                  padding: '0.5rem 0.7rem', fontWeight: 700, fontSize: '0.78rem',
                  background: '#1a3668', color: '#fff', textAlign: 'center',
                  border: '1px solid #1a3668',
                  width: `${(col.largeur_pct / colonnes.reduce((s, c) => s + c.largeur_pct, 0) * 100).toFixed(1)}%`
                }}>
                  {col.libelle}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lignes.map(ligne => (
              <tr key={ligne.numero_ligne}>
                {colonnes.map(col => {
                  const val = (ligne.valeurs || {})[col.cle] || '';
                  if (col.type === 'select') {
                    let opts = [];
                    try { opts = JSON.parse(col.options_json || '[]'); } catch {}
                    return (
                      <td key={col.cle} style={{ border: '1px solid #d1d5db', padding: '0.3rem', verticalAlign: 'top' }}>
                        <select value={val} disabled={!estModifiable}
                          onChange={e => setLigneVal(ligne.numero_ligne, col.cle, e.target.value)}
                          style={{ width: '100%', border: 'none', outline: 'none',
                                   background: 'var(--bg-elevated)', fontSize: '0.83rem',
                                   fontFamily: 'inherit', color: 'var(--text-primary)',
                                   cursor: estModifiable ? 'pointer' : 'default', padding: '0.2rem 0' }}>
                          <option value="" style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}>—</option>
                          {opts.map(o => <option key={o} value={o} style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}>{o}</option>)}
                        </select>
                      </td>
                    );
                  }
                  return (
                    <td key={col.cle} style={{ border: '1px solid #d1d5db', padding: '0.3rem', verticalAlign: 'top' }}>
                      <textarea value={val} disabled={!estModifiable}
                        onChange={e => setLigneVal(ligne.numero_ligne, col.cle, e.target.value)}
                        rows={3}
                        style={{ width: '100%', border: 'none', outline: 'none', resize: 'vertical',
                                 background: 'transparent', fontSize: '0.83rem', lineHeight: 1.4,
                                 fontFamily: 'inherit', color: 'var(--text-primary)',
                                 cursor: estModifiable ? 'text' : 'default', minHeight: 60 }}
                        placeholder={estModifiable ? '—' : ''}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

// ── Onglet SMS ────────────────────────────────────────────────────────────────
export function SmsTab({ sessionId, inscriptions }) {
  const [solde,       setSolde]       = useState(null);
  const [historique,  setHistorique]  = useState([]);
  const [senders,     setSenders]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [envoi,       setEnvoi]       = useState(null); // type en cours : 'confirmation' | 'rappel' | 'report' | 'libre' | 'changement'
  const [result,      setResult]      = useState(null);
  const [stagChangementId, setStagChangementId] = useState('');
  const [texteLibre,  setTexteLibre]  = useState('');
  const [modalLibre, setModalLibre] = useState(false);
  const [selectionLibre, setSelectionLibre] = useState(null); // message libre : null = tous les stagiaires, sinon liste d'ids

  const inscritsAvecTel = (inscriptions || []).filter(i => i.stagiaire_tel);
  const nbAvecTel = inscritsAvecTel.length;
  // Message libre : inscrits ACTIFS avec numéro
  const destinatairesLibres = inscritsAvecTel
    .filter(i => !i.statut || i.statut === 'active')
    .map(i => ({ id: String(i.stagiaire_id), nom: i.nom_stagiaire_snapshot || String(i.stagiaire_id) }));
  const idsLibres = (selectionLibre === null ? destinatairesLibres.map(d => d.id) : selectionLibre).filter(id => destinatairesLibres.some(d => d.id === id));
  const compteur = compterSms(texteLibre);

  const charger = React.useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      apiFetch(`/sms/solde`),
      apiFetch(`/sms/session/${sessionId}/historique`),
      apiFetch(`/sms/senders`),
    ]).then(([sr, hr, senr]) => {
      if (sr.status === 'fulfilled') setSolde(sr.value);
      if (hr.status === 'fulfilled') setHistorique(Array.isArray(hr.value) ? hr.value : []);
      if (senr.status === 'fulfilled') setSenders(senr.value);
    }).finally(() => setLoading(false));
  }, [sessionId]);

  useEffect(() => { charger(); }, [charger]);

  // Un seul chemin d'envoi : confirmation, résultat, rechargement de l'historique. Renvoie la réponse du serveur (undefined si échec ou annulé)
  async function lancer(type, confirmation, url, options = { method: 'POST' }) {
    if (!window.confirm(confirmation)) return undefined;
    setEnvoi(type);
    setResult(null);
    try {
      const r = await apiFetch(url, options);
      setResult({ ok: true, ...r });
      charger();
      return r;
    } catch (e) {
      setResult({ ok: false, error: e.message });
      return undefined;
    } finally {
      setEnvoi(null);
    }
  }

  const envoyer = action => lancer(action.type,
    `Envoyer ${action.libelleConfirmation} aux ${nbAvecTel} stagiaire(s) avec un numéro de téléphone ?`,
    `/sms/session/${sessionId}/${action.type}`);

  function envoyerChangement() {
    if (!stagChangementId) return;
    const stag = inscritsAvecTel.find(i => String(i.stagiaire_id) === String(stagChangementId));
    return lancer('changement', `Envoyer un SMS de changement de session à ${stag?.nom_stagiaire_snapshot || stagChangementId} ?`,
      `/sms/session/${sessionId}/changement-session/${stagChangementId}`);
  }

  async function envoyerLibre() {
    const message = texteLibre.trim();
    if (!message || !idsLibres.length) return;
    const r = await lancer('libre', `Envoyer ce SMS à ${idsLibres.length} stagiaire(s) ?\n\n« ${message} »`,
      `/sms/session/${sessionId}/libre`, { method: 'POST', body: JSON.stringify({ message, stagiaire_ids: idsLibres }) });
    if (r) { setTexteLibre(''); setModalLibre(false); }
  }

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement…</div>;

  if (solde && !solde.configured) {
    return (
      <div style={{ padding: '1.5rem', background: 'rgba(245,158,11,0.08)', borderRadius: '10px', border: '1px solid rgba(245,158,11,0.3)', marginTop: '1rem' }}>
        <div style={{ fontWeight: 700, color: '#d97706', marginBottom: '0.5rem' }}>⚠️ SMS non configurés</div>
        <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', margin: 0 }}>
          Les variables d'environnement OVH ne sont pas définies sur le serveur.<br />
          Ajoutez <code>OVH_APP_KEY</code>, <code>OVH_APP_SECRET</code>, <code>OVH_CONSUMER_KEY</code> et <code>OVH_SMS_SERVICE</code> dans <code>backend/.env</code>, puis redémarrez le backend.
        </p>
      </div>
    );
  }

  const pct = solde && solde.creditsHigh ? Math.round(100 * solde.credits / solde.creditsHigh) : null;
  const alerteSolde = pct !== null && pct < 10;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginTop: '0.5rem' }}>
      {/* Solde */}
      {solde && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.9rem 1.2rem',
          background: alerteSolde ? 'rgba(239,68,68,0.08)' : 'var(--bg-elevated)',
          border: `1px solid ${alerteSolde ? 'rgba(239,68,68,0.4)' : 'var(--border)'}`,
          borderRadius: '10px' }}>
          <div style={{ fontSize: '1.5rem' }}>{alerteSolde ? '⚠️' : '📶'}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
              Crédits SMS : <span style={{ color: alerteSolde ? '#ef4444' : 'var(--accent-blue)' }}>{solde.credits}</span>
              {pct !== null && <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: '0.82rem', marginLeft: '0.4rem' }}>({pct} %)</span>}
            </div>
            {alerteSolde && <div style={{ fontSize: '0.82rem', color: '#ef4444', marginTop: '0.2rem' }}>Solde faible — rechargez vos crédits OVH</div>}
          </div>
          <button onClick={charger} style={{ marginLeft: 'auto', padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: '0.8rem' }}>
            ↺ Actualiser
          </button>
        </div>
      )}

      {/* Expéditeur */}
      {senders && senders.configured && (
        <div style={{ padding: '0.7rem 1rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>📤 Expéditeur :</span>
          {senders.senders.length === 0 ? (
            <span style={{ color: '#d97706', fontStyle: 'italic' }}>
              Aucun expéditeur nommé — envoi via numéro court OVH (en attendant validation de votre expéditeur personnalisé)
            </span>
          ) : (
            senders.senders.map(s => (
              <span key={s.sender} style={{ padding: '0.15rem 0.55rem', borderRadius: '12px', fontWeight: 600,
                background: s.status === 'ok' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.1)',
                color: s.status === 'ok' ? 'var(--accent-emerald)' : '#ef4444' }}>
                {s.sender}
                <span style={{ fontWeight: 400, fontSize: '0.78rem', marginLeft: '0.3rem', opacity: 0.75 }}>({s.status})</span>
              </span>
            ))
          )}
        </div>
      )}

      {/* Envois groupés : confirmation, rappel, report */}
      <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
        {ACTIONS_SMS_GROUPE.map(a => (
          <CarteSms key={a.type} {...a} desactive={nbAvecTel === 0} bloque={!!envoi} enCours={envoi === a.type} onClick={() => envoyer(a)}>
            {nbAvecTel} destinataire(s) avec numéro renseigné
          </CarteSms>
        ))}
      </div>

      {/* Changement de session (à gauche, plus étroit) et message libre (à droite, saisie dans une fenêtre) */}
      <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
        <CarteSms titre="↔ Changement de session" largeur={2}
          description="Informe un stagiaire par SMS de son transfert dans cette session."
          couleur="#0891b2" libelleBouton="↔ Envoyer le changement"
          desactive={!stagChangementId} bloque={!!envoi} enCours={envoi === 'changement'} onClick={envoyerChangement}>
          <select value={stagChangementId} onChange={e => setStagChangementId(e.target.value)}
            style={{ width: '100%', padding: '0.4rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.84rem' }}>
            <option value="">— Sélectionner un stagiaire —</option>
            {inscritsAvecTel.map(i => (
              <option key={i.stagiaire_id} value={i.stagiaire_id}>{i.nom_stagiaire_snapshot}</option>
            ))}
          </select>
        </CarteSms>

        <CarteSms titre="✉ Message libre" largeur={3}
          description="Écrivez un SMS à un, plusieurs ou tous les stagiaires de la session (aucun message prédéfini)."
          couleur="#2563eb" libelleBouton="✉ Rédiger un message"
          desactive={!destinatairesLibres.length} bloque={!!envoi} onClick={() => setModalLibre(true)}>
          {destinatairesLibres.length} destinataire(s) possible(s) (inscrits actifs avec numéro)
        </CarteSms>
      </div>

      {modalLibre && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.5rem', width: 560, maxWidth: '95vw' }}>
            <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>✉ Message libre</h3>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Destinataires</div>
            <div style={{ marginBottom: '0.8rem' }}>
              <MenuStagiairesMulti stagiaires={destinatairesLibres} selection={selectionLibre} onChange={setSelectionLibre} />
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Message</div>
            <textarea value={texteLibre} onChange={e => setTexteLibre(e.target.value)} rows={5} maxLength={500} autoFocus
              placeholder="Votre message…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ marginTop: '0.3rem', fontSize: '0.78rem', color: compteur.special ? COULEUR_ATTENTION : 'var(--text-secondary)' }}>
              {compteur.caracteres} / 500 caractère(s) — {compteur.segments} SMS par destinataire
              {compteur.special && ' · caractères spéciaux (ç, â, ê, î, ô, û, œ, « », emoji…) : 70 caractères par SMS au lieu de 160'}
            </div>
            {result && !result.ok && <div style={{ marginTop: '0.8rem' }}><ResultatSms result={result} /></div>}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn-secondary" onClick={() => setModalLibre(false)}>Fermer</button>
              <button className="btn-primary" disabled={!texteLibre.trim() || !idsLibres.length || !!envoi} onClick={envoyerLibre}>
                {envoi === 'libre' ? '⏳ Envoi…' : `✉ Envoyer à ${idsLibres.length} stagiaire(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      <ResultatSms result={result} />
      <HistoriqueSms lignes={historique} />
    </div>
  );
}

// ─── Onglet Suivi de session ──────────────────────────────────────────────────

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

function SuiviTab({ sessionId, session }) {
  const [notes, setNotes] = useState([]);
  const [noteTypes, setNoteTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [cejSalaries, setCejSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | note obj
  const [apercu, setApercu] = useState(null); // note obj pour prévisualisation
  const [apercuFichierUrl, setApercuFichierUrl] = useState(null); // fichier joint chargé avec le jeton

  useEffect(() => {
    if (!apercu?.fichier_path) return;
    let url = null;
    let annule = false;
    const tok = localStorage.getItem('gf_formateur_token') || localStorage.getItem('gf_token');
    fetch(`${API_BASE}/notes/${apercu.id}/fichier`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob && !annule) { url = URL.createObjectURL(blob); setApercuFichierUrl(url); } })
      .catch(() => {});
    return () => { annule = true; if (url) URL.revokeObjectURL(url); setApercuFichierUrl(null); };
  }, [apercu]);
  const [filters, setFilters] = useState({ type_id: '', avec_client: false, client_secondaire_id: '', stagiaire_id: '' });
  const emptyForm = {
    note_type_id: '', titre: '', contenu: '',
    date_note: new Date().toISOString().slice(0, 10),
    avec_client_session: false, contact_client: '',
    client_secondaire_id: '', contact_entreprise_accueil: '',
    formateur_id: '', autres_participants: '',
    stagiaire_ids: [], cej_participants: [],
    fichier: null, fichier_nom: '', fichier_mime: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dragOverScan, setDragOverScan] = useState(false);
  const token = localStorage.getItem('gf_formateur_token') || localStorage.getItem('gf_token');

  const inscriptions = session?.inscriptions || [];
  const stagiairesSession = inscriptions.map(i => ({
    stagiaire_id: i.stagiaire_id,
    nom: i.nom_stagiaire_snapshot || i.stagiaire_id,
  }));

  function loadNotes() {
    const qp = new URLSearchParams();
    if (filters.type_id)              qp.set('type_id', filters.type_id);
    if (filters.avec_client)          qp.set('avec_client', 'true');
    if (filters.client_secondaire_id) qp.set('client_secondaire_id', filters.client_secondaire_id);
    if (filters.stagiaire_id)         qp.set('stagiaire_id', filters.stagiaire_id);
    setLoading(true);
    apiFetch(`/sessions/${sessionId}/notes?${qp}`)
      .then(d => { setNotes(d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(loadNotes, [sessionId, filters]);

  useEffect(() => {
    apiFetch('/note-types').then(d => setNoteTypes(d || [])).catch(() => {});
    apiFetch('/clients?limit=500').then(d => setClients(d?.data || [])).catch(() => {});
    apiFetch('/formateurs?limit=200').then(d => setFormateurs(d?.data || d || [])).catch(() => {});
    apiFetch('/parametres').then(d => {
      try { setCejSalaries(JSON.parse(d?.cej_salaries || '[]')); } catch { setCejSalaries([]); }
    }).catch(() => {});
  }, []);

  const clientPrincipalNom = session?.groupes?.[0]?.nom_client || session?.nom_client || 'Client de la session';

  function openNew() {
    setForm({ ...emptyForm });
    setModal('new');
    setMsg('');
  }

  function openEdit(note) {
    setForm({
      note_type_id: note.note_type_id ? String(note.note_type_id) : '',
      titre: note.titre || '',
      contenu: note.contenu || '',
      date_note: note.date_note ? note.date_note.slice(0, 10) : '',
      avec_client_session: !!note.avec_client_session,
      contact_client: note.contact_client || '',
      client_secondaire_id: note.client_secondaire_id || '',
      contact_entreprise_accueil: note.contact_entreprise_accueil || '',
      formateur_id: note.formateur_id || '',
      autres_participants: note.autres_participants || '',
      stagiaire_ids: (note.stagiaires || []).map(s => s.stagiaire_id),
      cej_participants: (() => { try { return JSON.parse(note.cej_participants || '[]'); } catch { return []; } })(),
      fichier: null,
      fichier_nom: note.fichier_nom || '',
      fichier_mime: note.fichier_mime || '',
    });
    setModal(note);
    setMsg('');
  }

  async function handleSave() {
    if (!form.date_note) { setMsg('❌ La date est requise'); return; }
    setSaving(true); setMsg('');
    try {
      const body = {
        note_type_id: form.note_type_id || null,
        titre: form.titre,
        contenu: form.contenu,
        date_note: form.date_note,
        avec_client_session: form.avec_client_session,
        contact_client: form.contact_client || null,
        client_secondaire_id: form.client_secondaire_id || null,
        contact_entreprise_accueil: form.contact_entreprise_accueil || null,
        formateur_id: form.formateur_id || null,
        autres_participants: form.autres_participants || null,
        stagiaire_ids: form.stagiaire_ids,
        cej_participants: form.cej_participants.length ? JSON.stringify(form.cej_participants) : null,
      };
      let note;
      if (modal === 'new') {
        note = await apiFetch(`/sessions/${sessionId}/notes`, { method: 'POST', body: JSON.stringify(body) });
      } else {
        await apiFetch(`/notes/${modal.id}`, { method: 'PUT', body: JSON.stringify(body) });
        note = { id: modal.id };
      }
      if (form.fichier) {
        const fd = new FormData();
        fd.append('fichier', form.fichier);
        await fetch(`${API_BASE}/notes/${note.id}/fichier`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      }
      setModal(null);
      loadNotes();
    } catch (e) { setMsg('❌ ' + e.message); setSaving(false); }
    finally { setSaving(false); }
  }

  async function handleDelete(note) {
    if (!window.confirm('Supprimer définitivement cette note ?')) return;
    try {
      await apiFetch(`/notes/${note.id}`, { method: 'DELETE' });
      loadNotes();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  function handlePdfMulti(ids) {
    const idsParam = ids && ids.length ? `?ids=${ids.join(',')}` : '';
    ouvrirDocument({ titre: 'Notes de la session', url: `/sessions/${sessionId}/notes/pdf-multi${idsParam}` });
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function buildParticipantsBadges(note, small = true) {
    const sz = small ? '0.72rem' : '0.82rem';
    const badges = [];
    if (note.avec_client_session) {
      const label = note.contact_client ? `${note.contact_client} (${clientPrincipalNom})` : clientPrincipalNom;
      badges.push(<span key="cp" style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6', borderRadius: '4px', padding: '0.1rem 0.45rem', fontSize: sz, whiteSpace: 'nowrap' }}>{label}</span>);
    }
    if (note.client_secondaire_nom) {
      const label = note.contact_entreprise_accueil ? `${note.contact_entreprise_accueil} (${note.client_secondaire_nom})` : note.client_secondaire_nom;
      badges.push(<span key="ea" style={{ background: 'rgba(245,158,11,0.1)', color: '#d97706', borderRadius: '4px', padding: '0.1rem 0.45rem', fontSize: sz, whiteSpace: 'nowrap' }}>{label}</span>);
    }
    if (note.formateur_nom) {
      badges.push(<span key="fm" style={{ background: 'rgba(139,92,246,0.1)', color: '#7c3aed', borderRadius: '4px', padding: '0.1rem 0.45rem', fontSize: sz, whiteSpace: 'nowrap' }}>{note.formateur_nom} (formateur)</span>);
    }
    if (note.autres_participants) {
      badges.push(<span key="ap" style={{ background: 'rgba(100,116,139,0.1)', color: 'var(--text-secondary)', borderRadius: '4px', padding: '0.1rem 0.45rem', fontSize: sz, whiteSpace: 'nowrap' }}>{note.autres_participants}</span>);
    }
    (note.stagiaires || []).forEach(s => {
      badges.push(<span key={s.stagiaire_id} style={{ background: 'rgba(16,185,129,0.08)', color: 'var(--accent-emerald)', borderRadius: '4px', padding: '0.1rem 0.45rem', fontSize: sz, whiteSpace: 'nowrap' }}>{s.nom}</span>);
    });
    return badges;
  }

  const selStyle = {
    padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)',
    background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem',
  };
  const inputModalStyle = {
    width: '100%', padding: '0.45rem 0.7rem', borderRadius: '7px', border: '1px solid var(--border)',
    background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.3rem' };

  return (
    <div style={{ padding: '1rem 0' }}>
      {/* ── Filtres ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1.2rem', padding: '0.8rem 1rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' }}>
        <select value={filters.type_id} onChange={e => setFilters(f => ({ ...f, type_id: e.target.value }))} style={selStyle}>
          <option value="">Tous les types</option>
          {noteTypes.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
        </select>
        <select value={filters.stagiaire_id} onChange={e => setFilters(f => ({ ...f, stagiaire_id: e.target.value }))} style={selStyle}>
          <option value="">Tous les stagiaires</option>
          {stagiairesSession.map(s => <option key={s.stagiaire_id} value={s.stagiaire_id}>{s.nom}</option>)}
        </select>
        <select value={filters.client_secondaire_id} onChange={e => setFilters(f => ({ ...f, client_secondaire_id: e.target.value }))} style={selStyle}>
          <option value="">Tous organismes d'accueil</option>
          {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={filters.avec_client} onChange={e => setFilters(f => ({ ...f, avec_client: e.target.checked }))} />
          Avec client session
        </label>
        <button onClick={() => setFilters({ type_id: '', avec_client: false, client_secondaire_id: '', stagiaire_id: '' })}
          style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '0.82rem' }}>
          Réinitialiser
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {notes.length > 0 && (
            <>
              <button
                onClick={() => handlePdfMulti([...selectedIds])}
                disabled={selectedIds.size === 0}
                style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid #1a3668', background: selectedIds.size > 0 ? '#1a3668' : 'transparent', color: selectedIds.size > 0 ? '#fff' : 'var(--text-tertiary)', cursor: selectedIds.size > 0 ? 'pointer' : 'default', fontSize: '0.82rem', fontWeight: 600, transition: 'all 0.15s' }}>
                ⬇ Sélection ({selectedIds.size})
              </button>
              <button
                onClick={() => handlePdfMulti([])}
                style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.82rem' }}>
                ⬇ Tout imprimer
              </button>
            </>
          )}
          <button className="btn-primary" onClick={openNew} style={{ fontSize: '0.88rem' }}>+ Nouvelle note</button>
        </div>
      </div>

      {/* ── Liste tableau ── */}
      {loading ? <Loading /> : notes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
          Aucune note de suivi pour cette session.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ width: '32px' }}>
                  <input type="checkbox"
                    checked={notes.length > 0 && selectedIds.size === notes.length}
                    onChange={e => setSelectedIds(e.target.checked ? new Set(notes.map(n => n.id)) : new Set())}
                    title="Tout sélectionner / désélectionner" />
                </th>
                <th style={{ width: '170px' }}>Type</th>
                <th style={{ width: '90px' }}>Date</th>
                <th>Objet</th>
                <th>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <span>Participants</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', fontWeight: 400 }}>
                      <span style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6', borderRadius: '3px', padding: '0.05rem 0.35rem', fontSize: '0.68rem' }}>Client</span>
                      <span style={{ background: 'rgba(245,158,11,0.15)', color: '#d97706', borderRadius: '3px', padding: '0.05rem 0.35rem', fontSize: '0.68rem' }}>Organisme</span>
                      <span style={{ background: 'rgba(139,92,246,0.15)', color: '#7c3aed', borderRadius: '3px', padding: '0.05rem 0.35rem', fontSize: '0.68rem' }}>Formateur</span>
                      <span style={{ background: 'rgba(100,116,139,0.15)', color: 'var(--text-secondary)', borderRadius: '3px', padding: '0.05rem 0.35rem', fontSize: '0.68rem' }}>Autres</span>
                      <span style={{ background: 'rgba(16,185,129,0.12)', color: 'var(--accent-emerald)', borderRadius: '3px', padding: '0.05rem 0.35rem', fontSize: '0.68rem' }}>Stagiaires</span>
                    </div>
                  </div>
                </th>
                <th style={{ width: '140px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {notes.map(note => (
                <tr key={note.id} style={{ background: selectedIds.has(note.id) ? 'rgba(26,54,104,0.05)' : undefined }}>
                  <td>
                    <input type="checkbox" checked={selectedIds.has(note.id)} onChange={() => toggleSelect(note.id)} />
                  </td>
                  <td>
                    {note.type_libelle
                      ? <span style={{ background: 'rgba(232,119,34,0.12)', color: '#E87722', borderRadius: '4px', padding: '0.1rem 0.45rem', fontSize: '0.75rem', fontWeight: 600 }}>{note.type_libelle}</span>
                      : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    {note.fichier_path && <span style={{ marginLeft: '0.3rem', fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>📎</span>}
                  </td>
                  <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {note.date_note ? new Date(note.date_note).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td style={{ fontWeight: 500 }}>{note.titre || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {buildParticipantsBadges(note, true)}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'flex-end' }}>
                      <button onClick={() => setApercu(note)}
                        style={{ padding: '0.22rem 0.55rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}>
                        👁 Aperçu
                      </button>
                      <button onClick={() => ouvrirDocument({ titre: note.titre || 'Note', url: `/notes/${note.id}/pdf` })} style={{ padding: '0.22rem 0.55rem', borderRadius: '5px', background: '#1a3668', color: '#fff', border: 'none', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                        PDF
                      </button>
                      <button onClick={() => openEdit(note)}
                        style={{ padding: '0.22rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}>
                        ✏️
                      </button>
                      <button onClick={() => handleDelete(note)}
                        style={{ padding: '0.22rem 0.5rem', borderRadius: '5px', border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal aperçu ── */}
      {apercu && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: '14px', width: '100%', maxWidth: '800px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                {apercu.type_libelle && <span style={{ background: 'rgba(232,119,34,0.12)', color: '#E87722', borderRadius: '5px', padding: '0.12rem 0.6rem', fontSize: '0.8rem', fontWeight: 600 }}>{apercu.type_libelle}</span>}
                <span style={{ fontWeight: 700, fontSize: '1rem' }}>{apercu.titre || '(sans objet)'}</span>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)' }}>{apercu.date_note ? new Date(apercu.date_note).toLocaleDateString('fr-FR') : ''}</span>
              </div>
              <button onClick={() => setApercu(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-tertiary)', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '1.2rem 1.5rem' }}>
              {buildParticipantsBadges(apercu, false).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
                  {buildParticipantsBadges(apercu, false)}
                </div>
              )}
              {apercu.fichier_path && (
                <div style={{ marginBottom: '1rem', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', background: 'var(--bg-elevated)' }}>
                  {apercu.fichier_mime?.startsWith('image/') ? (
                    <img
                      src={apercuFichierUrl || undefined}
                      alt={apercu.fichier_nom}
                      style={{ maxWidth: '100%', display: 'block' }}
                    />
                  ) : (
                    <iframe
                      src={apercuFichierUrl || undefined}
                      title={apercu.fichier_nom}
                      style={{ width: '100%', height: '500px', border: 'none' }}
                    />
                  )}
                </div>
              )}
              {apercu.contenu && (
                <div style={{ fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--text-primary)' }}
                  dangerouslySetInnerHTML={{ __html: apercu.contenu }} />
              )}
              {!apercu.fichier_path && !apercu.contenu && (
                <p style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Aucun contenu renseigné.</p>
              )}
              {apercu.created_by && (
                <div style={{ marginTop: '1rem', fontSize: '0.78rem', color: 'var(--text-tertiary)', borderTop: '1px solid var(--border)', paddingTop: '0.6rem' }}>Rédigé par {apercu.created_by}</div>
              )}
            </div>
            <div style={{ padding: '0.8rem 1.5rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button onClick={() => ouvrirDocument({ titre: apercu.titre || 'Note', url: `/notes/${apercu.id}/pdf` })} style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', background: '#1a3668', color: '#fff', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                ⬇ PDF
              </button>
              <button onClick={() => { setApercu(null); openEdit(apercu); }}
                style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>
                ✏️ Modifier
              </button>
              <button onClick={() => setApercu(null)}
                style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal création / édition ── */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: '14px', width: '100%', maxWidth: '760px', maxHeight: '92vh', overflow: 'auto', padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                {modal === 'new' ? 'Nouvelle note de suivi' : 'Modifier la note'}
              </h3>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'var(--text-tertiary)', lineHeight: 1 }}>×</button>
            </div>

            {msg && (
              <div style={{ padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', marginBottom: '1rem',
                ...styleMessage(niveauMessage(msg)) }}>
                {msg}
              </div>
            )}

            {/* Type + Date */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem', marginBottom: '0.8rem' }}>
              <div>
                <label style={labelStyle}>Type</label>
                <select value={form.note_type_id} onChange={e => setForm(f => ({ ...f, note_type_id: e.target.value }))} style={inputModalStyle}>
                  <option value="">— Choisir un type —</option>
                  {noteTypes.map(t => <option key={t.id} value={String(t.id)}>{t.libelle}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Date *</label>
                <input type="date" value={form.date_note} onChange={e => setForm(f => ({ ...f, date_note: e.target.value }))} style={inputModalStyle} />
              </div>
            </div>

            {/* Objet */}
            <div style={{ marginBottom: '0.8rem' }}>
              <label style={labelStyle}>Objet / Titre</label>
              <input type="text" value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))}
                placeholder="Objet de la réunion, de l'entretien..." style={inputModalStyle} />
            </div>

            {/* Participants */}
            <div style={{ marginBottom: '0.8rem' }}>
              <label style={labelStyle}>Participants</label>
              <div style={{ padding: '0.9rem 1rem', border: '1px solid var(--border)', borderRadius: '9px', background: 'var(--bg-elevated)' }}>

                {/* Client session */}
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Client</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', cursor: 'pointer', marginBottom: '0.4rem' }}>
                  <input type="checkbox" checked={form.avec_client_session}
                    onChange={e => setForm(f => ({ ...f, avec_client_session: e.target.checked, contact_client: e.target.checked ? f.contact_client : '' }))} />
                  <strong>{clientPrincipalNom}</strong>
                </label>
                {form.avec_client_session && (
                  <div style={{ marginLeft: '1.5rem', marginBottom: '0.6rem' }}>
                    <input type="text" value={form.contact_client}
                      onChange={e => setForm(f => ({ ...f, contact_client: e.target.value }))}
                      placeholder="Nom du contact (ex : M. Dupont)" style={{ ...inputModalStyle, fontSize: '0.85rem' }} />
                  </div>
                )}

                {/* Stagiaires */}
                <div style={{ marginBottom: '0.6rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                    <label style={{ ...labelStyle, marginBottom: 0, fontWeight: 500 }}>Stagiaires</label>
                    {stagiairesSession.length > 0 && (
                      <button type="button"
                        onClick={() => setForm(f => ({
                          ...f,
                          stagiaire_ids: f.stagiaire_ids.length === stagiairesSession.length
                            ? []
                            : stagiairesSession.map(s => s.stagiaire_id),
                        }))}
                        style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                        {form.stagiaire_ids.length === stagiairesSession.length ? 'Désélectionner tout' : 'Tout sélectionner'}
                      </button>
                    )}
                  </div>
                  {stagiairesSession.length > 0 ? (
                    <div style={{ maxHeight: '140px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '7px', padding: '0.5rem 0.8rem', background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      {stagiairesSession.map(s => (
                        <label key={s.stagiaire_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                          <input type="checkbox"
                            checked={form.stagiaire_ids.includes(s.stagiaire_id)}
                            onChange={e => setForm(f => ({
                              ...f,
                              stagiaire_ids: e.target.checked
                                ? [...f.stagiaire_ids, s.stagiaire_id]
                                : f.stagiaire_ids.filter(x => x !== s.stagiaire_id),
                            }))} />
                          {s.nom}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Aucun stagiaire inscrit à cette session</div>
                  )}
                </div>

                {/* Formateur */}
                <div style={{ marginBottom: '0.6rem' }}>
                  <label style={{ ...labelStyle, fontWeight: 500 }}>Formateur</label>
                  <select value={form.formateur_id} onChange={e => setForm(f => ({ ...f, formateur_id: e.target.value }))} style={inputModalStyle}>
                    <option value="">— Aucun —</option>
                    {formateurs.map(f => <option key={f.formateur_id} value={f.formateur_id}>{f.nom_formateur}</option>)}
                  </select>
                </div>

                {/* CEJ Concept */}
                {cejSalaries.length > 0 && (
                  <div style={{ marginBottom: '0.6rem' }}>
                    <label style={{ ...labelStyle, fontWeight: 500 }}>CEJ Concept</label>
                    <div style={{ border: '1px solid var(--border)', borderRadius: '7px', padding: '0.5rem 0.8rem', background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      {cejSalaries.map((s, i) => {
                        const fullName = [s.prenom, s.nom].filter(Boolean).join(' ');
                        return (
                          <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                            <input type="checkbox"
                              checked={form.cej_participants.includes(fullName)}
                              onChange={e => setForm(f => ({
                                ...f,
                                cej_participants: e.target.checked
                                  ? [...f.cej_participants, fullName]
                                  : f.cej_participants.filter(x => x !== fullName),
                              }))} />
                            {fullName}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Autres participants */}
                <div style={{ marginBottom: '0.6rem' }}>
                  <label style={{ ...labelStyle, fontWeight: 500 }}>Autres participants</label>
                  <input type="text" value={form.autres_participants}
                    onChange={e => setForm(f => ({ ...f, autres_participants: e.target.value }))}
                    placeholder="Ex : M. DUPONT (entreprise) - Mme MARTIN (conseillère)" style={inputModalStyle} />
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.25rem' }}>Indiquer le poste et/ou la fonction entre parenthèses</div>
                </div>

              </div>
            </div>

            {/* Document scanné — drag & drop */}
            <div style={{ marginBottom: '0.8rem', padding: '0.9rem 1rem', border: '1px solid var(--border)', borderRadius: '9px', background: 'var(--bg-elevated)' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>Document scanné (optionnel)</div>
              {form.fichier_nom && !form.fichier && (
                <div style={{ fontSize: '0.82rem', color: 'var(--accent-emerald)', marginBottom: '0.5rem' }}>
                  📎 Fichier existant : <strong>{form.fichier_nom}</strong>
                  <button type="button" onClick={() => setForm(f => ({ ...f, fichier_nom: '', fichier_mime: '' }))}
                    style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>supprimer</button>
                </div>
              )}
              <div
                onDragOver={e => { e.preventDefault(); setDragOverScan(true); }}
                onDragLeave={() => setDragOverScan(false)}
                onDrop={e => {
                  e.preventDefault(); setDragOverScan(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) setForm(f => ({ ...f, fichier: file, fichier_nom: file.name, fichier_mime: file.type }));
                }}
                onClick={() => document.getElementById('scan-input-' + sessionId)?.click()}
                style={{
                  border: `2px dashed ${dragOverScan ? '#E87722' : form.fichier ? 'var(--accent-emerald)' : 'var(--border)'}`,
                  borderRadius: '8px', padding: '1.2rem 1rem', textAlign: 'center', cursor: 'pointer',
                  background: dragOverScan ? 'rgba(232,119,34,0.05)' : form.fichier ? 'rgba(16,185,129,0.04)' : 'var(--bg-surface)',
                  color: 'var(--text-secondary)', fontSize: '0.85rem', transition: 'all 0.15s',
                }}>
                <input id={'scan-input-' + sessionId} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) setForm(f => ({ ...f, fichier: file, fichier_nom: file.name, fichier_mime: file.type }));
                  }} />
                {form.fichier
                  ? <><div style={{ fontSize: '1.3rem' }}>✅</div><div style={{ marginTop: '0.25rem', color: 'var(--accent-emerald)', fontWeight: 600 }}>{form.fichier.name}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.15rem' }}>Cliquer pour changer</div></>
                  : <><div style={{ fontSize: '1.5rem' }}>📎</div><div style={{ marginTop: '0.25rem' }}>Glissez un fichier ici ou <span style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}>parcourir</span></div><div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.15rem' }}>PDF ou image · 50 Mo max</div></>}
              </div>
            </div>

            {/* Contenu texte */}
            <div style={{ marginBottom: '1.3rem' }}>
              <label style={labelStyle}>Contenu (notes, compte rendu...)</label>
              <ReactQuill
                value={form.contenu}
                onChange={val => setForm(f => ({ ...f, contenu: val }))}
                theme="snow"
                modules={QUILL_MODULES}
                style={{ borderRadius: '7px', fontSize: '0.9rem' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.7rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)}
                style={{ padding: '0.5rem 1.2rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.88rem' }}>
                Annuler
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={saving || !form.date_note}>
                {saving ? '⏳ Enregistrement...' : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Réclamations liées à cette session ── */}
      <ReclamationsSessionBlock sessionId={sessionId} />
    </div>
  );
}

function ReclamationsSessionBlock({ sessionId }) {
  const navigate = useNavigate();
  const [recs, setRecs] = useState([]);
  useEffect(() => {
    apiFetch(`/reclamations?session_id=${sessionId}`)
      .then(d => setRecs(d || [])).catch(() => {});
  }, [sessionId]);

  async function handleNew() {
    try {
      const rec = await apiFetch('/reclamations', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId, date_reclamation: new Date().toISOString().slice(0, 10) }),
      });
      navigate(`/reclamations/${rec.id}`);
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  const STATUT_COLORS = { recue: { bg: '#fef3c7', color: '#92400e', label: 'Reçue' }, en_cours: { bg: '#dbeafe', color: '#1e40af', label: 'En cours' }, resolue: { bg: '#d1fae5', color: '#065f46', label: 'Résolue' }, cloturee: { bg: '#f1f5f9', color: '#64748b', label: 'Clôturée' } };

  return (
    <div style={{ marginTop: '2rem', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{ padding: '0.6rem 1rem', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)' }}>
          Réclamations liées à cette session {recs.length > 0 && <span style={{ background: '#ef4444', color: '#fff', borderRadius: '10px', padding: '0.1rem 0.45rem', fontSize: '0.72rem', marginLeft: '0.3rem' }}>{recs.length}</span>}
        </span>
        <button onClick={handleNew} style={{ padding: '0.3rem 0.8rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.78rem' }}>
          + Nouvelle réclamation
        </button>
      </div>
      {recs.length === 0 ? (
        <div style={{ padding: '1rem', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Aucune réclamation pour cette session.</div>
      ) : (
        <div style={{ padding: '0.5rem 0' }}>
          {recs.map(r => {
            const s = STATUT_COLORS[r.statut] || { bg: '#f1f5f9', color: '#64748b', label: r.statut };
            return (
              <div key={r.id} onClick={() => navigate(`/reclamations/${r.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 1rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ fontWeight: 700, color: 'var(--accent-blue)', minWidth: 110 }}>{r.reference}</span>
                <span style={{ padding: '0.1rem 0.5rem', borderRadius: '8px', fontSize: '0.72rem', fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>
                <span style={{ color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.objet || '—'}</span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>{r.date_reclamation ? new Date(r.date_reclamation).toLocaleDateString('fr-FR') : ''}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PanneauCloture({ sessionId, session, onCloture }) {
  const { hasRole } = useAuth();
  const [check, setCheck]       = React.useState(null);
  const [loading, setLoading]   = React.useState(false);
  const [cloturing, setCloturing] = React.useState(false);
  const [msg, setMsg]           = React.useState('');

  if (!hasRole('utilisateur')) return null;
  if (session?.annulee || session?.reportee) return null;

  const today = new Date().toISOString().slice(0, 10);
  const fin   = (session?.date_fin || '').slice(0, 10);
  if (fin >= today) return null; // Session pas encore terminée

  if (session?.terminee) {
    return (
      <div style={{ marginTop: '1rem', padding: '0.8rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', fontSize: '0.85rem', color: '#15803d' }}>
        ✅ Session clôturée le {session.terminee_at ? new Date(session.terminee_at).toLocaleDateString('fr-FR') : '—'}
      </div>
    );
  }

  async function verifier() {
    setLoading(true); setMsg(''); setCheck(null);
    try {
      const r = await apiFetch(`/sessions/${sessionId}/cloture-check`);
      setCheck(r);
    } catch (e) { setMsg('Erreur : ' + e.message); }
    finally { setLoading(false); }
  }

  async function cloturer(force = false) {
    if (!window.confirm(force
      ? 'Clôturer malgré les points incomplets ? La session sera marquée comme terminée (sans archivage).'
      : 'Clôturer la session ? Elle sera marquée comme terminée et archivée automatiquement.')) return;
    setCloturing(true); setMsg('');
    try {
      await apiFetch(`/sessions/${sessionId}/cloturer`, { method: 'POST', body: JSON.stringify({ force }) });
      setMsg('✅ Session clôturée avec succès.');
      onCloture && onCloture();
    } catch (e) {
      if (e.checks) { setCheck({ ok: false, checks: e.checks }); setMsg('Des points restent incomplets.'); }
      else setMsg('Erreur : ' + e.message);
    } finally { setCloturing(false); }
  }

  const LABELS = {
    emargements:       { icon: '📋', label: 'Émargements' },
    fiche_progres:     { icon: '📊', label: 'Fiche de progrès' },
    questionnaires:    { icon: '📝', label: 'Questionnaires' },
    documents_envoyes: { icon: '📧', label: 'Documents envoyés' },
    facturee:          { icon: '💶', label: 'Facturé' },
  };

  return (
    <div style={{ marginTop: '1rem', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem 1.2rem', background: 'var(--bg-elevated)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
        <h4 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '0.9rem' }}>Clôture de la session</h4>
        <button className="btn-secondary" onClick={verifier} disabled={loading} style={{ fontSize: '0.8rem' }}>
          {loading ? '⏳ Vérification...' : '🔍 Vérifier'}
        </button>
      </div>

      {check && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.9rem' }}>
          {Object.entries(check.checks).map(([key, v]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.83rem' }}>
              <span style={{ color: v.ok ? '#16a34a' : '#dc2626', fontSize: '1rem' }}>{v.ok ? '✓' : '✗'}</span>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{LABELS[key]?.icon} {LABELS[key]?.label}</span>
              <span style={{ color: 'var(--text-secondary)' }}>— {v.detail}</span>
            </div>
          ))}
        </div>
      )}

      {msg && <p style={{ fontSize: '0.82rem', color: couleurMessage(niveauMessage(msg)), marginBottom: '0.7rem' }}>{msg}</p>}

      {check && (
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          {check.ok && (
            <button className="btn-primary" onClick={() => cloturer(false)} disabled={cloturing} style={{ fontSize: '0.85rem' }}>
              {cloturing ? '⏳ Clôture...' : '⏹ Clôturer la session'}
            </button>
          )}
          {!check.ok && (
            <button className="btn-secondary" onClick={() => cloturer(true)} disabled={cloturing}
              style={{ fontSize: '0.82rem', color: '#d97706', borderColor: '#d97706' }}>
              {cloturing ? '⏳...' : '⚠ Forcer la clôture'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Envoi documents par groupe ──────────────────────────────────────────────
function EnvoiDocumentsParGroupe({ sessionId, session }) {
  const { token } = useAuth();
  const API = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

  const [info, setInfo] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(null);
  // emailOverrides: { [sous_session_id]: string }
  const [emailOverrides, setEmailOverrides] = React.useState({});
  // docsSelection: { [sous_session_id]: Set<string> }
  const [docsSelection, setDocsSelection] = React.useState({});
  // sendingState: { [sous_session_id]: 'idle'|'sending'|'ok'|'error' }
  const [sendingState, setSendingState] = React.useState({});
  const [sendMsg, setSendMsg] = React.useState({});

  const groupes = session?.groupes || [];

  async function charger() {
    if (info || loading) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/sessions/${sessionId}/docs-envoi-groupes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      setInfo(d);
      // init sélection par défaut : tout coché
      const sel = {};
      (d.groupes || []).forEach(g => {
        const docs = new Set(['emargement', 'certificat']);
        g.types_docs_dispos.forEach(t => docs.add(t.abrege));
        sel[g.sous_session_id] = docs;
      });
      setDocsSelection(sel);
      if (d.groupes?.length) setActiveTab(d.groupes[0].sous_session_id);
    } catch {}
    setLoading(false);
  }

  function toggleDoc(sous_session_id, doc) {
    setDocsSelection(prev => {
      const set = new Set(prev[sous_session_id] || []);
      if (set.has(doc)) set.delete(doc); else set.add(doc);
      return { ...prev, [sous_session_id]: set };
    });
  }

  async function envoyer(g) {
    const email = (emailOverrides[g.sous_session_id] ?? g.email_effectif ?? '').trim();
    if (!email) { setSendMsg(m => ({ ...m, [g.sous_session_id]: { type: 'error', text: 'Email requis' } })); return; }
    const docs = [...(docsSelection[g.sous_session_id] || [])];
    if (!docs.length) { setSendMsg(m => ({ ...m, [g.sous_session_id]: { type: 'error', text: 'Sélectionnez au moins un document' } })); return; }

    setSendingState(s => ({ ...s, [g.sous_session_id]: 'sending' }));
    setSendMsg(m => ({ ...m, [g.sous_session_id]: null }));
    try {
      const r = await fetch(`${API}/sessions/${sessionId}/groupes/${g.sous_session_id}/envoyer-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, docs }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `Erreur ${r.status}`);
      setSendingState(s => ({ ...s, [g.sous_session_id]: 'ok' }));
      const averts = d.avertissements?.length ? ` — ⚠ ${d.avertissements.join(', ')}` : '';
      setSendMsg(m => ({ ...m, [g.sous_session_id]: { type: 'ok', text: `✓ Envoyé (${d.nb_docs} doc${d.nb_docs > 1 ? 's' : ''})${averts}` } }));
      // Refresh info
      setInfo(null);
      setTimeout(() => charger(), 500);
    } catch (e) {
      setSendingState(s => ({ ...s, [g.sous_session_id]: 'idle' }));
      setSendMsg(m => ({ ...m, [g.sous_session_id]: { type: 'error', text: '✗ ' + e.message } }));
    }
  }

  // Chargé dès qu'il y a au moins un groupe (y compris si le 1er groupe est ajouté après coup)
  React.useEffect(() => { if (groupes.length) charger(); }, [groupes.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const cardStyle = { marginTop: '1.5rem', padding: '1rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' };
  const tabBtnStyle = (active, statut) => ({
    flex: 1, padding: '0.5rem 0.6rem', borderRadius: '8px',
    background: active ? 'var(--accent-blue)' : statut === 'ok' ? 'rgba(16,185,129,0.12)' : 'var(--bg-surface)',
    color: active ? '#fff' : statut === 'ok' ? 'var(--accent-emerald)' : 'var(--text-primary)',
    cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', transition: 'background 0.15s',
    border: statut === 'ok' && !active ? '1px solid var(--accent-emerald)' : '1px solid var(--border)',
  });

  // Après tous les hooks : un return anticipé avant un hook fait planter React si le nombre de groupes passe de 0 à 1
  if (!groupes.length) return null;

  if (loading) return (
    <div style={cardStyle}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.6rem' }}>📧 Envoi documents par groupe</div>
      <span style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>Chargement…</span>
    </div>
  );

  const infoGroupes = info?.groupes || [];
  const activeGroupe = infoGroupes.find(g => g.sous_session_id === activeTab);

  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.8rem' }}>📧 Envoi documents par groupe</div>

      {/* Onglets groupes */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {infoGroupes.map(g => {
          const statut = g.dernier_envoi ? 'ok' : 'pending';
          const isActive = activeTab === g.sous_session_id;
          return (
            <button key={g.sous_session_id} onClick={() => setActiveTab(g.sous_session_id)}
              style={tabBtnStyle(isActive, statut)}>
              <div>{g.groupe} — {g.nom_client || '—'}</div>
              <div style={{ fontSize: '0.7rem', fontWeight: 400, marginTop: '0.15rem', opacity: 0.85 }}>
                {g.dernier_envoi
                  ? `✓ Envoyé le ${new Date(g.dernier_envoi.envoye_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                  : (sendingState[g.sous_session_id] === 'ok' ? '✓ Envoyé' : '[Envoyer]')
                }
              </div>
            </button>
          );
        })}
      </div>

      {/* Contenu de l'onglet actif */}
      {activeGroupe && (() => {
        const g = activeGroupe;
        const emailVal = emailOverrides[g.sous_session_id] ?? g.email_effectif ?? '';
        const emailManquant = !emailVal.trim();
        const sel = docsSelection[g.sous_session_id] || new Set();
        const isSending = sendingState[g.sous_session_id] === 'sending';
        const msg = sendMsg[g.sous_session_id];

        // Docs avec alerte incomplétude
        const docsAttest = g.types_docs_dispos || [];
        const hasIncomplete = docsAttest.some(t => sel.has(t.abrege) && !t.complet);

        return (
          <div style={{ padding: '0.8rem 1rem', background: 'var(--bg-surface)', borderRadius: '8px', border: '1px solid var(--border)' }}>
            {/* Email */}
            <div style={{ marginBottom: '0.8rem' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                {emailManquant ? '⚠ Email manquant — saisir manuellement' : '📧 Email(s) destinataire(s)'}
              </label>
              <input type="text"
                value={emailVal}
                onChange={e => setEmailOverrides(ev => ({ ...ev, [g.sous_session_id]: e.target.value }))}
                placeholder="email@client.fr ; autre@client.fr"
                style={{
                  width: '100%', padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.85rem',
                  border: `1px solid ${emailManquant ? '#f97316' : 'var(--border)'}`,
                  background: 'var(--bg-surface)', color: 'var(--text-primary)',
                }} />
              {emailVal.includes(';') && (
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.2rem' }}>
                  {emailVal.split(';').map(e => e.trim()).filter(Boolean).length} destinataire(s)
                </div>
              )}
            </div>

            {/* Documents à envoyer */}
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Documents à envoyer :</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.8rem' }}>
              {[
                { key: 'emargement', label: 'Feuille d\'émargement journalière', always: true },
                { key: 'certificat', label: 'Certificat de réalisation', always: true },
                ...docsAttest.map(t => ({
                  key: t.abrege,
                  label: `${t.type_document} (${t.abrege})`,
                  nb: t.nb_docs, total: t.nb_stagiaires, complet: t.complet,
                }))
              ].map(doc => (
                <label key={doc.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.83rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={sel.has(doc.key)}
                    onChange={() => toggleDoc(g.sous_session_id, doc.key)}
                    style={{ width: 14, height: 14, accentColor: 'var(--accent-blue)', cursor: 'pointer' }} />
                  <span style={{ color: 'var(--text-primary)' }}>{doc.label}</span>
                  {doc.nb !== undefined && (
                    <span style={{ fontSize: '0.72rem', color: doc.complet ? 'var(--accent-emerald)' : '#f97316', fontWeight: 600 }}>
                      — {doc.complet ? `${doc.nb} stagiaire${doc.nb > 1 ? 's' : ''} ✓` : `⚠ ${doc.nb}/${doc.total} doc${doc.nb > 1 ? 's' : ''}`}
                    </span>
                  )}
                </label>
              ))}
            </div>

            {/* Alerte docs incomplets */}
            {hasIncomplete && (
              <div style={{ padding: '0.45rem 0.7rem', borderRadius: '6px', background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.3)', fontSize: '0.78rem', color: '#ea580c', marginBottom: '0.7rem' }}>
                ⚠ Certains stagiaires n'ont pas encore leur attestation dans leur fiche — les documents disponibles seront envoyés.
              </div>
            )}

            {/* Bouton envoyer */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
              <button
                disabled={isSending || !sel.size}
                onClick={() => envoyer(g)}
                style={{
                  padding: '0.5rem 1.2rem', borderRadius: '8px', border: 'none',
                  background: isSending || !sel.size ? 'var(--bg-elevated)' : '#1a3668',
                  color: isSending || !sel.size ? 'var(--text-tertiary)' : '#fff',
                  fontWeight: 700, fontSize: '0.85rem', cursor: isSending || !sel.size ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}>
                {isSending ? '⏳ Envoi en cours…' : '📧 Envoyer à ce groupe'}
              </button>
              {msg && (
                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: couleurMessage(msg.type === 'ok') }}>
                  {msg.text}
                </span>
              )}
            </div>

            {/* Historique dernier envoi */}
            {g.dernier_envoi && (
              <div style={{ marginTop: '0.6rem', fontSize: '0.74rem', color: 'var(--text-tertiary)' }}>
                Dernier envoi : {new Date(g.dernier_envoi.envoye_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} → {g.dernier_envoi.email_envoye}
                {Array.isArray(g.dernier_envoi.documents) && g.dernier_envoi.documents.length > 0 && (
                  <span> ({g.dernier_envoi.documents.join(', ')})</span>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole, canAccessOnglet, isFormateur, formateurSessionId } = useAuth();
  const { data, loading, reload } = useApi(`/sessions/${id}`);
  const { data: paramsData } = useApi('/parametres');
  const typesQ = React.useMemo(() => {
    if (!paramsData?.types_questionnaire) return TYPES_Q_DEFAULT;
    try { return JSON.parse(paramsData.types_questionnaire); } catch { return TYPES_Q_DEFAULT; }
  }, [paramsData]);
  const [activeTab, setActiveTab] = useState(isFormateur ? 'questionnaires' : 'inscrits');
  const [convocEmail, setConvocEmail] = useState('');
  const [convocClientId, setConvocClientId] = useState('');
  const [sendingConvoc, setSendingConvoc] = useState(false);
  const [convocMsg, setConvocMsg] = useState(null);
  const [showDocsSessions, setShowDocsSessions] = useState(false);
  const [modalModif, setModalModif] = useState(false);
  const [modalGroupe, setModalGroupe] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [adjSessions, setAdjSessions] = useState({ prev: null, next: null });

  useEffect(() => {
    if (!id) return;
    apiFetch(`/sessions/${id}/adjacent`)
      .then(d => setAdjSessions(d))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    apiFetch(`/sessions/${id}/documents?categorie=session`)
      .then(d => { if (Array.isArray(d) && d.length > 0) setShowDocsSessions(true); })
      .catch(() => {});
  }, [id]);

  if (loading || !data) return <Loading />;

  // Formateur : vérifier qu'il accède bien à sa session
  if (isFormateur && formateurSessionId && id !== formateurSessionId) {
    return <Navigate to={`/sessions/${formateurSessionId}`} replace />;
  }

  const allTabs = [
    { key: 'inscrits',      label: `👥 Inscrits (${data.inscriptions?.length || 0})`,    ongletKey: 'inscrits' },
    { key: 'financement',   label: '💰 Financement',                                     ongletKey: 'financement' },
    { key: 'devis',         label: '📄 Documents clients',                               ongletKey: 'documents_clients' },
    ...((() => {
      const hasMain = !!data.formateur_id;   // interne : accès à la session ; non interne : bon de commande puis accès
      const hasExtModule = (data.modules || []).some(m => m.formateur_id && m.formateur_type !== 'interne' && m.formateur_id !== data.formateur_id);
      return (hasMain || hasExtModule) ? [{ key: 'commande', label: '🖊️ Documents intervenants', ongletKey: 'documents_formateurs' }] : [];
    })()),
    { key: 'documents',     label: '🗂️ Documents stagiaires',                            ongletKey: 'documents_stagiaires' },
    { key: 'suivi',          label: '📋 Suivi',                                           ongletKey: 'suivi' },
    { key: 'questionnaires',label: '📊 Questionnaires & Émargement',                        ongletKey: 'questionnaires' },
    { key: 'resultats',     label: '🏆 Résultats',                                       ongletKey: 'resultats' },
    { key: 'parcours',      label: `📚 Parcours${data.modules?.length ? ` (${data.modules.length})` : ''}`, ongletKey: 'inscrits' },

    { key: 'sms',           label: '💬 SMS',                                            ongletKey: 'inscrits' },
  ];

  // Formateur : uniquement les 3 onglets autorisés
  const FORMATEUR_TABS = ['questionnaires', 'resultats'];
  // Filtrer les onglets masqués par le RBAC (ou par le rôle formateur)
  const tabs = allTabs.filter(t =>
    isFormateur
      ? FORMATEUR_TABS.includes(t.key)
      : canAccessOnglet('sessions', t.ongletKey)
  );

  const occurrenceCols = [
    { header: 'Date', render: r => formatDate(r.date) },
    { header: 'Créneau', key: 'slot' },
    { header: 'Début', key: 'heure_debut' },
    { header: 'Fin', key: 'heure_fin' },
    { header: 'Durée', render: r => `${r.duree_heures}h` },
    { header: 'Sous-session', key: 'sous_session_id' },
    { header: 'Type', key: 'type_occurrence' },
  ];

  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();

  const presenceCols = [
    { header: 'Date', render: r => formatDate(r.date) },
    { header: 'Créneau', key: 'slot' },
    { header: 'Stagiaire', key: 'stagiaire_id' },
    { header: 'Début', key: 'heure_debut' },
    { header: 'Fin', key: 'heure_fin' },
    { header: 'Statut', render: r => {
      const dateP = r.date_iso || String(r.date||'').substring(0,10);
      if (dateP > todayStr) return <Badge style={{ background: '#f1f5f9', color: '#94a3b8' }}>Planifié</Badge>;
      return <Badge>{r.statut}</Badge>;
    }},
    { header: 'Sous-session', key: 'sous_session_id' },
  ];

  const tabData = {
    occurrences: { columns: occurrenceCols, data: data.occurrences },
    presences: { columns: presenceCols, data: data.presences },
  };

  const presencesEcoulees = data.presences?.filter(p => (p.date_iso || String(p.date||'').substring(0,10)) <= todayStr) || [];
  const nbPresent = presencesEcoulees.filter(p => p.statut === 'Présent').length;
  const nbAbsent = presencesEcoulees.filter(p => p.statut === 'Absent').length;
  const tauxPresence = presencesEcoulees.length ? Math.round(100 * nbPresent / presencesEcoulees.length) : 0;

  return (
    <div className="page">
      <PageHeader
        title={<span style={{ color: '#E87722', fontWeight: 800 }}>{data.session_id}</span>}
        subtitle={
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span>{data.intitule_formation}</span>
            {data.salle_id && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: data.salle_couleur || '#6b7280', flexShrink: 0 }} />
                {data.nom_salle}
              </span>
            )}
          </span>
        }
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <BackButton />
            {!isFormateur && (
              <>
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/sessions/${adjSessions.prev}`)}
                  disabled={!adjSessions.prev}
                  title={adjSessions.prev ? `Session ${adjSessions.prev}` : 'Première session'}
                  style={{ opacity: adjSessions.prev ? 1 : 0.35 }}>
                  ◀ Précédent
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/sessions/${adjSessions.next}`)}
                  disabled={!adjSessions.next}
                  title={adjSessions.next ? `Session ${adjSessions.next}` : 'Dernière session'}
                  style={{ opacity: adjSessions.next ? 1 : 0.35 }}>
                  Suivant ▶
                </button>
                <button className="btn-secondary" onClick={() => navigate('/calendrier')} title="Voir le planning">
                  📅 Planning
                </button>
              </>
            )}
            {hasRole('utilisateur') && !isFormateur && (
              <button
                onClick={() => navigate(`/prefacturations?session_id=${id}`)}
                style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', background: 'rgba(16,185,129,0.15)', color: 'var(--accent-emerald)', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
                💶 Facturer
              </button>
            )}
            {hasRole('utilisateur') && !isFormateur && (
              <>
                {!data.actif && (
                  <span style={{ padding: '0.3rem 0.8rem', borderRadius: '20px', background: 'rgba(239,68,68,0.12)', color: '#f87171', fontSize: '0.8rem', fontWeight: 600 }}>ARCHIVÉE</span>
                )}
                <button className="btn-secondary" onClick={() => setModalModif(true)}>✏ Modifier</button>
                {data.actif !== false ? (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Archiver la session "${data.session_id}" ?\nElle restera accessible et pourra être restaurée.`)) return;
                      setDeleting(true);
                      try {
                        await apiFetch(`/sessions/${id}`, { method: 'DELETE' });
                        navigate('/sessions');
                      } catch (e) { alert(`Erreur : ${e.message}`); setDeleting(false); }
                    }}
                    disabled={deleting}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.5)', background: 'transparent', color: '#f87171', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    {deleting ? '⏳...' : '🗑 Archiver'}
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      setDeleting(true);
                      try {
                        await apiFetch(`/sessions/${id}/restaurer`, { method: 'POST' });
                        reload();
                      } catch (e) { alert(`Erreur : ${e.message}`); setDeleting(false); }
                    }}
                    disabled={deleting}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid rgba(52,211,153,0.5)', background: 'transparent', color: 'var(--accent-emerald)', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    {deleting ? '⏳...' : '↩ Restaurer'}
                  </button>
                )}
              </>
            )}
          </div>
        }
      />

      <div className="detail-grid">
        <div className="card" style={data.salle_couleur ? { borderLeft: `4px solid ${data.salle_couleur}` } : {}}>
          <h3 className="card-title">Informations</h3>
          <div className="info-grid">
            <div className="info-row"><label>Formation</label><span>{data.intitule_formation}</span></div>
            <div className="info-row"><label>Dates</label><span>{formatDate(data.date_debut)} → {formatDate(data.date_fin)}</span></div>
            <div className="info-row"><label>Durée</label><span>{data.duree}h</span></div>
            <div className="info-row"><label>Horaires</label><span>{data.horaires || '—'}</span></div>
            <div className="info-row"><label>Type</label>
              <span>{data.type_session
                ? <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 600,
                    background: data.type_session === 'Inter' ? 'rgba(59,130,246,0.15)' : 'rgba(16,185,129,0.15)',
                    color: data.type_session === 'Inter' ? '#3b82f6' : '#10b981' }}>{data.type_session}</span>
                : <em style={{color:'var(--text-tertiary)'}}>Non renseigné</em>}
              </span>
            </div>
            <div className="info-row"><label>Lieu</label><span>{data.lieu || <em style={{color:'var(--text-tertiary)'}}>Non renseigné</em>}</span></div>
            <div className="info-row"><label>Salle</label>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {data.salle_id ? (
                  <><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: data.salle_couleur || '#6b7280', flexShrink: 0 }} />{data.nom_salle || data.salle_id}</>
                ) : <em style={{color:'var(--text-tertiary)'}}>Non assignée</em>}
              </span>
            </div>
            <div className="info-row"><label>Formateur</label><span>{data.nom_formateur || '—'}</span></div>
            <div className="info-row"><label>Occurrences</label><span>{data.occurrence_count}</span></div>
          </div>
        </div>
        <div className="card">
          <h3 className="card-title">Statut & Présences</h3>
          <div className="info-grid">
            <div className="info-row"><label>Statut</label>
              {data.annulee ? <Badge variant="badge-danger">Annulée</Badge> :
               data.reportee ? <Badge variant="badge-warning">Reportée</Badge> :
               data.terminee ? <Badge variant="badge-success">Clôturée</Badge> :
               (() => {
                 const today = new Date().toISOString().slice(0, 10);
                 const debut = (data.date_debut || '').slice(0, 10);
                 const fin = (data.date_fin || '').slice(0, 10);
                 if (debut > today) return <Badge variant="badge-info">À venir</Badge>;
                 if (fin >= today) return <Badge variant="badge-warning">En cours</Badge>;
                 return <Badge variant="badge-warning">À clôturer</Badge>;
               })()}
            </div>
            <div className="info-row"><label>Groupes</label><span>{data.groupes?.length || 0}</span></div>
            <div className="info-row"><label>Inscrits</label><span>{data.inscriptions?.length || 0}</span></div>
            <div className="info-row"><label>Taux présence</label>
              <span className="presence-rate">
                <span className="presence-bar" style={{width: `${tauxPresence}%`}} />
                <span className="presence-text">{tauxPresence}% ({nbPresent}P / {nbAbsent}A)</span>
              </span>
            </div>
          </div>
          {data.dates_occurrences && (
            <>
              <h3 className="card-title" style={{marginTop: '1rem'}}>Dates</h3>
              <p className="notes-text">{data.dates_occurrences}</p>
            </>
          )}
          {data.notes && (
            <>
              <h3 className="card-title" style={{marginTop: '1rem'}}>Notes</h3>
              <p className="notes-text" style={{ whiteSpace: 'pre-wrap' }}>{data.notes}</p>
            </>
          )}
        </div>
      </div>

      <PanneauCloture sessionId={id} session={data} onCloture={reload} />

      {/* ── Documents de session ─────────────────────────────────────────── */}
      <div style={{ marginTop: '1rem', padding: '0.85rem 1.1rem', background: showDocsSessions ? 'rgba(245,158,11,0.06)' : 'var(--bg-elevated)', borderRadius: '10px', border: `1px solid ${showDocsSessions ? 'rgba(245,158,11,0.35)' : 'var(--border)'}`, transition: 'all 0.2s' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer', fontSize: '0.88rem' }}>
          <input type="checkbox" checked={showDocsSessions} style={{ marginTop: '0.2rem' }}
            onChange={e => setShowDocsSessions(e.target.checked)} />
          <span>
            <strong>Documents sessions</strong>
            <br />
            <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>
              Déposer des documents liés à cette session (programme, attestations, supports…).
            </span>
          </span>
        </label>
        {showDocsSessions && (
          <div style={{ marginTop: '0.9rem' }}>
            <ConventionExterneZone sessionId={id} titre="📎 Documents de session déposés" categorie="session" />
          </div>
        )}
      </div>

      <div className="card" style={{marginTop: '1rem'}}>
        <div className="tab-bar">
          {tabs.map(t => (
            <button key={t.key} className={`tab-btn ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          ))}
          {!isFormateur && (
            <button
              className={`tab-btn ${activeTab === 'occurrences' ? 'active' : ''}`}
              onClick={() => setActiveTab('occurrences')}>
              🗓 Créneaux ({data.occurrences?.length || 0})
            </button>
          )}
        </div>

        {activeTab === 'documents' ? (
          <DocumentsTab data={data} sessionId={id} />
        ) : activeTab === 'financement' ? (
          <FinancementTab sessionId={id} session={data} hasRole={hasRole} />
        ) : activeTab === 'devis' ? (
          <div style={{ padding: '1rem 0' }}>
            <div style={{ marginBottom: '1.2rem', padding: '0.8rem 1rem', background: data.sans_convention ? 'rgba(245,158,11,0.08)' : 'var(--bg-elevated)', borderRadius: '10px', border: `1px solid ${data.sans_convention ? 'rgba(245,158,11,0.35)' : 'var(--border)'}` }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer', fontSize: '0.88rem' }}>
                <input type="checkbox" checked={!!data.sans_convention} style={{ marginTop: '0.2rem' }}
                  onChange={async e => {
                    const checked = e.target.checked;
                    try {
                      await apiFetch(`/sessions/${id}/sans-convention`, { method: 'PATCH', body: JSON.stringify({ sans_convention: checked }) });
                      reload();
                    } catch (err) { alert('Erreur : ' + err.message); }
                  }} />
                <span>
                  <strong>Convention établie hors application</strong>
                  <br />
                  <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>
                    Masque l'édition et l'envoi de la fiche d'inscription et de la convention pour cette session — passez directement à l'envoi de la convocation.
                  </span>
                </span>
              </label>
            </div>
            {data.sans_convention && (
              <div style={{ marginTop: '0.9rem', padding: '0.85rem 1.1rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                <ConventionExterneZone sessionId={id} categorie="convention" />
              </div>
            )}
            <DevisEtConventions sessionId={id} formationId={data.formation_id} hideConventions={!!data.sans_convention} groupes={data.groupes || []} inscriptions={data.inscriptions || []} />
            {(() => {
              const groupes = data.groupes || [];
              const multiClient = groupes.length > 1;
              const canConvoc = !data.sans_convention || (data.inscriptions || []).length > 0;
              const aucunStagiaire = (data.inscriptions || []).length === 0;
              const convocUrl = `/sessions/${id}/convocation${convocClientId ? `?client_id=${encodeURIComponent(convocClientId)}` : ''}`;

              function telechargerConvoc() {
                ouvrirDocument({ titre: `Convocation — ${id}`, url: convocUrl });
              }

              return (
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: data.sans_convention ? 'rgba(16,185,129,0.08)' : 'var(--bg-elevated)', borderRadius: '10px', border: `1px solid ${data.sans_convention ? 'var(--accent-emerald)' : 'var(--border)'}` }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.6rem' }}>📨 Convocation</div>
                  <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', marginBottom: '0.8rem' }}>
                    {data.sans_convention
                      ? 'Téléchargez ou envoyez la convocation par email directement depuis cette page.'
                      : 'Téléchargez la convocation pour cette session. Elle est envoyée automatiquement au client lors de la signature de la convention ; vous pouvez aussi la renvoyer par email ci-dessous (ex. : le client ne la retrouve pas).'}
                  </p>

                  {/* Sélecteur de client si session multi-groupes */}
                  {multiClient && (
                    <div style={{ marginBottom: '0.7rem' }}>
                      <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.3rem' }}>
                        Filtrer par client
                      </label>
                      <select value={convocClientId} onChange={e => setConvocClientId(e.target.value)}
                        style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                        <option value=''>— Tous les stagiaires —</option>
                        {groupes.map(g => (
                          <option key={g.client_id} value={g.client_id}>{g.nom_client}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {canConvoc ? (
                      <button onClick={telechargerConvoc}
                        style={{ display: 'inline-block', padding: '0.45rem 1rem', borderRadius: '8px', background: '#1a3668', color: '#fff', border: 'none', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                        ⬇ Télécharger la convocation PDF
                      </button>
                    ) : (
                      <span style={{ display: 'inline-block', padding: '0.45rem 1rem', borderRadius: '8px', background: 'var(--bg-elevated)', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: '0.85rem', cursor: 'not-allowed' }}>
                        ⬇ Télécharger la convocation PDF
                      </span>
                    )}
                  </div>

                  {(
                    aucunStagiaire ? (
                      <div style={{ marginTop: '0.8rem', padding: '0.6rem 0.9rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '7px', fontSize: '0.83rem', color: '#ef4444' }}>
                        ⚠ Aucun stagiaire inscrit — ajoutez des stagiaires à cette session pour pouvoir générer et envoyer la convocation.
                      </div>
                    ) : (
                      <div style={{ marginTop: '0.8rem' }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>{data.sans_convention ? 'Envoyer par email' : 'Renvoyer par email'}</div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input type="email" placeholder="Email du destinataire" value={convocEmail}
                            onChange={e => { setConvocEmail(e.target.value); setConvocMsg(null); }}
                            style={{ flex: 1, padding: '0.4rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                          <button disabled={sendingConvoc || !convocEmail}
                            onClick={async () => {
                              setSendingConvoc(true); setConvocMsg(null);
                              try {
                                await apiFetch(`/sessions/${id}/envoyer-convocation-externe`, { method: 'POST', body: JSON.stringify({ email: convocEmail, client_id: convocClientId || null }) });
                                setConvocMsg('✓ Convocation envoyée');
                                setConvocEmail('');
                                reload();
                              } catch (e) { setConvocMsg('✗ Erreur : ' + e.message); }
                              finally { setSendingConvoc(false); }
                            }}
                            style={{ padding: '0.4rem 0.9rem', borderRadius: '6px', border: 'none', background: 'var(--accent-emerald)', color: '#fff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            {sendingConvoc ? 'Envoi…' : '✉ Envoyer'}
                          </button>
                        </div>
                        {convocMsg && (
                          <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: couleurMessage(niveauMessage(convocMsg)) }}>
                            {convocMsg}
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              );
            })()}

            {/* Encart envoi documents par groupe */}
            <EnvoiDocumentsParGroupe sessionId={id} session={data} />
          </div>
        ) : activeTab === 'suivi' ? (
          <SuiviTab sessionId={id} session={data} />
        ) : activeTab === 'commande' ? (
          <CommandeFormateurTab sessionId={id} formateurId={data.formateur_id} nomFormateur={data.nom_formateur} dateDebut={data.date_debut} dateFin={data.date_fin} modules={data.modules || []} occurrences={data.occurrences || []} />
        ) : activeTab === 'parcours' ? (
          <ParcoursTab sessionId={id} session={data} />
        ) : activeTab === 'questionnaires' ? (
          <QRQuestionnairesPanel
            sessionId={id}
            formationId={data.formation_id}
            nomFormation={data.intitule_formation}
            occurrences={data.occurrences || []}
            groupes={data.groupes || []}
            presences={data.presences || []}
            inscriptions={data.inscriptions || []}
            session={data}
            modules={data.modules || []}
            typesQ={typesQ}
          />
        ) : activeTab === 'resultats' ? (
          <ResultatsQuestionnaires sessionId={id} inscriptions={data.inscriptions || []} modules={data.modules || []} session={data} typesQ={typesQ} />
        ) : activeTab === 'inscrits' ? (
          <InscritsCombinedTab
            sessionId={id}
            inscriptions={data.inscriptions || []}
            groupes={data.groupes || []}
            reload={reload}
            hasRole={hasRole}
            onAddGroupe={() => setModalGroupe(true)}
            session={data}
          />
        ) : activeTab === 'sms' ? (
          <SmsTab sessionId={id} inscriptions={data.inscriptions || []} />
        ) : activeTab === 'occurrences' ? (
          <OccurrencesSallesTab
            sessionId={id}
            occurrences={data.occurrences || []}
            salleSessionId={data.salle_id}
            formateurSessionId={data.formateur_id}
            nomFormateurSession={data.nom_formateur}
          />
        ) : (
          <DataTable columns={tabData[activeTab].columns} data={tabData[activeTab].data} />
        )}
      </div>

      {modalModif && (
        <ModalModifierSession
          session={data}
          onSave={() => { setModalModif(false); reload(); }}
          onClose={() => setModalModif(false)}
        />
      )}
      {modalGroupe && (
        <ModalNouveauGroupe
          sessionId={id}
          groupesExistants={data.groupes || []}
          onSave={() => { setModalGroupe(false); reload(); }}
          onClose={() => setModalGroupe(false)}
        />
      )}
    </div>
  );
}